import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import config from '../config.js';
import { indexTruthGroups, invoiceAssemblyPricing } from '../utils/salesOrderMath';

// Additional imports for MUI
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Snackbar,
  Alert as MuiAlert,
  Tooltip,
  Badge
} from "@mui/material";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { useThemeMode } from '../ThemeContext';
import {
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    ReceiptLong as ReceiptIcon,
    Search as SearchIcon,
    Add as AddIcon,
    Sync as SyncIcon,
    Warning as WarningIcon,
    Email as EmailIcon,
    Download as DownloadIcon,
    Send as SendIcon,
    Description as DescriptionIcon,
    AccountBalance as AccountBalanceIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Handshake as HandshakeIcon,
    Savings as SavingsIcon,
    Block as BlockIcon,
    Close as CloseIcon,
    AttachFile as AttachFileIcon,
    MarkEmailUnread as InquiryIcon
} from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import { fmt } from "../utils/financeFormat";
import {
  loadInvoices, customersFromInvoices, invoiceRowInfo, loadInvoice, loadInvoiceEmails, loadPaymentInquiries,
  inquiryForInvoice, inquiryBalanceAsked, invoiceStatusDisplay, invoiceBalance, chainAlignmentMeta, isCustomerPoDoc,
  groupInvoiceLines, daysPastDue, arSummary, loadBookedRevenue, dsoLabel, INVOICE_STATUS_OPTIONS,
} from "../features/invoices/invoiceData";
import { shopToday } from "../utils/shopDate";
import pinPrompt from "../utils/pinPrompt";
// Time & Materials (slice 1d): close-out invoice locks + Booked Revenue split.
import { isTm, isTmLinked } from "../utils/tmPricing";
import { axiosErrorMessage } from "../utils/apiError";
import { emailSenderHeaders } from "../utils/emailSender";
import { fetchAllJobPages } from "../utils/jobPages";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import TmInvoiceLockNote from "../components/tm/TmInvoiceParts.jsx";
import { Link as RouterLink } from "react-router-dom";
import TaxCertRequestModal from "../components/TaxCertRequestModal";
import PaymentInquiryDialog from "../components/PaymentInquiryDialog";
import DepositSlipsView from "./DepositSlipsView";
import { CUSTOMER_STATUS as TAXCERT_STATUS, fmtDate as fmtCertDate } from "../utils/taxCertificates";
import Switch from "@mui/material/Switch";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";

// -----------------------------
// CHART IMPORTS
// -----------------------------
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

// Register chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  Filler
);

// -----------------------------
// Adjust baseURL to your server
// -----------------------------
axios.defaults.baseURL = config.API_BASE_URL;

// A custom MUI theme with a more legible font — respects global dark/light mode
function getInvoiceTheme(mode) {
  const isDark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      text: {
        primary: isDark ? "rgba(255,255,255,0.9)" : "#000000",
        secondary: isDark ? "rgba(255,255,255,0.6)" : "#333333",
      },
      primary: { main: "#1976d2" },
      success: { main: "#2e7d32" },
      warning: { main: "#ed6c02" },
      background: { default: isDark ? "#121218" : "#f7f7f7", paper: isDark ? "#1e1e2d" : "#fff" },
      divider: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)",
    },
    typography: {
      fontFamily: "Arial, Helvetica, sans-serif",
      fontSize: 14,
      allVariants: {
        color: isDark ? "rgba(255,255,255,0.9)" : "#000000",
      },
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(224,224,224,1)",
          },
          head: {
            backgroundColor: isDark ? "#1a1a2e" : undefined,
            fontWeight: 600,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: isDark ? "rgba(25,118,210,0.2)" : undefined,
              '&:hover': {
                backgroundColor: isDark ? "rgba(25,118,210,0.3)" : undefined,
              },
            },
            '&:hover': {
              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : undefined,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#1e1e2d" : "#fff",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? "rgba(255,255,255,0.15)" : undefined,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderColor: isDark ? "rgba(255,255,255,0.15)" : undefined,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#1e1e2d" : "#fff",
            color: isDark ? "rgba(255,255,255,0.9)" : undefined,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#1e1e2d" : "#fff",
          },
        },
      },
    },
  });
}

/* ---------------------------------------------
   Payment Documents — scans of what we RECEIVE
   (check images, remittance advices, ACH/wire
   confirmations). Stored in the PRIVATE
   jobq-payment-documents bucket; every link below
   is a short-lived presigned URL handed out by the
   API, never a public S3 path.
   Mirror of PAYMENT_DOC_TYPES in jobq-api main.py.
   --------------------------------------------- */
const PAYMENT_DOC_TYPES = [
  "Check",
  "Remittance Advice",
  "ACH Advice",
  "Wire Confirmation",
  "Deposit Slip",
  "Other",
];

/* File picker used by the Record Payment dialog, the Batch Payment dialog,
   and the backfill Attach dialog. Collapsed to a single link until the
   operator opts in, so the common no-scan path stays one click. */
function PaymentDocumentPicker({ file, onFile, docType, onDocType, inputId, alwaysExpanded = false }) {
  const [expanded, setExpanded] = useState(alwaysExpanded);

  if (!expanded) {
    return (
      <Button size="small" startIcon={<AttachFileIcon />} sx={{ mt: 2 }} onClick={() => setExpanded(true)}>
        Attach check scan / remittance
      </Button>
    );
  }

  return (
    <Box sx={{ mt: 2, p: 1.5, border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
      {/* Say what happens to the FILE here. The 15-minute expiry applies to
          the signed view link handed out on read, not to the stored file —
          mentioning it at upload time reads as "this gets deleted", which is
          the opposite of the truth. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Attachment — kept on file. Stored privately; only JobQ can open it.
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="outlined" size="small" component="label" htmlFor={inputId} startIcon={<AttachFileIcon />}>
          {file ? "Change file" : "Choose file"}
          <input
            id={inputId}
            type="file"
            hidden
            accept="image/*,application/pdf"
            onChange={(e) => onFile(e.target.files?.[0] || null)}
          />
        </Button>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Type</InputLabel>
          <Select label="Type" value={docType} onChange={(e) => onDocType(e.target.value)}>
            {PAYMENT_DOC_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        {file && (
          <Chip size="small" label={file.name} onDelete={() => onFile(null)} />
        )}
      </Box>
    </Box>
  );
}

/* Renders the documents visible from one payment row — its own, plus any
   filed on the check group it belongs to. */
function PaymentDocumentLinks({ documents, onDelete }) {
  if (!documents || documents.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
      {documents.map((doc) => (
        <Box key={doc.payment_document_id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={`${doc.doc_type} · uploaded by ${doc.uploaded_by_name || "Unknown"} · ${(doc.created_at || "").slice(0, 10)} — opens a private link that expires in 15 min; reload for a fresh one`}>
            <Chip
              size="small"
              icon={<AttachFileIcon />}
              label={doc.doc_type}
              color={doc.payment_group_id ? "info" : "default"}
              variant="outlined"
              component="a"
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              // A missing URL means presigning failed — don't offer a dead link.
              disabled={!doc.url}
            />
          </Tooltip>
          {onDelete && (
            <IconButton size="small" onClick={() => onDelete(doc)}>
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          )}
        </Box>
      ))}
    </Box>
  );
}

/* ---------------------------------------------
   Accounts Receivable Dashboard
   Fetches all invoices (all statuses) independently
   to compute comprehensive AR metrics.
   --------------------------------------------- */
function DashboardView({ invoiceList: filteredInvoiceList, onSelectInvoice, onNotify }) {
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agingSortField, setAgingSortField] = useState('total');
  const [agingSortDir, setAgingSortDir] = useState('desc');
  const [overdueSortField, setOverdueSortField] = useState('days_overdue');
  const [overdueSortDir, setOverdueSortDir] = useState('desc');
  const [inquiryInvoiceId, setInquiryInvoiceId] = useState(null);

  // Re-read the invoice list after a payment inquiry so "Last Inquiry" updates.
  const refreshAllInvoices = async () => {
    try {
      const res = await axios.get("/invoices");
      setAllInvoices(res.data);
    } catch (err) {
      console.error("Failed to refresh dashboard invoices:", err);
    }
  };

  // ─── Date Range State ───
  const getPresetRange = (preset) => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();
    const toISO = (dt) => dt.toISOString().split('T')[0];
    switch (preset) {
      case 'this_month':
        return { start: toISO(new Date(y, m, 1)), end: toISO(today), label: 'This Month' };
      case 'last_30':
        return { start: toISO(new Date(y, m, d - 30)), end: toISO(today), label: 'Last 30 Days' };
      case 'this_quarter': {
        const qStart = new Date(y, Math.floor(m / 3) * 3, 1);
        return { start: toISO(qStart), end: toISO(today), label: 'This Quarter' };
      }
      case 'ytd':
        return { start: `${y}-01-01`, end: toISO(today), label: 'Year to Date' };
      case 'last_12':
      default:
        return { start: toISO(new Date(y - 1, m, d)), end: toISO(today), label: 'Last 12 Months' };
    }
  };

  const defaultRange = getPresetRange('last_12');
  const [rangeStart, setRangeStart] = useState(defaultRange.start);
  const [rangeEnd, setRangeEnd] = useState(defaultRange.end);
  const [activePreset, setActivePreset] = useState('last_12');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [bookedRevenue, setBookedRevenue] = useState({ total: 0, count: 0 });

  const fetchSummary = async (start, end) => {
    setSummaryLoading(true);
    try {
      const res = await axios.get("/invoices/dashboard-summary", {
        params: { start_date: start, end_date: end }
      });
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch dashboard summary:", err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handlePreset = (preset) => {
    const range = getPresetRange(preset);
    setRangeStart(range.start);
    setRangeEnd(range.end);
    setActivePreset(preset);
    fetchSummary(range.start, range.end);
  };

  const handleCustomRange = () => {
    if (rangeStart && rangeEnd) {
      setActivePreset(null);
      fetchSummary(rangeStart, rangeEnd);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await axios.get("/invoices");
        setAllInvoices(res.data);
      } catch (err) {
        console.error("Failed to fetch dashboard invoices:", err);
        setAllInvoices(filteredInvoiceList || []);
      } finally {
        setLoading(false);
      }
    };
    const fetchBookedRevenue = async () => {
      try {
        const split = await loadBookedRevenue(); // features/invoices/invoiceData
        setBookedRevenue({ total: split.total, count: split.count, tmPendingCount: split.tmPendingCount, tmPendingEst: split.tmPendingEst });
      } catch (err) {
        console.error("Failed to fetch booked revenue:", err);
      }
    };
    fetchAll();
    fetchBookedRevenue();
    fetchSummary(defaultRange.start, defaultRange.end);
  }, []);

  const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ─── Computed Metrics (features/invoices/invoiceData.arSummary) ───
  // AR outstanding, overdue (due dates are calendar days: past due from the day after; the shop's
  // Central day), DSO, aging buckets, aging by customer, the overdue invoices.
  const computed = useMemo(() => arSummary(allInvoices, shopToday()), [allInvoices]);

  // ─── Sorting helpers ───
  const sortedAgingRows = useMemo(() => {
    if (!computed) return [];
    return [...computed.agingRows].sort((a, b) => {
      const av = agingSortField === 'customer' ? a.customer.toLowerCase() : a[agingSortField];
      const bv = agingSortField === 'customer' ? b.customer.toLowerCase() : b[agingSortField];
      if (av < bv) return agingSortDir === 'asc' ? -1 : 1;
      if (av > bv) return agingSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [computed, agingSortField, agingSortDir]);

  const sortedOverdue = useMemo(() => {
    if (!computed) return [];
    return [...computed.overdueDetail].sort((a, b) => {
      const av = overdueSortField === 'customer_name' ? a.customer_name.toLowerCase() : a[overdueSortField];
      const bv = overdueSortField === 'customer_name' ? b.customer_name.toLowerCase() : b[overdueSortField];
      if (av < bv) return overdueSortDir === 'asc' ? -1 : 1;
      if (av > bv) return overdueSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [computed, overdueSortField, overdueSortDir]);

  const handleAgingSort = (field) => {
    if (agingSortField === field) setAgingSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setAgingSortField(field); setAgingSortDir('desc'); }
  };

  const handleOverdueSort = (field) => {
    if (overdueSortField === field) setOverdueSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setOverdueSortField(field); setOverdueSortDir('desc'); }
  };

  const sortArrow = (field, activeField, dir) => field === activeField ? (dir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  // ─── Build trend chart from server summary data ───
  // (must be before early returns to satisfy React hooks rules)
  const summaryMonths = useMemo(() => {
    if (!summary) return [];
    const allMonths = new Set();
    (summary.invoiced?.by_month || []).forEach(m => allMonths.add(m.month));
    (summary.collected?.by_month || []).forEach(m => allMonths.add(m.month));
    return [...allMonths].sort();
  }, [summary]);

  const invoicedMap = useMemo(() => {
    const m = {};
    (summary?.invoiced?.by_month || []).forEach(r => { m[r.month] = r.total; });
    return m;
  }, [summary]);

  const collectedMap = useMemo(() => {
    const m = {};
    (summary?.collected?.by_month || []).forEach(r => { m[r.month] = r.total; });
    return m;
  }, [summary]);

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>Accounts Receivable Dashboard</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!computed) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>Accounts Receivable Dashboard</Typography>
        <Typography color="text.secondary">No invoice data available.</Typography>
      </Box>
    );
  }

  // ─── Chart Data ───
  // Helper: round to 2 decimals to avoid floating point display issues
  const r2 = (n) => Math.round(n * 100) / 100;

  const trendMonthLabels = summaryMonths.map(l => {
    const [y, m] = l.split('-');
    return new Date(y, m - 1).toLocaleString('default', { month: 'short', year: '2-digit' });
  });

  const trendData = {
    labels: trendMonthLabels,
    datasets: [
      {
        label: "Invoiced",
        data: summaryMonths.map(k => r2(invoicedMap[k] || 0)),
        backgroundColor: "rgba(25, 118, 210, 0.7)",
        borderColor: "rgba(25, 118, 210, 1)",
        borderWidth: 1,
      },
      {
        label: "Collected",
        data: summaryMonths.map(k => r2(collectedMap[k] || 0)),
        backgroundColor: "rgba(46, 125, 50, 0.7)",
        borderColor: "rgba(46, 125, 50, 1)",
        borderWidth: 1,
      },
    ],
  };

  const fmtShort = (v) => {
    if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
    return `$${v.toFixed(0)}`;
  };

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.dataset.label}: $${fmt(ctx.parsed.y)}` },
      },
      datalabels: {
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
        anchor: 'end',
        align: 'top',
        font: { size: 10 },
        formatter: (v) => fmtShort(v),
      },
    },
    scales: {
      y: {
        ticks: { callback: (v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}` },
      },
    },
  };

  const agingChartData = {
    labels: ["Current", "1-30 Days", "31-60 Days", "61-90 Days", "90+ Days"],
    datasets: [{
      data: [
        r2(computed.agingBuckets.current),
        r2(computed.agingBuckets.d1_30),
        r2(computed.agingBuckets.d31_60),
        r2(computed.agingBuckets.d61_90),
        r2(computed.agingBuckets.d90plus),
      ],
      backgroundColor: [
        "rgba(46, 125, 50, 0.75)",
        "rgba(255, 193, 7, 0.75)",
        "rgba(255, 152, 0, 0.75)",
        "rgba(244, 67, 54, 0.75)",
        "rgba(183, 28, 28, 0.85)",
      ],
      borderColor: [
        "rgba(46, 125, 50, 1)",
        "rgba(255, 193, 7, 1)",
        "rgba(255, 152, 0, 1)",
        "rgba(244, 67, 54, 1)",
        "rgba(183, 28, 28, 1)",
      ],
      borderWidth: 1,
    }],
  };

  const agingChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (ctx) => `$${fmt(ctx.parsed.x)}` },
      },
      datalabels: {
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
        anchor: 'end',
        align: 'right',
        font: { size: 11, weight: 'bold' },
        formatter: (v) => `$${fmt(v)}`,
      },
    },
    scales: {
      x: {
        ticks: { callback: (v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}` },
        // Add padding so labels don't get clipped
        afterFit: (scale) => { scale.paddingRight = 20; },
      },
    },
  };

  // Collection rate (invoiced vs collected trend line) — from server data
  const collectionRateData = summaryMonths.map(k => {
    const inv = invoicedMap[k] || 0;
    const col = collectedMap[k] || 0;
    if (inv <= 0) return null;
    return Math.min(Math.round((col / inv) * 100), 200);
  });

  const collectionRateChartData = {
    labels: trendMonthLabels,
    datasets: [{
      label: "Collection Rate %",
      data: collectionRateData,
      borderColor: "rgba(25, 118, 210, 1)",
      backgroundColor: "rgba(25, 118, 210, 0.1)",
      fill: true,
      tension: 0.3,
      spanGaps: true, // connect across null gaps instead of dropping to 0
      pointRadius: (ctx) => collectionRateData[ctx.dataIndex] !== null ? 5 : 0,
      pointBackgroundColor: collectionRateData.map(v => v !== null && v >= 90 ? "#2e7d32" : v !== null && v >= 70 ? "#ff9800" : "#f44336"),
    }],
  };

  const collectionRateOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (ctx) => ctx.parsed.y !== null ? `${ctx.parsed.y}%` : 'N/A' },
      },
      datalabels: {
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== null,
        anchor: 'end',
        align: 'top',
        font: { size: 10 },
        formatter: (v) => v !== null ? `${v}%` : '',
      },
    },
    scales: {
      y: {
        min: 0,
        suggestedMax: 120,
        ticks: { callback: (v) => `${v}%` },
      },
    },
  };

  // Aging doughnut for visual balance
  const agingDoughnutData = {
    labels: ["Current", "1-30", "31-60", "61-90", "90+"],
    datasets: [{
      data: [
        r2(computed.agingBuckets.current),
        r2(computed.agingBuckets.d1_30),
        r2(computed.agingBuckets.d31_60),
        r2(computed.agingBuckets.d61_90),
        r2(computed.agingBuckets.d90plus),
      ],
      backgroundColor: ["#2e7d32", "#ffc107", "#ff9800", "#f44336", "#b71c1c"],
    }],
  };

  const doughnutOptions = {
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12 } },
      datalabels: {
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
        formatter: (v) => `$${fmtShort(v).replace('$', '')}`,
        color: '#fff',
        font: { size: 10, weight: 'bold' },
      },
    },
  };

  const kpiCardStyle = (color) => ({
    p: 2,
    textAlign: 'center',
    borderTop: `4px solid ${color}`,
    height: '100%',
  });

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Accounts Receivable Dashboard
      </Typography>

      {/* ─── Current AR Health KPIs ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3} md={3}>
          <Card sx={kpiCardStyle('#1976d2')}>
            <CardContent sx={{ p: '12px !important' }}>
              <Typography variant="caption" color="text.secondary">Total AR Outstanding</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>${fmt(computed.totalAR)}</Typography>
              <Typography variant="caption" color="text.secondary">{computed.openCount} open invoice{computed.openCount !== 1 ? 's' : ''}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} md={3}>
          <Card sx={kpiCardStyle(computed.totalOverdue > 0 ? '#f44336' : '#2e7d32')}>
            <CardContent sx={{ p: '12px !important' }}>
              <Typography variant="caption" color="text.secondary">Overdue</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: computed.totalOverdue > 0 ? 'error.main' : 'success.main' }}>
                ${fmt(computed.totalOverdue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">{computed.overdueCount} invoice{computed.overdueCount !== 1 ? 's' : ''} past due</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} md={3}>
          <Card sx={kpiCardStyle('#ff9800')}>
            <CardContent sx={{ p: '12px !important' }}>
              <Typography variant="caption" color="text.secondary">Days Sales Outstanding</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{computed.dso} days</Typography>
              <Typography variant="caption" color="text.secondary">{dsoLabel(computed.dso)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} md={3}>
          <Card sx={kpiCardStyle('#7b1fa2')}>
            <CardContent sx={{ p: '12px !important' }}>
              <Typography variant="caption" color="text.secondary">Booked Revenue (Uninvoiced)</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>${fmt(bookedRevenue.total)}</Typography>
              <Typography variant="caption" color="text.secondary">{bookedRevenue.count} sales order{bookedRevenue.count !== 1 ? 's' : ''}</Typography>
              {bookedRevenue.tmPendingCount > 0 && (
                <Typography variant="caption" color="text.secondary" component="div" data-testid="booked-tm-caption">
                  + {bookedRevenue.tmPendingCount} T&amp;M order{bookedRevenue.tmPendingCount !== 1 ? 's' : ''} not yet priced (est. ${fmt(bookedRevenue.tmPendingEst || 0)}), not included
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ─── Date Range Picker ─── */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ mr: 1 }}>Date Range:</Typography>
          {[
            { key: 'this_month', label: 'This Month' },
            { key: 'last_30', label: 'Last 30 Days' },
            { key: 'this_quarter', label: 'This Quarter' },
            { key: 'ytd', label: 'YTD' },
            { key: 'last_12', label: 'Last 12 Months' },
          ].map(p => (
            <Chip
              key={p.key}
              label={p.label}
              size="small"
              variant={activePreset === p.key ? 'filled' : 'outlined'}
              color={activePreset === p.key ? 'primary' : 'default'}
              onClick={() => handlePreset(p.key)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <TextField
            type="date"
            size="small"
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
            sx={{ width: 150 }}
            InputLabelProps={{ shrink: true }}
            label="From"
          />
          <TextField
            type="date"
            size="small"
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
            sx={{ width: 150 }}
            InputLabelProps={{ shrink: true }}
            label="To"
          />
          <Button variant="outlined" size="small" onClick={handleCustomRange}>Apply</Button>
        </Box>
        {summaryLoading && <LinearProgress sx={{ mt: 1 }} />}
      </Card>

      {/* ─── Period Summary Totals ─── */}
      {summary && !summaryLoading && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #1976d2', background: 'linear-gradient(135deg, rgba(25,118,210,0.04) 0%, transparent 100%)' }}>
              <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, fontSize: '0.65rem' }}>
                    Total Invoiced
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                    ${fmt(summary.invoiced.total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.invoiced.count} invoice{summary.invoiced.count !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #2e7d32', background: 'linear-gradient(135deg, rgba(46,125,50,0.04) 0%, transparent 100%)' }}>
              <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, fontSize: '0.65rem' }}>
                    Total Collected
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                    ${fmt(summary.collected.total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.collected.count} payment{summary.collected.count !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: `4px solid ${summary.net_ar_change > 0 ? '#ed6c02' : '#2e7d32'}`, background: `linear-gradient(135deg, ${summary.net_ar_change > 0 ? 'rgba(237,108,2,0.04)' : 'rgba(46,125,50,0.04)'} 0%, transparent 100%)` }}>
              <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, fontSize: '0.65rem' }}>
                    Net AR Change
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: summary.net_ar_change > 0 ? '#ed6c02' : '#2e7d32' }}>
                    {summary.net_ar_change >= 0 ? '+' : ''}${fmt(summary.net_ar_change)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.net_ar_change > 0 ? 'AR increased' : summary.net_ar_change < 0 ? 'AR decreased' : 'No change'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #7b1fa2', background: 'linear-gradient(135deg, rgba(123,31,162,0.04) 0%, transparent 100%)' }}>
              <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, fontSize: '0.65rem' }}>
                    Collection Rate
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                    {summary.invoiced.total > 0
                      ? `${Math.round((summary.collected.total / summary.invoiced.total) * 100)}%`
                      : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.invoiced.total > 0
                      ? (summary.collected.total / summary.invoiced.total >= 0.9
                        ? 'On track' : summary.collected.total / summary.invoiced.total >= 0.7
                        ? 'Moderate' : 'Needs attention')
                      : 'No invoices in range'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ─── Charts Row 1: Invoiced vs Collected + Aging Bar ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: 380 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Invoiced vs Collected</Typography>
              <Box sx={{ height: 300 }}>
                <Bar data={trendData} options={trendOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 380 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>AR Aging Breakdown</Typography>
              <Box sx={{ height: 300 }}>
                <Bar data={agingChartData} options={agingChartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ─── Charts Row 2: Collection Rate + Aging Doughnut ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: 340 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Collection Rate Trend
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (Collected / Invoiced per month)
                </Typography>
              </Typography>
              <Box sx={{ height: 260 }}>
                <Line data={collectionRateChartData} options={collectionRateOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 340 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>AR by Age</Typography>
              <Box sx={{ height: 230, display: 'flex', justifyContent: 'center' }}>
                <Doughnut data={agingDoughnutData} options={doughnutOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ─── Aging Schedule Table ─── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Aging Schedule by Customer</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    { field: 'customer', label: 'Customer' },
                    { field: 'current', label: 'Current' },
                    { field: 'd1_30', label: '1-30 Days' },
                    { field: 'd31_60', label: '31-60 Days' },
                    { field: 'd61_90', label: '61-90 Days' },
                    { field: 'd90plus', label: '90+ Days' },
                    { field: 'total', label: 'Total' },
                  ].map(col => (
                    <TableCell
                      key={col.field}
                      align={col.field === 'customer' ? 'left' : 'right'}
                      sx={{ cursor: 'pointer', fontWeight: 600, userSelect: 'none', whiteSpace: 'nowrap' }}
                      onClick={() => handleAgingSort(col.field)}
                    >
                      {col.label}{sortArrow(col.field, agingSortField, agingSortDir)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAgingRows.map((row, idx) => {
                  const hasOverdue = row.d1_30 > 0 || row.d31_60 > 0 || row.d61_90 > 0 || row.d90plus > 0;
                  return (
                    <TableRow
                      key={idx}
                      sx={{
                        backgroundColor: row.d90plus > 0
                          ? (theme) => theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.08)' : 'rgba(244,67,54,0.04)'
                          : hasOverdue
                          ? (theme) => theme.palette.mode === 'dark' ? 'rgba(255,152,0,0.06)' : 'rgba(255,152,0,0.03)'
                          : 'inherit',
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{row.customer}</TableCell>
                      <TableCell align="right">{row.current > 0 ? `$${fmt(row.current)}` : '-'}</TableCell>
                      <TableCell align="right" sx={{ color: row.d1_30 > 0 ? 'warning.main' : 'inherit' }}>
                        {row.d1_30 > 0 ? `$${fmt(row.d1_30)}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: row.d31_60 > 0 ? 'warning.dark' : 'inherit' }}>
                        {row.d31_60 > 0 ? `$${fmt(row.d31_60)}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: row.d61_90 > 0 ? 'error.main' : 'inherit' }}>
                        {row.d61_90 > 0 ? `$${fmt(row.d61_90)}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: row.d90plus > 0 ? 'error.dark' : 'inherit', fontWeight: row.d90plus > 0 ? 700 : 400 }}>
                        {row.d90plus > 0 ? `$${fmt(row.d90plus)}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(row.total)}</TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                {sortedAgingRows.length > 0 && (
                  <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
                    <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(computed.agingBuckets.current)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(computed.agingBuckets.d1_30)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(computed.agingBuckets.d31_60)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(computed.agingBuckets.d61_90)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(computed.agingBuckets.d90plus)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(computed.totalAR)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          {sortedAgingRows.length === 0 && (
            <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>No open invoices.</Typography>
          )}
        </CardContent>
      </Card>

      {/* ─── Overdue Invoice Detail ─── */}
      {sortedOverdue.length > 0 && (
        <Card sx={{ border: '1px solid', borderColor: 'error.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WarningIcon color="error" />
              <Typography variant="h6" color="error.main">
                Overdue Invoices ({computed.overdueCount})
              </Typography>
              <Chip label={`$${fmt(computed.totalOverdue)}`} color="error" size="small" sx={{ ml: 'auto' }} />
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { field: 'invoice_number', label: 'Invoice #' },
                      { field: 'customer_name', label: 'Customer' },
                      { field: 'due_date', label: 'Due Date' },
                      { field: 'total', label: 'Invoice Total' },
                      { field: 'balance', label: 'Balance Due' },
                      { field: 'days_overdue', label: 'Days Overdue' },
                      { field: 'last_emailed_at', label: 'Last Emailed' },
                      { field: 'last_inquiry_at', label: 'Last Inquiry' },
                    ].map(col => (
                      <TableCell
                        key={col.field}
                        align={['total', 'balance', 'days_overdue'].includes(col.field) ? 'right' : 'left'}
                        sx={{ cursor: 'pointer', fontWeight: 600, userSelect: 'none', whiteSpace: 'nowrap' }}
                        onClick={() => handleOverdueSort(col.field)}
                      >
                        {col.label}{sortArrow(col.field, overdueSortField, overdueSortDir)}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="center">Inquire</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOverdue.map((inv, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        backgroundColor: inv.days_overdue > 90
                          ? (theme) => theme.palette.mode === 'dark' ? 'rgba(183,28,28,0.12)' : 'rgba(183,28,28,0.04)'
                          : inv.days_overdue > 60
                          ? (theme) => theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.08)' : 'rgba(244,67,54,0.03)'
                          : 'inherit',
                      }}
                    >
                      <TableCell>
                        {onSelectInvoice
                          ? <Button size="small" variant="text" sx={{ minWidth: 0, p: 0, fontWeight: 600 }}
                                    onClick={() => onSelectInvoice(inv.invoice_id)}>#{inv.invoice_number}</Button>
                          : <>#{inv.invoice_number}</>}
                      </TableCell>
                      <TableCell>{inv.customer_name}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{inv.due_date}</TableCell>
                      <TableCell align="right">${fmt(inv.total)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>${fmt(inv.balance)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${inv.days_overdue}d`}
                          size="small"
                          color={inv.days_overdue > 90 ? 'error' : inv.days_overdue > 30 ? 'warning' : 'default'}
                          variant={inv.days_overdue > 60 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        {inv.last_emailed_at
                          ? <Tooltip title={new Date(inv.last_emailed_at).toLocaleString()}>
                              <Chip icon={<EmailIcon />} label={new Date(inv.last_emailed_at).toLocaleDateString()} size="small" variant="outlined" color="success" />
                            </Tooltip>
                          : <Chip label="Never" size="small" variant="outlined" color="warning" />
                        }
                      </TableCell>
                      <TableCell>
                        {inv.last_inquiry_at
                          ? <Tooltip title={`${inv.inquiry_count} payment inquir${inv.inquiry_count === 1 ? 'y' : 'ies'} — last ${new Date(inv.last_inquiry_at).toLocaleString()}`}>
                              <Chip icon={<InquiryIcon />} label={new Date(inv.last_inquiry_at).toLocaleDateString()} size="small" variant="outlined" color="warning" />
                            </Tooltip>
                          : <Typography variant="body2" color="text.secondary">—</Typography>
                        }
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0 }}>
                        <Tooltip describeChild title="Email the customer a payment inquiry with the invoice attached">
                          <IconButton size="small" color="warning"
                                      aria-label={`Send a payment inquiry for invoice #${inv.invoice_number}`}
                                      onClick={() => setInquiryInvoiceId(inv.invoice_id)}>
                            <InquiryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}

      <PaymentInquiryDialog
        open={!!inquiryInvoiceId}
        invoiceId={inquiryInvoiceId}
        onClose={() => setInquiryInvoiceId(null)}
        onSent={(result) => {
          setInquiryInvoiceId(null);
          if (onNotify) onNotify(result.message || "Payment inquiry sent.", result.duplicate ? "info" : "success");
          refreshAllInvoices();
        }}
      />
    </Box>
  );
}

// -----------------------------
// Validation Helper
// -----------------------------
const validateInvoiceRules = (linesToValidate, policy) => {
    // A. GHOST BILLING CHECK (Billing > Shipped)
    // Ensures we aren't billing for items that haven't physically left the shop
    const ghostBillingErrors = linesToValidate.filter(ln => {
        // Skip validation if we don't have shipping context (e.g. manual lines)
        if (ln.quantity_shipped === undefined || ln.quantity_shipped === null) return false;

        const currentQty = parseInt(ln.quantity, 10) || 0;
        // For Edit mode, we use 'others' context. For Create mode, 'quantity_invoiced' is total historic
        const historicBilled = ln.quantity_invoiced_others !== undefined 
            ? ln.quantity_invoiced_others 
            : (ln.quantity_invoiced || 0);

        const totalBilled = currentQty + historicBilled;
        
        // Error if Total Billed exceeds Total Shipped
        return totalBilled > ln.quantity_shipped;
    });

    // B. YIELD POLICY CHECK (Billing > Ordered)
    // Ensures we don't overbill the customer unless their policy allows it
    const yieldPolicyErrors = linesToValidate.filter(ln => {
        // Only check if it's linked to an SO line and policy info exists
        if (!ln.sales_order_line_id || !policy) return false;
        if (policy.allowOverShip || policy.allow_over_ship) return false;

        const currentQty = parseInt(ln.quantity, 10) || 0;
        const historicBilled = ln.quantity_invoiced_others !== undefined 
            ? ln.quantity_invoiced_others 
            : (ln.quantity_invoiced || 0);
        
        const totalBilled = currentQty + historicBilled;
        
        // Error if Total Billed exceeds Quantity Ordered
        return totalBilled > ln.quantity_ordered;
    });

    let warningMsg = "";

    if (ghostBillingErrors.length > 0) {
        warningMsg += "⚠️ GHOST BILLING WARNING (Billing > Shipped)\n";
        warningMsg += "You are attempting to invoice for items not yet shipped:\n";
        ghostBillingErrors.forEach(err => {
            const currentQty = parseInt(err.quantity, 10);
            const historic = err.quantity_invoiced_others !== undefined ? err.quantity_invoiced_others : (err.quantity_invoiced || 0);
            const product = err.product_id || "Unknown Item";
            warningMsg += ` - ${product}: Shipped ${err.quantity_shipped} vs Total Billed ${currentQty + historic}\n`;
        });
        warningMsg += "\n";
    }

    if (yieldPolicyErrors.length > 0) {
        warningMsg += `⛔ YIELD POLICY VIOLATION: ${policy.name || 'Strict Order'}\n`;
        warningMsg += "This customer's policy forbids billing more than the ordered quantity:\n";
        yieldPolicyErrors.forEach(err => {
            const currentQty = parseInt(err.quantity, 10);
            const historic = err.quantity_invoiced_others !== undefined ? err.quantity_invoiced_others : (err.quantity_invoiced || 0);
            const product = err.product_id || "Unknown Item";
            warningMsg += ` - ${product}: Ordered ${err.quantity_ordered} vs Total Billed ${currentQty + historic}\n`;
        });
        warningMsg += "\n";
    }

    return warningMsg;
};

/* ------------------------------------------------------------------
   The main InvoicesModule
   ------------------------------------------------------------------ */
// ========================================================================
// STATEMENTS VIEW
// ========================================================================
// ─────────────────────────────────────────────────────
// SALES TAX VIEW
// ─────────────────────────────────────────────────────
function SalesTaxView({ isDark }) {
  const API = config.API_BASE_URL;
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [certRequestFor, setCertRequestFor] = useState(null); // customer row → TaxCertRequestModal
  const [reloadTick, setReloadTick] = useState(0);

  const mutedText = isDark ? "#94a3b8" : "#64748b";
  const cardBg = isDark ? "#1e1e2d" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const headerBg = isDark ? "#0f3460" : "#f0f4ff";
  const accentGreen = isDark ? "#4ade80" : "#16a34a";
  const text = isDark ? "#e2e8f0" : "#1e293b";

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/reports/sales-tax?month=${month}`)
      .then((res) => {
        if (!res.ok) throw new Error(`API ${res.status}`);
        return res.json();
      })
      .then((r) => setData(r))
      .catch(() => setError("Failed to load report."))
      .finally(() => setLoading(false));
  }, [month, API, reloadTick]);

  const monthLabel = month
    ? new Date(month + "-15").toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "";

  // Taxable invoices summary (only invoices with tax > 0)
  const taxableInvoices = data ? data.invoices.filter((inv) => inv.tax_amount > 0) : [];
  const taxableSummary = {
    count: taxableInvoices.length,
    subtotal: taxableInvoices.reduce((sum, inv) => sum + inv.subtotal, 0),
    tax: taxableInvoices.reduce((sum, inv) => sum + inv.tax_amount, 0),
    total: taxableInvoices.reduce((sum, inv) => sum + inv.total, 0),
  };

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 3, backgroundColor: isDark ? "#121218" : "#f7f7f7" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Sales Tax Report</Typography>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            border: `1px solid ${border}`,
            background: cardBg,
            color: text,
            fontSize: 14,
          }}
        />
      </Box>

      {loading && <Typography sx={{ color: mutedText }}>Loading...</Typography>}
      {error && <Typography sx={{ color: "#ef4444" }}>{error}</Typography>}

      {/* Summary cards */}
      {data && !loading && (
        <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
          <Paper elevation={0} sx={{ background: cardBg, borderRadius: 3, p: "16px 24px", border: `1px solid ${border}`, minWidth: 160 }}>
            <Typography sx={{ fontSize: 13, color: mutedText, mb: 0.5 }}>Invoices</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700 }}>{data.summary.total_invoices}</Typography>
          </Paper>
          <Paper elevation={0} sx={{ background: cardBg, borderRadius: 3, p: "16px 24px", border: `1px solid ${border}`, minWidth: 160 }}>
            <Typography sx={{ fontSize: 13, color: mutedText, mb: 0.5 }}>Total Invoiced</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700 }}>{fmt(data.summary.total_invoiced)}</Typography>
          </Paper>
          <Paper elevation={0} sx={{ background: cardBg, borderRadius: 3, p: "16px 24px", border: `2px solid ${accentGreen}`, minWidth: 200 }}>
            <Typography sx={{ fontSize: 13, color: mutedText, mb: 0.5 }}>Sales Tax to Remit</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: accentGreen }}>{fmt(data.summary.total_tax)}</Typography>
          </Paper>
        </Box>
      )}

      {/* Exemption-certificate coverage — every untaxed invoice needs a signed
          Kansas certificate (ST-28D / ST-28A / ST-28M) behind it. Rows come from
          the same /reports/sales-tax call (data.customers). */}
      {data && !loading && Array.isArray(data.customers) && data.customers.length > 0 && (() => {
        const needing = data.customers.filter((c) => c.needs_attention);
        const chipColor = (st) => ({
          on_file: "success", not_required: "default", pending_review: "info",
          requested: "warning", expired: "error", request_expired: "error", missing: "error",
        }[st] || "default");
        return (
          <Paper elevation={0} sx={{ background: cardBg, borderRadius: 3, mb: 3, border: `1px solid ${needing.length ? "#ef4444" : border}`, overflow: "hidden" }}>
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: text }}>
                Exemption certificates — customers invoiced in {monthLabel}
              </Typography>
              {needing.length > 0
                ? <Chip size="small" color="error" label={`${needing.length} customer${needing.length !== 1 ? "s" : ""} invoiced without tax and without a certificate on file (${fmt(data.summary.uncovered_untaxed_subtotal || 0)})`} />
                : <Chip size="small" color="success" label="Every untaxed customer has a certificate on file (or is taxable)" />}
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: headerBg }}>
                  <TableCell sx={{ fontWeight: 700, color: text }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: text }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: text }}>Billing</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: text }} align="right">Untaxed sales</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: text }} align="right">Tax collected</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: text }}>Certificate</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: text }}>Details</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: text }} align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.customers.map((c) => {
                  const cert = c.certificate;
                  const label = (TAXCERT_STATUS[c.certificate_status] || {}).label || c.certificate_status;
                  return (
                    <TableRow key={c.customer_id} sx={{ background: c.needs_attention ? (isDark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.05)") : "transparent" }}>
                      <TableCell sx={{ color: text, fontWeight: c.needs_attention ? 600 : 400 }}>{c.name}</TableCell>
                      <TableCell sx={{ color: mutedText }}>{c.state || "—"}</TableCell>
                      <TableCell sx={{ color: mutedText, fontSize: 12 }}>{{ unknown: "not classified", exempt: "exempt", taxable: "taxable" }[c.tax_treatment] || c.tax_treatment}</TableCell>
                      <TableCell sx={{ color: text }} align="right">{fmt(c.untaxed_subtotal || 0)}</TableCell>
                      <TableCell sx={{ color: c.tax_amount > 0 ? accentGreen : mutedText }} align="right">{fmt(c.tax_amount || 0)}</TableCell>
                      <TableCell><Chip size="small" color={chipColor(c.certificate_status)} label={label} /></TableCell>
                      <TableCell sx={{ color: mutedText, fontSize: 12 }}>
                        {cert && cert.status === "verified" && <>{cert.form_type}{cert.signed_on ? ` · signed ${fmtCertDate(cert.signed_on)}` : ""}{cert.expires_on ? ` · expires ${fmtCertDate(cert.expires_on)}` : ""}{cert.tax_id ? ` · #${cert.tax_id}` : ""}</>}
                        {cert && cert.status === "received" && <>Uploaded {fmtCertDate(cert.uploaded_at)} — review on the customer page</>}
                        {cert && cert.status === "requested" && <>Sent {fmtCertDate(cert.last_sent_at || cert.requested_at)} to {cert.requested_to_email} ({(cert.requested_form_types || []).join(" / ")})</>}
                        {!cert && c.certificate_status === "missing" && <>Suggested: {(c.suggested_form_types || []).join(" + ")}</>}
                        {c.taxable_but_untaxed && <Chip size="small" color="warning" sx={{ ml: 1 }} label="marked taxable but invoiced without tax" />}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {["missing", "expired", "request_expired", "requested"].includes(c.certificate_status) && c.tax_treatment !== "taxable" && (
                          <Button size="small" variant={c.certificate_status === "requested" ? "text" : "outlined"} onClick={() => setCertRequestFor(c)} sx={{ mr: 1 }}>
                            {c.certificate_status === "requested" ? "Request again" : "Request…"}
                          </Button>
                        )}
                        <Button size="small" variant="text" component={RouterLink} to={`/customers/${c.customer_id}`}>Customer</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        );
      })()}

      {certRequestFor && (
        <TaxCertRequestModal
          customer={{ customer_id: certRequestFor.customer_id, name: certRequestFor.name, contact_name: certRequestFor.contact_name, email: certRequestFor.email, state: certRequestFor.state }}
          suggestedFormTypes={certRequestFor.suggested_form_types}
          onClose={() => setCertRequestFor(null)}
          onSent={(r) => { setCertRequestFor(null); alert(r.message); setReloadTick((t) => t + 1); }}
        />
      )}

      {/* Taxable sales summary — for state filing */}
      {data && !loading && taxableSummary.count > 0 && (
        <Paper elevation={0} sx={{ background: cardBg, borderRadius: 3, p: 2, mb: 3, border: `1px solid ${accentGreen}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1, color: text }}>
            Taxable Sales ({taxableSummary.count} invoice{taxableSummary.count !== 1 ? "s" : ""})
          </Typography>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: mutedText }}>Taxable Subtotal</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: text }}>{fmt(taxableSummary.subtotal)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: mutedText }}>Tax Collected</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: accentGreen }}>{fmt(taxableSummary.tax)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: mutedText }}>Total w/ Tax</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: text }}>{fmt(taxableSummary.total)}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Invoice table */}
      {data && !loading && data.invoices.length > 0 && (
        <Paper elevation={0} sx={{ background: cardBg, borderRadius: 3, overflow: "hidden", border: `1px solid ${border}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: headerBg }}>
                <TableCell sx={{ fontWeight: 700, color: text }}>Invoice #</TableCell>
                <TableCell sx={{ fontWeight: 700, color: text }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: text }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: text }} align="right">Subtotal</TableCell>
                <TableCell sx={{ fontWeight: 700, color: text }} align="right">Tax</TableCell>
                <TableCell sx={{ fontWeight: 700, color: text }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.invoices.map((inv) => (
                <TableRow key={inv.invoice_number} sx={{ "&:hover": { background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" } }}>
                  <TableCell sx={{ color: text }}>{inv.invoice_number}</TableCell>
                  <TableCell sx={{ color: text }}>{inv.invoice_date}</TableCell>
                  <TableCell sx={{ color: text }}>{inv.customer_name}</TableCell>
                  <TableCell sx={{ color: text }} align="right">{fmt(inv.subtotal)}</TableCell>
                  <TableCell sx={{ color: inv.tax_amount > 0 ? accentGreen : mutedText, fontWeight: inv.tax_amount > 0 ? 600 : 400 }} align="right">{fmt(inv.tax_amount)}</TableCell>
                  <TableCell sx={{ color: text }} align="right">{fmt(inv.total)}</TableCell>
                </TableRow>
              ))}
              {/* Totals row */}
              <TableRow sx={{ background: headerBg }}>
                <TableCell sx={{ fontWeight: 700, color: text }} colSpan={3}>TOTALS ({data.summary.total_invoices} invoices)</TableCell>
                <TableCell sx={{ fontWeight: 700, color: text }} align="right">{fmt(data.summary.total_subtotal)}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: accentGreen }} align="right">{fmt(data.summary.total_tax)}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: text }} align="right">{fmt(data.summary.total_invoiced)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      {data && !loading && data.invoices.length === 0 && (
        <Typography sx={{ color: mutedText, textAlign: "center", mt: 6 }}>No invoices found for {monthLabel}.</Typography>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────
// STATEMENTS VIEW
// ─────────────────────────────────────────────────────
function StatementsView({ isDark }) {
  // --- State ---
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [settings, setSettings] = useState({ statement_enabled: false, statement_frequency: "monthly", statement_contacts: [] });
  const [contacts, setContacts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);

  // Generate
  const [genCustomer, setGenCustomer] = useState(null);
  const [genStart, setGenStart] = useState("");
  const [genEnd, setGenEnd] = useState("");
  const [generating, setGenerating] = useState(false);

  // History
  const [statements, setStatements] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCustomerFilter, setHistoryCustomerFilter] = useState(null);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // --- Load customers ---
  useEffect(() => {
    axios.get("/customers").then(res => {
      setCustomers(res.data.sort((a, b) => a.name.localeCompare(b.name)));
    }).catch(err => console.error("Error fetching customers:", err));
  }, []);

  // --- Load statement history ---
  const fetchStatements = async (custId) => {
    setHistoryLoading(true);
    try {
      const params = custId ? `?customer_id=${custId}` : "";
      const res = await axios.get(`/statements${params}`);
      setStatements(res.data);
    } catch (err) {
      console.error("Error fetching statements:", err);
    }
    setHistoryLoading(false);
  };

  useEffect(() => { fetchStatements(historyCustomerFilter?.customer_id); }, [historyCustomerFilter]);

  // --- Load settings when customer selected ---
  useEffect(() => {
    if (!selectedCustomer) { setSettings({ statement_enabled: false, statement_frequency: "monthly", statement_contacts: [] }); setContacts([]); return; }
    setSettingsLoading(true);
    Promise.all([
      axios.get(`/customers/${selectedCustomer.customer_id}/statement-settings`),
      axios.get(`/customers/${selectedCustomer.customer_id}/contacts`),
    ]).then(([settRes, conRes]) => {
      const s = settRes.data;
      setSettings(s);
      setSelectedContactIds(s.statement_contacts?.map(c => c.contact_id) || []);
      setContacts(conRes.data.filter(c => c.email));
      setSettingsDirty(false);
    }).catch(err => console.error("Error loading settings:", err))
      .finally(() => setSettingsLoading(false));
  }, [selectedCustomer]);

  // --- Save settings ---
  const saveSettings = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.patch(`/customers/${selectedCustomer.customer_id}/statement-settings`, {
        statement_enabled: settings.statement_enabled,
        statement_frequency: settings.statement_frequency,
        statement_contact_ids: selectedContactIds,
      });
      setSettingsDirty(false);
      setSnack({ open: true, message: "Statement settings saved.", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: "Error saving settings.", severity: "error" });
    }
  };

  // --- Generate ---
  const handleGenerate = async (mode) => {
    if (!genCustomer || !genStart || !genEnd) { setSnack({ open: true, message: "Select customer and date range.", severity: "warning" }); return; }
    setGenerating(true);
    try {
      if (mode === "preview") {
        const res = await fetch(`${config.API_BASE_URL}/statements/generate-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customer_id: genCustomer.customer_id, period_start: genStart, period_end: genEnd }),
        });
        if (!res.ok) throw new Error("Preview failed");
        const blob = await res.blob();
        window.open(URL.createObjectURL(blob), "_blank");
      } else if (mode === "save") {
        const res = await axios.post("/statements/generate", {
          customer_id: genCustomer.customer_id, period_start: genStart, period_end: genEnd,
        });
        setSnack({ open: true, message: `Statement generated. Closing balance: $${res.data.closing_balance?.toFixed(2)}`, severity: "success" });
        fetchStatements(historyCustomerFilter?.customer_id);
      } else if (mode === "send") {
        const genRes = await axios.post("/statements/generate", {
          customer_id: genCustomer.customer_id, period_start: genStart, period_end: genEnd,
        });
        await axios.post(`/statements/${genRes.data.statement_id}/send`, { send_copy_to_self: false },
          { headers: emailSenderHeaders() });
        setSnack({ open: true, message: "Statement generated and sent.", severity: "success" });
        fetchStatements(historyCustomerFilter?.customer_id);
      }
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.detail || "Error generating statement.", severity: "error" });
    }
    setGenerating(false);
  };

  // --- Download PDF ---
  const handleDownload = async (stmt) => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/statements/${stmt.statement_id}/pdf`);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statement_${stmt.customer_name}_${stmt.period_end}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setSnack({ open: true, message: "Error downloading PDF.", severity: "error" });
    }
  };

  // --- Resend ---
  const handleResend = async (stmt) => {
    try {
      await axios.post(`/statements/${stmt.statement_id}/send`, { send_copy_to_self: false },
        { headers: emailSenderHeaders() });
      setSnack({ open: true, message: "Statement re-sent.", severity: "success" });
      fetchStatements(historyCustomerFilter?.customer_id);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.detail || "Error sending statement.", severity: "error" });
    }
  };

  // --- Delete ---
  const handleDelete = async (stmt) => {
    if (!window.confirm(`Delete statement for ${stmt.customer_name} (${stmt.period_start} — ${stmt.period_end})?`)) return;
    try {
      await axios.delete(`/statements/${stmt.statement_id}`);
      setSnack({ open: true, message: "Statement deleted.", severity: "success" });
      fetchStatements(historyCustomerFilter?.customer_id);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.detail || "Error deleting statement.", severity: "error" });
    }
  };

  // --- Bulk send ---
  const handleBulkSend = async () => {
    try {
      const res = await axios.get("/alerts/send_statements");
      const d = res.data;
      setSnack({ open: true, message: `Sent: ${d.sent_count}, Skipped: ${d.skipped_count}${d.errors?.length ? `, Errors: ${d.errors.length}` : ""}`, severity: d.errors?.length ? "warning" : "success" });
      fetchStatements(historyCustomerFilter?.customer_id);
    } catch (err) {
      setSnack({ open: true, message: "Error running bulk send.", severity: "error" });
    }
  };

  return (
    <Box sx={{ p: 3, overflowY: "auto", height: "100%" }}>
      {/* Statement Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Statement Settings</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={customers}
                getOptionLabel={(o) => o.name || ""}
                value={selectedCustomer}
                onChange={(e, v) => setSelectedCustomer(v)}
                renderInput={(params) => <TextField {...params} label="Select Customer" size="small" />}
                isOptionEqualToValue={(o, v) => o.customer_id === v.customer_id}
              />
            </Grid>
            {selectedCustomer && !settingsLoading && (
              <>
                <Grid item xs={6} md={2}>
                  <FormControlLabel
                    control={<Switch checked={settings.statement_enabled} onChange={(e) => { setSettings(s => ({ ...s, statement_enabled: e.target.checked })); setSettingsDirty(true); }} />}
                    label="Enabled"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontSize: "0.75rem" }}>Frequency</FormLabel>
                    <RadioGroup row value={settings.statement_frequency} onChange={(e) => { setSettings(s => ({ ...s, statement_frequency: e.target.value })); setSettingsDirty(true); }}>
                      <FormControlLabel value="weekly" control={<Radio size="small" />} label="Weekly" />
                      <FormControlLabel value="monthly" control={<Radio size="small" />} label="Monthly" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    multiple
                    options={contacts}
                    getOptionLabel={(o) => `${o.name} (${o.email})${o.role ? ` — ${o.role}` : ""}`}
                    value={contacts.filter(c => selectedContactIds.includes(c.contact_id))}
                    onChange={(e, v) => { setSelectedContactIds(v.map(c => c.contact_id)); setSettingsDirty(true); }}
                    isOptionEqualToValue={(o, v) => o.contact_id === v.contact_id}
                    renderInput={(params) => <TextField {...params} label="Statement Recipients" size="small" />}
                    renderTags={(val, getTagProps) => val.map((opt, idx) => (
                      <Chip {...getTagProps({ index: idx })} key={opt.contact_id} label={`${opt.name}`} size="small" />
                    ))}
                  />
                </Grid>
                {settingsDirty && (
                  <Grid item xs={12}>
                    <Button variant="contained" size="small" onClick={saveSettings}>Save Settings</Button>
                  </Grid>
                )}
              </>
            )}
            {settingsLoading && <Grid item xs={8}><LinearProgress /></Grid>}
          </Grid>
        </CardContent>
      </Card>

      {/* Generate Statement */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Generate Statement</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={customers}
                getOptionLabel={(o) => o.name || ""}
                value={genCustomer}
                onChange={(e, v) => setGenCustomer(v)}
                renderInput={(params) => <TextField {...params} label="Customer" size="small" />}
                isOptionEqualToValue={(o, v) => o.customer_id === v.customer_id}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Period Start" type="date" size="small" fullWidth
                value={genStart} onChange={(e) => setGenStart(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Period End" type="date" size="small" fullWidth
                value={genEnd} onChange={(e) => setGenEnd(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" onClick={() => handleGenerate("preview")} disabled={generating}>Preview</Button>
                <Button variant="outlined" onClick={() => handleGenerate("save")} disabled={generating}>Generate & Save</Button>
                <Button variant="contained" startIcon={<SendIcon />} onClick={() => handleGenerate("send")} disabled={generating}>Generate & Send</Button>
              </Box>
            </Grid>
          </Grid>
          {generating && <LinearProgress sx={{ mt: 1 }} />}
        </CardContent>
      </Card>

      {/* Statement History */}
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Statement History</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Autocomplete
                options={customers}
                getOptionLabel={(o) => o.name || ""}
                value={historyCustomerFilter}
                onChange={(e, v) => setHistoryCustomerFilter(v)}
                renderInput={(params) => <TextField {...params} label="Filter by Customer" size="small" sx={{ width: 250 }} />}
                isOptionEqualToValue={(o, v) => o.customer_id === v.customer_id}
                size="small"
              />
              <Tooltip title="Send all due statements now">
                <Button variant="outlined" size="small" onClick={handleBulkSend} startIcon={<SendIcon />}>
                  Send All Due
                </Button>
              </Tooltip>
            </Box>
          </Box>
          {historyLoading ? <LinearProgress /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Opening</TableCell>
                  <TableCell align="right">Invoiced</TableCell>
                  <TableCell align="right">Payments</TableCell>
                  <TableCell align="right">Closing</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statements.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>No statements found.</TableCell></TableRow>
                ) : statements.map((s) => (
                  <TableRow key={s.statement_id} hover>
                    <TableCell>{s.customer_name}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{s.period_start} — {s.period_end}</TableCell>
                    <TableCell align="right">${Number(s.opening_balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right">${Number(s.total_invoiced).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right">${Number(s.total_payments).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>${Number(s.closing_balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      {s.sent_at ? (
                        <Tooltip title={`Sent to: ${s.sent_to?.join(", ") || "—"}`}>
                          <Chip label={new Date(s.sent_at).toLocaleDateString()} size="small" color="success" variant="outlined" icon={<EmailIcon />} />
                        </Tooltip>
                      ) : (
                        <Chip label="Not sent" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Download PDF">
                          <IconButton size="small" onClick={() => handleDownload(s)}><DownloadIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Resend">
                          <IconButton size="small" onClick={() => handleResend(s)}><SendIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(s)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <MuiAlert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} variant="filled">{snack.message}</MuiAlert>
      </Snackbar>
    </Box>
  );
}

// ========================================================================
// UNMATCHED DEPOSITS VIEW (Plaid → invoice matching queue)
// ========================================================================
function UnmatchedDepositsView({ isDark, onShowMessage, onCountChange }) {
  const [deposits, setDeposits] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [includeFiltered, setIncludeFiltered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Match dialog state
  const [matchOpen, setMatchOpen] = useState(false);
  const [activeDeposit, setActiveDeposit] = useState(null);
  const [matchCustomerId, setMatchCustomerId] = useState("");
  const [matchCustomersList, setMatchCustomersList] = useState([]);
  const [matchOpenInvoices, setMatchOpenInvoices] = useState([]);
  const [matchAllocations, setMatchAllocations] = useState({}); // {invoice_id: amount}
  const [matchDispositions, setMatchDispositions] = useState({}); // {invoice_id: 'auto'|'retainage_held'|'writeoff'}
  const [matchSubmitting, setMatchSubmitting] = useState(false);
  // Mode: "new" creates new invoice_payment rows; "link" stamps an existing
  // payment that the operator already entered manually.
  const [matchMode, setMatchMode] = useState("new");
  const [recentPayments, setRecentPayments] = useState([]);
  const [linkingPaymentId, setLinkingPaymentId] = useState(null);

  // Dismiss dialog state
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissTxn, setDismissTxn] = useState(null);
  const [dismissReason, setDismissReason] = useState("");
  const [dismissBy, setDismissBy] = useState("");

  const cardBg = isDark ? "#1e1e2d" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const mutedText = isDark ? "#94a3b8" : "#64748b";

  const loadDeposits = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/finance/unmatched-deposits", {
        params: { include_filtered: includeFiltered ? "true" : "false" }
      });
      setDeposits(res.data?.data || []);
      setHiddenCount(res.data?.hidden_count || 0);
      onCountChange?.();
    } catch (e) {
      console.error("Failed to load deposits", e);
      onShowMessage?.("Failed to load unmatched deposits.", "error");
    }
    setLoading(false);
  }, [onShowMessage, includeFiltered, onCountChange]);

  useEffect(() => { loadDeposits(); }, [loadDeposits]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post("/finance/transactions/sync");
      await loadDeposits();
      onShowMessage?.("Bank transactions synced from Plaid.", "success");
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Failed to sync transactions", "error");
    }
    setSyncing(false);
  };

  // ------------------- Match Dialog -------------------
  const openMatchFor = async (deposit, suggestedCustomerId = null) => {
    setActiveDeposit(deposit);
    setMatchAllocations({});
    setMatchDispositions({});
    setMatchOpenInvoices([]);
    setMatchMode("new");
    setRecentPayments([]);

    // Load customer list for picker
    try {
      const res = await axios.get("/customers");
      setMatchCustomersList(res.data || []);
    } catch (e) {
      console.error("Failed to load customers", e);
      setMatchCustomersList([]);
    }

    // Pre-select suggested customer if provided
    if (suggestedCustomerId) {
      setMatchCustomerId(suggestedCustomerId);
      await loadCustomerInvoicesForMatch(suggestedCustomerId, deposit.amount);
      await loadRecentPaymentsForCustomer(suggestedCustomerId);
    } else {
      setMatchCustomerId("");
    }
    setMatchOpen(true);
  };

  const loadCustomerInvoicesForMatch = async (customerId, depositAmount) => {
    if (!customerId) { setMatchOpenInvoices([]); return; }
    try {
      const res = await axios.get("/invoices", { params: { customer_id: customerId, status: "Open" } });
      const invoices = res.data || [];
      setMatchOpenInvoices(invoices);

      // Auto-allocate from oldest first
      try {
        const auto = await axios.get("/payments/auto-allocate", {
          params: { customer_id: customerId, amount: depositAmount }
        });
        const newAllocations = {};
        const newDispositions = {};
        (auto.data?.proposed_allocations || []).forEach(a => {
          if (a.allocation > 0) {
            newAllocations[a.invoice_id] = a.allocation;
            newDispositions[a.invoice_id] = "auto";
          }
        });
        setMatchAllocations(newAllocations);
        setMatchDispositions(newDispositions);
      } catch (autoErr) {
        // No open invoices? Allow operator to add manually
        console.warn("Auto-allocate failed; operator can allocate manually", autoErr);
      }
    } catch (e) {
      console.error("Failed to load customer invoices", e);
      setMatchOpenInvoices([]);
    }
  };

  // Recent unlinked invoice_payments for the chosen customer — powers the
  // "Link to existing payment" mode where the operator already manually
  // recorded the payment in JobQ before the bank deposit landed.
  const loadRecentPaymentsForCustomer = async (customerId) => {
    if (!customerId) { setRecentPayments([]); return; }
    try {
      const res = await axios.get(`/customers/${customerId}/recent-payments`);
      setRecentPayments(res.data?.data || []);
    } catch (e) {
      console.error("Failed to load recent payments", e);
      setRecentPayments([]);
    }
  };

  const handleMatchCustomerChange = (customerId) => {
    setMatchCustomerId(customerId);
    loadCustomerInvoicesForMatch(customerId, activeDeposit?.amount || 0);
    loadRecentPaymentsForCustomer(customerId);
  };

  const handleLinkPayment = async (paymentId) => {
    if (!activeDeposit) return;
    setLinkingPaymentId(paymentId);
    try {
      const res = await axios.post(
        `/finance/unmatched-deposits/${activeDeposit.id}/link-payment`,
        { invoice_payment_id: paymentId, matched_by: "operator" }
      );
      onShowMessage?.(
        `Linked deposit to existing payment on Invoice #${res.data.invoice_id}.`,
        "success"
      );
      setMatchOpen(false);
      loadDeposits();
    } catch (e) {
      console.error("Link failed", e);
      onShowMessage?.(e.response?.data?.detail || "Failed to link payment", "error");
    }
    setLinkingPaymentId(null);
  };

  const matchAllocSum = useMemo(() => {
    return Object.values(matchAllocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  }, [matchAllocations]);

  const handleConfirmMatch = async () => {
    if (!activeDeposit) return;
    if (!matchCustomerId) { onShowMessage?.("Pick a customer.", "warning"); return; }

    const allocs = Object.entries(matchAllocations)
      .filter(([, amt]) => parseFloat(amt) > 0)
      .map(([invoiceId, amt]) => ({
        invoice_id: parseInt(invoiceId),
        amount: parseFloat(amt),
        disposition: matchDispositions[invoiceId] || "auto",
      }));

    if (allocs.length === 0) { onShowMessage?.("No allocations specified.", "error"); return; }

    const allocSum = allocs.reduce((s, a) => s + a.amount, 0);
    if (Math.abs(activeDeposit.amount - allocSum) > 0.01) {
      onShowMessage?.(
        `Allocations ($${allocSum.toFixed(2)}) must sum to deposit ($${activeDeposit.amount.toFixed(2)}).`,
        "error"
      );
      return;
    }

    setMatchSubmitting(true);
    try {
      const payload = {
        customer_id: parseInt(matchCustomerId),
        total_amount: activeDeposit.amount,
        payment_method: "ACH",
        memo: `Plaid: ${activeDeposit.merchant_name || activeDeposit.name || ""} ${activeDeposit.date || ""}`.trim(),
        allocations: allocs,
      };
      await axios.post(`/finance/unmatched-deposits/${activeDeposit.id}/match`, payload);
      onShowMessage?.(`Matched $${activeDeposit.amount.toFixed(2)} to ${allocs.length} invoice(s).`, "success");
      setMatchOpen(false);
      loadDeposits();
    } catch (e) {
      console.error("Match failed", e);
      onShowMessage?.(e.response?.data?.detail || "Failed to match deposit", "error");
    }
    setMatchSubmitting(false);
  };

  // ------------------- Dismiss Dialog -------------------
  const openDismissFor = (deposit) => {
    setDismissTxn(deposit);
    setDismissReason("");
    setDismissBy("");
    setDismissOpen(true);
  };

  const handleConfirmDismiss = async () => {
    if (!dismissTxn) return;
    if (!dismissBy.trim()) { onShowMessage?.("Enter your name (so the audit log knows who dismissed).", "warning"); return; }
    try {
      await axios.post(`/finance/unmatched-deposits/${dismissTxn.id}/dismiss`, {
        dismissed_by: dismissBy.trim(),
        reason: dismissReason.trim() || null,
      });
      onShowMessage?.("Deposit dismissed.", "success");
      setDismissOpen(false);
      loadDeposits();
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Failed to dismiss", "error");
    }
  };

  // ------------------- Render -------------------
  return (
    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5">Unmatched Deposits</Typography>
          <Typography variant="caption" sx={{ color: mutedText }}>
            {includeFiltered
              ? "All Plaid-synced inflows on Labette CHECKING (Direct Deposit + Bill.com + check deposits)."
              : "Direct ACH deposits on Labette CHECKING. Bill.com payments and check deposits are excluded — those flow through other paths."
            }
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeFiltered}
                onChange={(e) => setIncludeFiltered(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Show all{hiddenCount > 0 && !includeFiltered ? ` (${hiddenCount} hidden)` : ""}</Typography>}
            sx={{ mr: 0 }}
          />
          <Button variant="outlined" size="small" onClick={loadDeposits} disabled={loading} startIcon={<SyncIcon />}>
            {loading ? "Loading..." : "Refresh"}
          </Button>
          <Button variant="contained" size="small" onClick={handleSync} disabled={syncing} startIcon={<SyncIcon />}>
            {syncing ? "Syncing..." : "Sync from Plaid"}
          </Button>
        </Box>
      </Box>

      {!loading && deposits.length === 0 && (
        <Card sx={{ background: cardBg, border: `1px solid ${border}`, p: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: mutedText }}>
            {hiddenCount > 0 && !includeFiltered
              ? `No direct ACH deposits to match. ${hiddenCount} Bill.com / check deposit${hiddenCount === 1 ? '' : 's'} hidden — toggle "Show all" to view.`
              : "No unmatched deposits in the last 90 days. New ACH deposits will appear here after the next Plaid sync."}
          </Typography>
        </Card>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {deposits.map((d) => (
          <Card key={d.id} sx={{ background: cardBg, border: `1px solid ${d.is_stale ? '#ef4444' : border}`, position: 'relative' }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 320px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="h6" component="span">
                      {fmt(d.amount)}
                    </Typography>
                    <Chip size="small" label={d.date} />
                    {d.is_stale && (
                      <Chip size="small" label={`${d.age_days}d unmatched`} color="error" icon={<WarningIcon />} />
                    )}
                    {!d.is_stale && d.age_days > 0 && (
                      <Chip size="small" label={`${d.age_days}d ago`} variant="outlined" />
                    )}
                    {d.pending && <Chip size="small" label="Pending" color="warning" />}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {d.merchant_name || d.name || "(no merchant)"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: mutedText }}>
                    {d.institution_name} · {d.account_name} {d.account_mask ? `(...${d.account_mask})` : ""}
                  </Typography>
                </Box>

                <Box sx={{ flex: '1 1 320px' }}>
                  <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5 }}>
                    Suggested customers
                  </Typography>
                  {(d.suggested_customers || []).length === 0 ? (
                    <Typography variant="caption" sx={{ color: mutedText, fontStyle: 'italic' }}>
                      No suggestions — pick a customer manually when matching.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {d.suggested_customers.map((s) => (
                        <Tooltip
                          key={s.customer_id}
                          title={`${s.open_invoice_count} open invoice(s) · ${fmt(s.open_balance)} outstanding${s.matched_alias ? ` · alias "${s.matched_alias}"` : ' · name match'}`}
                        >
                          <Chip
                            size="small"
                            label={`${s.customer_name} · ${fmt(s.open_balance)}`}
                            color="primary"
                            variant="outlined"
                            clickable
                            onClick={() => openMatchFor(d, s.customer_id)}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', alignSelf: 'center' }}>
                  <Button variant="contained" size="small" onClick={() => openMatchFor(d, d.suggested_customers?.[0]?.customer_id)}>
                    Match
                  </Button>
                  <Button variant="outlined" size="small" color="inherit" startIcon={<BlockIcon />} onClick={() => openDismissFor(d)}>
                    Dismiss
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Match Dialog */}
      <Dialog open={matchOpen} onClose={() => setMatchOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Match Deposit
          {activeDeposit && (
            <Typography variant="caption" sx={{ display: 'block', color: mutedText, fontSize: 13 }}>
              {fmt(activeDeposit.amount)} on {activeDeposit.date} — {activeDeposit.merchant_name || activeDeposit.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Customer</InputLabel>
            <Select label="Customer" value={matchCustomerId} onChange={(e) => handleMatchCustomerChange(e.target.value)}>
              {matchCustomersList.map((c) => (
                <MenuItem key={c.customer_id} value={c.customer_id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {matchCustomerId && (
            <FormControl sx={{ mb: 2 }}>
              <FormLabel sx={{ fontSize: 13, mb: 0.5 }}>Match type</FormLabel>
              <RadioGroup
                row
                value={matchMode}
                onChange={(e) => setMatchMode(e.target.value)}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio size="small" />}
                  label="Record new payment"
                />
                <FormControlLabel
                  value="link"
                  control={<Radio size="small" />}
                  label={
                    <span>
                      Link to existing payment
                      {recentPayments.length > 0 && (
                        <Chip
                          size="small"
                          label={recentPayments.length}
                          sx={{ ml: 0.75, height: 18, fontSize: '0.7rem' }}
                        />
                      )}
                    </span>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}

          {/* NEW PAYMENT MODE — allocate against open invoices */}
          {matchCustomerId && matchMode === "new" && matchOpenInvoices.length === 0 && (
            <Typography variant="body2" sx={{ color: 'warning.main', mb: 1 }}>
              This customer has no open invoices.
            </Typography>
          )}

          {matchMode === "new" && matchOpenInvoices.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell align="right">Apply</TableCell>
                  <TableCell>Disposition (when partial)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matchOpenInvoices.map((inv) => {
                  const total = parseFloat(inv.total || 0);
                  const paid = parseFloat(inv.total_paid || 0);
                  const remaining = Math.max(0, total - paid);
                  const alloc = parseFloat(matchAllocations[inv.invoice_id] || 0);
                  const isPartial = alloc > 0 && alloc < remaining - 0.01;
                  return (
                    <TableRow key={inv.invoice_id}>
                      <TableCell>#{inv.invoice_number}</TableCell>
                      <TableCell align="right">{fmt(total)}</TableCell>
                      <TableCell align="right">{fmt(remaining)}</TableCell>
                      <TableCell align="right" sx={{ width: 130 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={matchAllocations[inv.invoice_id] || ""}
                          onChange={(e) => setMatchAllocations(prev => ({ ...prev, [inv.invoice_id]: e.target.value }))}
                          sx={{ width: 110 }}
                          inputProps={{ step: '0.01', min: '0', max: remaining }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        {isPartial ? (
                          <Select
                            size="small"
                            value={matchDispositions[inv.invoice_id] || "auto"}
                            onChange={(e) => setMatchDispositions(prev => ({ ...prev, [inv.invoice_id]: e.target.value }))}
                          >
                            <MenuItem value="auto">Partial — still expected</MenuItem>
                            <MenuItem value="retainage_held">Retainage held</MenuItem>
                            <MenuItem value="writeoff">Short-pay write-off</MenuItem>
                          </Select>
                        ) : (
                          <Typography variant="caption" sx={{ color: mutedText }}>(full)</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {matchMode === "new" && activeDeposit && matchOpenInvoices.length > 0 && (
            <Box sx={{ mt: 2, p: 1.25, backgroundColor: 'action.hover', borderRadius: 1, fontSize: 13 }}>
              Deposit total: <b>{fmt(activeDeposit.amount)}</b> · Allocated: <b>{fmt(matchAllocSum)}</b> · Unallocated: <b>{fmt(activeDeposit.amount - matchAllocSum)}</b>
              {Math.abs(activeDeposit.amount - matchAllocSum) > 0.01 && (
                <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 0.5 }}>
                  Allocations must sum exactly to the deposit total.
                </Typography>
              )}
            </Box>
          )}

          {/* LINK MODE — pick an already-recorded payment */}
          {matchMode === "link" && (
            <>
              <Typography variant="body2" sx={{ color: mutedText, mb: 1 }}>
                Recent payments for this customer that haven't been linked to a bank deposit yet (last 60 days). Click <b>Link</b> on the row that corresponds to this deposit — the bank deposit will be marked matched without creating a duplicate payment.
              </Typography>
              {recentPayments.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                  No unlinked payments found in the last 60 days for this customer. Switch to "Record new payment" to allocate against open invoices.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Memo</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentPayments.map((p) => {
                      const amountMatches = activeDeposit && Math.abs(activeDeposit.amount - p.payment_amount) < 0.01;
                      return (
                        <TableRow
                          key={p.invoice_payment_id}
                          sx={{
                            backgroundColor: amountMatches ? 'rgba(16,185,129,0.08)' : undefined,
                          }}
                        >
                          <TableCell>{p.payment_date}</TableCell>
                          <TableCell>#{p.invoice_number}</TableCell>
                          <TableCell>{p.payment_method || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: amountMatches ? 700 : 400 }}>
                            {fmt(p.payment_amount)}
                            {amountMatches && (
                              <Tooltip title="Matches the deposit amount exactly">
                                <Chip size="small" label="match" color="success" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Tooltip title={p.memo || ''}>
                              <span>{p.memo || '—'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              disabled={linkingPaymentId === p.invoice_payment_id}
                              onClick={() => handleLinkPayment(p.invoice_payment_id)}
                            >
                              {linkingPaymentId === p.invoice_payment_id ? '...' : 'Link'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchOpen(false)}>Cancel</Button>
          {matchMode === "new" && (
            <Button variant="contained" onClick={handleConfirmMatch} disabled={matchSubmitting || !matchCustomerId}>
              {matchSubmitting ? "Recording..." : "Match & Record Payment"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dismiss Dialog */}
      <Dialog open={dismissOpen} onClose={() => setDismissOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Dismiss Deposit</DialogTitle>
        <DialogContent>
          {dismissTxn && (
            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
              {fmt(dismissTxn.amount)} on {dismissTxn.date} — {dismissTxn.merchant_name || dismissTxn.name}
            </Typography>
          )}
          <TextField label="Your name" required fullWidth value={dismissBy} onChange={(e) => setDismissBy(e.target.value)} sx={{ mb: 2 }} />
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            rows={2}
            placeholder="e.g. loan draw, owner transfer, refund, internal transfer"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmDismiss}>Dismiss</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ========================================================================
// RETAINAGE VIEW (outstanding retainage by customer)
// ========================================================================
function RetainageView({ isDark, onShowMessage }) {
  const [data, setData] = useState({ total_outstanding: 0, customer_count: 0, by_customer: [], invoices: [] });
  const [loading, setLoading] = useState(false);

  const cardBg = isDark ? "#1e1e2d" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const mutedText = isDark ? "#94a3b8" : "#64748b";

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/retainage/summary");
      setData(res.data || { total_outstanding: 0, customer_count: 0, by_customer: [], invoices: [] });
    } catch (e) {
      onShowMessage?.("Failed to load retainage summary.", "error");
    }
    setLoading(false);
  }, [onShowMessage]);

  useEffect(() => { load(); }, [load]);

  const ageDays = (heldAt) => {
    if (!heldAt) return null;
    const ms = Date.now() - new Date(heldAt).getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  };

  return (
    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">Outstanding Retainage</Typography>
          <Typography variant="caption" sx={{ color: mutedText }}>
            Money customers are holding back as retainage. Aged from when we marked the partial payment.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={load} disabled={loading} startIcon={<SyncIcon />}>
          {loading ? "Loading..." : "Refresh"}
        </Button>
      </Box>

      <Card sx={{ background: cardBg, border: `1px solid ${border}`, mb: 2, p: 2 }}>
        <Typography variant="subtitle2" sx={{ color: mutedText }}>Total Outstanding</Typography>
        <Typography variant="h4">{fmt(data.total_outstanding)}</Typography>
        <Typography variant="caption" sx={{ color: mutedText }}>across {data.customer_count} customer{data.customer_count === 1 ? '' : 's'}</Typography>
      </Card>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>By Customer</Typography>
      <Card sx={{ background: cardBg, border: `1px solid ${border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell align="right"># Invoices</TableCell>
              <TableCell align="right">Retainage Held</TableCell>
              <TableCell>Oldest</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.by_customer.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ color: mutedText }}>No outstanding retainage.</TableCell></TableRow>
            )}
            {data.by_customer.map((c) => {
              const age = ageDays(c.oldest_held_at);
              const stale = age !== null && age >= 90;
              return (
                <TableRow key={c.customer_id}>
                  <TableCell sx={{ fontWeight: 500 }}>{c.customer_name}</TableCell>
                  <TableCell align="right">{c.invoice_count}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(c.total_retainage)}</TableCell>
                  <TableCell>
                    {c.oldest_held_at ? String(c.oldest_held_at).slice(0, 10) : "—"}
                    {age !== null && (
                      <Chip
                        size="small"
                        label={`${age}d`}
                        color={stale ? "error" : age >= 60 ? "warning" : "default"}
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>By Invoice</Typography>
      <Card sx={{ background: cardBg, border: `1px solid ${border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell align="right">Invoice Total</TableCell>
              <TableCell align="right">Retainage Held</TableCell>
              <TableCell>Held Since</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.invoices.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: mutedText }}>No outstanding retainage.</TableCell></TableRow>
            )}
            {data.invoices.map((inv) => {
              const age = ageDays(inv.retainage_held_at);
              const stale = age !== null && age >= 90;
              return (
                <TableRow key={inv.invoice_id}>
                  <TableCell>#{inv.invoice_number}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell align="right">{fmt(inv.invoice_total)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: stale ? 'error.main' : 'text.primary' }}>
                    {fmt(inv.retainage_held_amount)}
                  </TableCell>
                  <TableCell>
                    {inv.retainage_held_at ? String(inv.retainage_held_at).slice(0, 10) : "—"}
                    {age !== null && (
                      <Chip
                        size="small"
                        label={`${age}d`}
                        color={stale ? "error" : age >= 60 ? "warning" : "default"}
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
}

// ========================================================================
// CUSTOMER CREDITS VIEW (money we hold on account — overpayments etc.)
// ========================================================================
function CreditsView({ isDark, onShowMessage }) {
  const [credits, setCredits] = useState([]);
  const [openTotal, setOpenTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [applyTarget, setApplyTarget] = useState(null);       // credit in the Apply dialog
  const [applyInvoices, setApplyInvoices] = useState([]);
  const [applyInvoiceId, setApplyInvoiceId] = useState("");
  const [applyAmount, setApplyAmount] = useState("");
  const [refundTarget, setRefundTarget] = useState(null);     // credit in the Refund dialog
  const [refundRef, setRefundRef] = useState("");
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async (sf) => {
    setLoading(true);
    try {
      const status = sf !== undefined ? sf : statusFilter;
      const params = status && status !== "all" ? { status } : {};
      const res = await axios.get("/credits", { params });
      setCredits(res.data.credits || []);
      setOpenTotal(res.data.total_open_credit || 0);
    } catch (err) {
      console.error("Error loading credits:", err);
      onShowMessage("Failed to load customer credits", "error");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openApplyDialog = async (credit) => {
    setApplyTarget(credit);
    setApplyInvoiceId("");
    setApplyAmount("");
    setApplyInvoices([]);
    try {
      const res = await axios.get("/invoices", { params: { customer_id: credit.customer_id, status: "Open" } });
      setApplyInvoices(res.data || []);
    } catch (err) {
      console.error("Error fetching open invoices:", err);
    }
  };

  const applySelectedInvoice = applyInvoices.find(i => String(i.invoice_id) === String(applyInvoiceId));
  const applyMax = applyTarget && applySelectedInvoice
    ? Math.min(applyTarget.remaining_amount,
               (applySelectedInvoice.total || 0) - (applySelectedInvoice.total_paid || 0))
    : 0;

  const handleApply = async () => {
    if (!applyTarget || !applyInvoiceId) return;
    const amount = parseFloat(applyAmount || applyMax);
    setBusy(true);
    try {
      const res = await axios.post(`/credits/${applyTarget.credit_id}/apply`, {
        invoice_id: parseInt(applyInvoiceId, 10),
        amount,
      });
      onShowMessage(res.data.message || "Credit applied.", "success");
      setApplyTarget(null);
      load();
    } catch (err) {
      onShowMessage(err.response?.data?.detail || "Failed to apply credit", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleRefund = async () => {
    if (!refundTarget) return;
    setBusy(true);
    try {
      const res = await axios.post(`/credits/${refundTarget.credit_id}/refund`, {
        refund_method: "Check",
        refund_reference: refundRef || null,
      });
      onShowMessage(res.data.message || "Credit refunded.", "success");
      setRefundTarget(null);
      setRefundRef("");
      load();
    } catch (err) {
      onShowMessage(err.response?.data?.detail || "Failed to refund credit", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleVoid = async (credit) => {
    if (!window.confirm(`Void Credit #${credit.credit_id} ($${credit.remaining_amount.toFixed(2)})? Only for credits created in error.`)) return;
    try {
      const res = await axios.post(`/credits/${credit.credit_id}/void`);
      onShowMessage(res.data.message || "Credit voided.", "success");
      load();
    } catch (err) {
      onShowMessage(err.response?.data?.detail || "Failed to void credit", "error");
    }
  };

  const statusChip = (s) => {
    const map = { open: "info", applied: "success", refunded: "default", void: "default" };
    return <Chip size="small" label={s} color={map[s] || "default"} />;
  };

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
      <Card sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <SavingsIcon color="primary" />
          <Typography variant="h6" sx={{ flex: 1 }}>
            Customer Credits
            {openTotal > 0 && (
              <Chip size="small" color="info" sx={{ ml: 1 }}
                    label={`$${openTotal.toFixed(2)} open on account`} />
            )}
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={statusFilter}
                             onChange={(e, v) => { if (v) { setStatusFilter(v); } }}>
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="applied">Applied</ToggleButton>
            <ToggleButton value="refunded">Refunded</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" onClick={() => load()}>Refresh</Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Money held on account for a customer — usually an overpaid check. Apply it to open
          invoices or record a refund. Credits are created from the Batch Payment dialog
          (overage checkbox) or automatically when a payment exceeds an invoice balance.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
        ) : credits.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No {statusFilter !== "all" ? statusFilter : ""} credits.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Applications</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credits.map((cr) => (
                <TableRow key={cr.credit_id}>
                  <TableCell>{cr.credit_id}</TableCell>
                  <TableCell>{cr.customer_name}</TableCell>
                  <TableCell>{cr.created_at ? cr.created_at.slice(0, 10) : "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 340 }}>
                    <Typography variant="body2" sx={{ whiteSpace: "normal" }}>{cr.reason || "—"}</Typography>
                  </TableCell>
                  <TableCell align="right">${cr.amount.toFixed(2)}</TableCell>
                  <TableCell align="right"><strong>${cr.remaining_amount.toFixed(2)}</strong></TableCell>
                  <TableCell>{statusChip(cr.status)}</TableCell>
                  <TableCell>
                    {(cr.applications || []).map(a => (
                      <Typography key={a.invoice_payment_id} variant="caption" display="block">
                        ${a.amount.toFixed(2)} → #{a.invoice_number} ({a.payment_date})
                      </Typography>
                    ))}
                    {cr.status === "refunded" && cr.refund_date && (
                      <Typography variant="caption" display="block">
                        Refunded {cr.refund_date}{cr.refund_reference ? ` (${cr.refund_reference})` : ""}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {cr.status === "open" && (
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <Button size="small" variant="outlined" onClick={() => openApplyDialog(cr)}>Apply</Button>
                        <Button size="small" variant="outlined" color="warning"
                                onClick={() => setRefundTarget(cr)}>Refund</Button>
                        {Math.abs(cr.remaining_amount - cr.amount) < 0.01 && (
                          <Button size="small" color="error" onClick={() => handleVoid(cr)}>Void</Button>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Apply dialog */}
      <Dialog open={!!applyTarget} onClose={() => setApplyTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply Credit #{applyTarget?.credit_id} — {applyTarget?.customer_name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Credit remaining: <strong>${applyTarget?.remaining_amount?.toFixed(2)}</strong>
          </Typography>
          {applyInvoices.length === 0 ? (
            <Typography color="text.secondary">No open invoices for this customer — use Refund instead.</Typography>
          ) : (
            <>
              <TextField select fullWidth label="Open invoice" value={applyInvoiceId}
                         onChange={(e) => { setApplyInvoiceId(e.target.value); setApplyAmount(""); }}
                         SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}>
                <option value="">Select invoice…</option>
                {applyInvoices.map(inv => (
                  <option key={inv.invoice_id} value={inv.invoice_id}>
                    {`#${inv.invoice_number} — ${inv.order_number || ""} — balance $${((inv.total || 0) - (inv.total_paid || 0)).toFixed(2)}`}
                  </option>
                ))}
              </TextField>
              {applyInvoiceId && (
                <TextField fullWidth type="number" label={`Amount (max $${applyMax.toFixed(2)})`}
                           value={applyAmount} placeholder={applyMax.toFixed(2)}
                           onChange={(e) => setApplyAmount(e.target.value)} />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyTarget(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !applyInvoiceId ||
                    (applyAmount !== "" && (parseFloat(applyAmount) <= 0 || parseFloat(applyAmount) > applyMax + 0.001))}
                  onClick={handleApply}>
            {busy ? "Applying…" : "Apply Credit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={!!refundTarget} onClose={() => setRefundTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Refund Credit #{refundTarget?.credit_id}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Records that <strong>${refundTarget?.remaining_amount?.toFixed(2)}</strong> was refunded
            to {refundTarget?.customer_name}. Cut the actual check first.
          </Typography>
          <TextField fullWidth label="Refund check # (optional)" value={refundRef}
                     onChange={(e) => setRefundRef(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundTarget(null)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={busy} onClick={handleRefund}>
            {busy ? "Recording…" : "Record Refund"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ========================================================================
// INVOICES MODULE (main)
// ========================================================================
function InvoicesModule() {
  const { mode, isDark } = useThemeMode();
  const theme = React.useMemo(() => getInvoiceTheme(mode), [mode]);

  // Tab: Invoices vs Statements
  const [activeTab, setActiveTab] = useState("invoices");
  const [depositsView, setDepositsView] = useState("slips");

  // Unmatched-deposits badge count for the Deposits tab
  const [unmatchedDepositsCount, setUnmatchedDepositsCount] = useState(0);
  const refreshUnmatchedDepositsCount = React.useCallback(async () => {
    try {
      const res = await axios.get("/finance/unmatched-deposits/count");
      setUnmatchedDepositsCount(res.data?.count || 0);
    } catch (e) {
      // Endpoint may briefly be unavailable during deploy; fail silent.
    }
  }, []);
  useEffect(() => { refreshUnmatchedDepositsCount(); }, [refreshUnmatchedDepositsCount]);

  // -----------------------------
  // PARTS MAP (part_id -> product_id)
  // -----------------------------
  const [partsMap, setPartsMap] = useState({});
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchAllParts = async () => {
    try {
      const res = await axios.get("/parts");
      const map = {};
      res.data.forEach((p) => {
        map[p.part_id] = p.product_id;
      });
      setPartsMap(map);
    } catch (err) {
      console.error("Error fetching all parts:", err);
    }
  };

  // -----------------------------
  // Invoice List + Filters
  // -----------------------------
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const [filterStatus, setFilterStatus] = useState("Open");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  
  const [filterCustomerId, setFilterCustomerId] = useState("");
  const [customerFilterList, setCustomerFilterList] = useState([]);

  // -----------------------------
  // Invoice Detail
  // -----------------------------
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  // F19: the invoice's SO /truth assembly groups (per_set_price, sets) keyed by
  // source_assembly_id, tagged with the SO id so a late response for a
  // previously opened invoice is never applied to the current one.
  const [detailTruthGroups, setDetailTruthGroups] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  // Payment inquiries ("what's the status of this payment?" emails) for the open invoice
  const [paymentInquiries, setPaymentInquiries] = useState([]);
  const [openInquiryDialog, setOpenInquiryDialog] = useState(false);
  const [openInquiryMessageId, setOpenInquiryMessageId] = useState(null);
  const inquiriesForRef = useRef(null);

  // States for Editing Tax in Detail View
  const [editTaxRate, setEditTaxRate] = useState(0.0);

  // -----------------------------
  // Payment Dialog
  // -----------------------------
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [payAmount, setPayAmount] = useState(0);
  const [payMethod, setPayMethod] = useState("Check");
  const [payMemo, setPayMemo] = useState("");
  const [payCheckNumber, setPayCheckNumber] = useState("");
  // Disposition: "auto" | "retainage_held" | "writeoff"
  // Drives whether a partial payment leaves invoice Open, holds retainage,
  // or accepts the short pay as final via a companion write-off entry.
  const [payDisposition, setPayDisposition] = useState("auto");

  // -----------------------------
  // Payment Documents (check scans, remittance advices)
  // -----------------------------
  // Files go to the PRIVATE jobq-payment-documents bucket and are read back
  // through short-lived presigned URLs — never a public S3 link.
  const [payDocFile, setPayDocFile] = useState(null);
  const [payDocType, setPayDocType] = useState("Check");
  const [batchDocFile, setBatchDocFile] = useState(null);
  const [batchDocType, setBatchDocType] = useState("Check");
  // Backfill: attach a scan to a payment that was already recorded.
  const [attachTarget, setAttachTarget] = useState(null); // the payment row
  const [attachFile, setAttachFile] = useState(null);
  const [attachDocType, setAttachDocType] = useState("Check");
  const [attachBusy, setAttachBusy] = useState(false);

  // -----------------------------
  // Release Retainage Dialog
  // -----------------------------
  const [openRetainageDialog, setOpenRetainageDialog] = useState(false);
  const [retainageAmount, setRetainageAmount] = useState(0);
  const [retainageMethod, setRetainageMethod] = useState("Check");
  const [retainageMemo, setRetainageMemo] = useState("Retainage release");

  // -----------------------------
  // Batch Payment Dialog
  // -----------------------------
  const [openBatchPayDialog, setOpenBatchPayDialog] = useState(false);
  const [batchCustomerId, setBatchCustomerId] = useState("");
  const [batchCustomersList, setBatchCustomersList] = useState([]);
  const [batchTotalAmount, setBatchTotalAmount] = useState("");
  const [batchCheckNumber, setBatchCheckNumber] = useState("");
  const [batchPayMethod, setBatchPayMethod] = useState("Check");
  const [batchMemo, setBatchMemo] = useState("");
  const [batchOpenInvoices, setBatchOpenInvoices] = useState([]);
  const [batchAllocations, setBatchAllocations] = useState({});
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  // Overpaid check → hold the excess as a customer credit (explicit opt-in)
  const [batchOverageToCredit, setBatchOverageToCredit] = useState(false);
  const [batchOpenCreditTotal, setBatchOpenCreditTotal] = useState(0);

  // Open-credit banner on the invoice detail view
  const [detailCustomerCredits, setDetailCustomerCredits] = useState([]);

  // Open-credit lookup for the invoice detail banner
  useEffect(() => {
    const custId = invoiceDetail?.customer_id;
    if (!custId || invoiceDetail?.status !== "Open") { setDetailCustomerCredits([]); return; }
    let cancelled = false;
    axios.get(`/customers/${custId}/credits`, { params: { status: "open" } })
      .then(res => { if (!cancelled) setDetailCustomerCredits(res.data.credits || []); })
      .catch(() => { if (!cancelled) setDetailCustomerCredits([]); });
    return () => { cancelled = true; };
  }, [invoiceDetail?.customer_id, invoiceDetail?.status, invoiceDetail?.total_paid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyCreditToDetailInvoice = async (credit) => {
    const balance = (invoiceDetail.total || 0) - (invoiceDetail.total_paid || 0);
    const amount = Math.min(credit.remaining_amount, balance);
    if (!window.confirm(`Apply $${amount.toFixed(2)} from Credit #${credit.credit_id} to invoice #${invoiceDetail.invoice_number}?`)) return;
    try {
      const res = await axios.post(`/credits/${credit.credit_id}/apply`, {
        invoice_id: invoiceDetail.invoice_id, amount
      });
      showMessage(res.data.message || "Credit applied.", "success");
      handleSelectInvoice(invoiceDetail.invoice_id);
      fetchInvoices();
    } catch (err) {
      showMessage(err.response?.data?.detail || "Failed to apply credit", "error");
    }
  };

  // -----------------------------
  // Create Invoice Dialog
  // -----------------------------
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  // NEW: Toggle between creating from 'sales_order' or 'direct_job'
  const [invoiceCreationMode, setInvoiceCreationMode] = useState('sales_order'); 

  const [customersList, setCustomersList] = useState([]);
  const [newInvoiceCustomerId, setNewInvoiceCustomerId] = useState("");
  const [jobsListForCustomer, setJobsListForCustomer] = useState([]);
  // The customer whose jobs the Create-invoice picker is loading (a newer pick cancels an older load).
  const pickerCustomerRef = useRef(null);
  const [jobsListLoading, setJobsListLoading] = useState(false);
  
  const todayStr = new Date().toISOString().split("T")[0];
  const shopDay = shopToday(); // the list's past-due mark (calendar days, the shop's Central day)
  const [newInvoiceDate, setNewInvoiceDate] = useState(todayStr);
  const [newLines, setNewLines] = useState([]);

  // *** States for Financials in Create Dialog ***
  const [createShipping, setCreateShipping] = useState(0.0);
  const [createFees, setCreateFees] = useState(0.0);
  const [createDiscount, setCreateDiscount] = useState(0.0);
  const [createTaxRate, setCreateTaxRate] = useState(0.0);
  const [createTaxAmount, setCreateTaxAmount] = useState(0.0);

  // *** States for additional invoice fields (previously missing) ***
  const [newDueDate, setNewDueDate] = useState("");
  const [newPaymentTerms, setNewPaymentTerms] = useState("");
  const [newNotes, setNewNotes] = useState("");

  // *** States for job filtering and selection ***
  const [jobFilterMasterNum, setJobFilterMasterNum] = useState("");
  const [jobFilterCustomerPo, setJobFilterCustomerPo] = useState("");
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());

  // *** States for Sales Orders ***
  const [salesOrdersList, setSalesOrdersList] = useState([]);
  const [newInvoiceSalesOrderId, setNewInvoiceSalesOrderId] = useState("");
  const [soBillingStatus, setSoBillingStatus] = useState(null);
  const [yieldPolicy, setYieldPolicy] = useState(null);

  // -----------------------------
  // UX State: Snackbar & Audit
  // -----------------------------
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // success, error, warning, info

  const [openAuditDialog, setOpenAuditDialog] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [isAuditing, setIsAuditing] = useState(false);

  // -----------------------------
  // Email Invoice Modal
  // -----------------------------
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailContacts, setEmailContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [ccContactIds, setCcContactIds] = useState([]);
  const [sendCopyToSelf, setSendCopyToSelf] = useState(false);
  const [emailBody, setEmailBody] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // -----------------------------
  // Helpers
  // -----------------------------
  const showMessage = (msg, severity="info") => {
      setSnackbarMessage(msg);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
  };
  const handleCloseSnackbar = () => setSnackbarOpen(false);

  // -----------------------------
  // Fetch Invoices
  // -----------------------------
  const fetchInvoices = async () => {
    try {
      // features/invoices/invoiceData: a search spans ALL statuses (customer/date narrowing still
      // applies); else the status filter.
      const data = await loadInvoices({
        search: filterSearch, status: filterStatus, invoiceNumber: filterInvoiceNumber,
        startDate: filterStartDate, endDate: filterEndDate, customerId: filterCustomerId,
      });
      setInvoiceList(data);
      // Alphabetical customer list for the filter dropdown
      setCustomerFilterList(customersFromInvoices(data));

    } catch (err) {
      console.error("Failed to fetch invoices:", err);
      showMessage("Error fetching invoices", "error");
    }
  };

  const fetchInvoiceDetail = async (invoiceId) => {
    try {
      const invoiceData = await loadInvoice(invoiceId); // features/invoices/invoiceData

      if (invoiceData.lines) {
        invoiceData.lines = invoiceData.lines.map((ln) => {
          const numericId = ln.part_id;
          const foundProdId = partsMap[numericId] || "";
          return {
            ...ln,
            part_id: numericId,
            product_id: ln.product_id || foundProdId,
          };
        });
      }

      setInvoiceDetail(invoiceData);

      // F19: the read-only detail groups ASSEMBLY rows at the ORDER's per-set
      // price. Load the SO's /truth groups; an order-less invoice, an older
      // API or a failed fetch leaves the local formula in charge.
      setDetailTruthGroups(null);
      if (invoiceData.sales_order_id) {
        const soIdForTruth = invoiceData.sales_order_id;
        axios.get(`/sales_orders/${soIdForTruth}/truth`)
          .then((t) => setDetailTruthGroups({ sales_order_id: soIdForTruth, groups: indexTruthGroups(t.data?.lines) }))
          .catch(() => setDetailTruthGroups(null));
      }
      
      // Calculate derived tax rate for edit view
      const sub = invoiceData.subtotal || 0;
      const ship = invoiceData.shipping_amount || 0;
      const fees = invoiceData.additional_fees || 0;
      const disc = invoiceData.discount_amount || 0;
      const tax = invoiceData.tax_amount || 0;
      
      const taxableBase = sub + fees - disc; // Exclude shipping from base
      if (taxableBase > 0) {
        const derivedRate = (tax / taxableBase) * 100;
        setEditTaxRate(parseFloat(derivedRate.toFixed(3)));
      } else {
        setEditTaxRate(0.0);
      }

      // Fetch email history from snapshots
      fetchEmailHistory(invoiceId);
      fetchPaymentInquiries(invoiceId);

    } catch (err) {
      console.error("Error fetching invoice detail:", err);
      showMessage("Error fetching invoice detail", "error");
    }
  };

  const fetchEmailHistory = async (invoiceId) => {
    setEmailHistoryLoading(true);
    try {
      setEmailHistory(await loadInvoiceEmails(invoiceId)); // features/invoices/invoiceData
    } catch (err) {
      console.error("Error fetching email history:", err);
      setEmailHistory([]);
    } finally {
      setEmailHistoryLoading(false);
    }
  };

  const fetchPaymentInquiries = async (invoiceId) => {
    inquiriesForRef.current = invoiceId;
    setPaymentInquiries([]);
    setOpenInquiryMessageId(null);
    try {
      const inquiries = await loadPaymentInquiries(invoiceId); // features/invoices/invoiceData
      // A slow answer for an invoice the user already left must not land on this one.
      if (inquiriesForRef.current === invoiceId) setPaymentInquiries(inquiries);
    } catch (err) {
      console.error("Error fetching payment inquiries:", err);
    }
  };

  const handleInquirySent = (result) => {
    setOpenInquiryDialog(false);
    showMessage(result.message || "Payment inquiry sent.", result.duplicate ? "info" : "success");
    const ids = new Set(result.invoice_ids || [invoiceDetail?.invoice_id]);
    if (!result.duplicate && result.sent_at) {
      setInvoiceList(prev => prev.map(inv => ids.has(inv.invoice_id)
        ? { ...inv, last_inquiry_at: result.sent_at, inquiry_count: (inv.inquiry_count || 0) + 1 }
        : inv));
    }
    if (invoiceDetail) fetchPaymentInquiries(invoiceDetail.invoice_id);
  };

  useEffect(() => {
    fetchAllParts();
    fetchInvoices();
  }, []);

  const handleFilterApply = () => {
    fetchInvoices();
  };

  const handleSelectInvoice = (id) => {
    setSelectedInvoiceId(id);
    setIsEditing(false);
    fetchInvoiceDetail(id);
  };

    // -----------------------------
  // Edit Invoice
  // -----------------------------
  const handleEditInvoice = () => {
    setIsEditing(true);
  };

  // -----------------------------
  // Delete a single line item
  // -----------------------------
  const handleDeleteLineItem = async (line, index) => {
    if (!invoiceDetail) return;

    if (!line.invoice_line_id) {
      // Just remove it locally if not saved on server yet
      const updated = [...invoiceDetail.lines];
      updated.splice(index, 1);
      
      // Recalculate totals locally for display
      const newLinesSub = updated.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
      const ship = invoiceDetail.shipping_amount || 0;
      const fees = invoiceDetail.additional_fees || 0;
      const disc = invoiceDetail.discount_amount || 0;
      
      const taxableBase = newLinesSub + fees - disc; // Exclude shipping
      const newTaxAmt = taxableBase * (editTaxRate / 100);
      const newTotal = newLinesSub + ship + fees - disc + newTaxAmt;

      setInvoiceDetail({ ...invoiceDetail, lines: updated, subtotal: newLinesSub, tax_amount: newTaxAmt, total: newTotal });
      return;
    }

    if (!window.confirm("Are you sure you want to delete this line?")) return;

    try {
      await axios.delete(
        `/invoices/${invoiceDetail.invoice_id}/lines/${line.invoice_line_id}`
      );
      showMessage("Line item deleted", "success");
      fetchInvoiceDetail(invoiceDetail.invoice_id);
    } catch (err) {
      console.error("Error deleting line item:", err);
      showMessage("Failed to delete line item", "error");
    }
  };

  const handleRemoveEditLine = (index) => {
    if (!invoiceDetail) return;
    const line = invoiceDetail.lines[index];
    handleDeleteLineItem(line, index);
  };

  // --- Auto-Recalculate Tax in Edit Mode ---
  const handleEditTaxRateChange = (newRate) => {
      setEditTaxRate(newRate);
      if (!invoiceDetail) return;
      
      const sub = invoiceDetail.lines.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
      const ship = invoiceDetail.shipping_amount || 0;
      const fees = invoiceDetail.additional_fees || 0;
      const disc = invoiceDetail.discount_amount || 0;
      
      const taxableBase = Math.max(0, sub + fees - disc); // Exclude shipping
      const newTax = taxableBase * (newRate / 100);
      const newTotal = sub + ship + fees - disc + newTax;
      
      setInvoiceDetail(prev => ({
          ...prev,
          tax_amount: parseFloat(newTax.toFixed(2)),
          total: parseFloat(newTotal.toFixed(2))
      }));
  };

  const handleEditTaxAmountChange = (newAmount) => {
      if (!invoiceDetail) return;
      
      const sub = invoiceDetail.lines.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
      const ship = invoiceDetail.shipping_amount || 0;
      const fees = invoiceDetail.additional_fees || 0;
      const disc = invoiceDetail.discount_amount || 0;
      
      const taxableBase = Math.max(0, sub + fees - disc); // Exclude shipping
      const newTotal = sub + ship + fees - disc + newAmount;
      
      setInvoiceDetail(prev => ({
          ...prev,
          tax_amount: newAmount,
          total: parseFloat(newTotal.toFixed(2))
      }));
      
      // Reverse calculate rate
      if (taxableBase > 0) {
          const derivedRate = (newAmount / taxableBase) * 100;
          setEditTaxRate(parseFloat(derivedRate.toFixed(3)));
      }
  };

  // -----------------------------
  // Save Invoice
  // -----------------------------
  const handleSaveInvoice = async () => {
    if (!invoiceDetail) return;
    
    // Perform validation using the context we loaded in GET /invoices/{id}
    const msg = validateInvoiceRules(invoiceDetail.lines, invoiceDetail.yield_policy);
    
    if (msg) {
        // Allow operator override via confirmation
        if (!window.confirm(msg + "Do you want to proceed with these changes?")) return;
    }

    try {
      const payload = {
        invoice_date: invoiceDetail.invoice_date,
        due_date: invoiceDetail.due_date,
        payment_terms: invoiceDetail.payment_terms,
        shipping_amount: invoiceDetail.shipping_amount,
        additional_fees: invoiceDetail.additional_fees,
        discount_amount: invoiceDetail.discount_amount,
        tax_amount: invoiceDetail.tax_amount,
        notes: invoiceDetail.notes,
        lines: invoiceDetail.lines.map((ln) => ({
          invoice_line_id: ln.invoice_line_id || null,
          part_id: ln.part_id && ln.part_id !== 0 ? ln.part_id : 9999,
          product_id: ln.product_id && ln.product_id.trim() !== "" ? ln.product_id : "9999",
          customer_po: ln.customer_po && ln.customer_po.trim() !== "" ? ln.customer_po : "N/A",
          customer_so: ln.customer_so && ln.customer_so.trim() !== "" ? ln.customer_so : null,
          job_id: ln.job_id && ln.job_id > 0 ? ln.job_id : null,
          description: ln.description && ln.description.trim() !== "" ? ln.description : "Custom Item",
          quantity: parseInt(ln.quantity, 10) || 1,
          unit_price: parseFloat(ln.unit_price) || 0,
          sales_order_line_id: ln.sales_order_line_id || null,
          source_assembly_id: ln.source_assembly_id || null,
        })),
      };

      const res = await axios.patch(`/invoices/${invoiceDetail.invoice_id}`, payload);

      setIsEditing(false);
      fetchInvoiceDetail(invoiceDetail.invoice_id);
      showMessage(res.data?.message || "Invoice updated", "success");
    } catch (err) {
      console.error("Error saving invoice:", err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 502) {
        showMessage(detail || "Failed to sync changes to Bill.com. Local changes were rolled back. Please try again.", "warning");
      } else {
        showMessage(detail || "Failed to update invoice", "error");
      }
    }
};

  // -----------------------------
  // Void/Delete Invoice
  // -----------------------------
  const handleVoidInvoice = async () => {
    if (!invoiceDetail) return;
    if (!window.confirm("Are you sure you want to void/delete this invoice?"))
      return;
    try {
      await axios.delete(`/invoices/${invoiceDetail.invoice_id}?pin=ignored`);
      showMessage("Invoice deleted", "success");
      setInvoiceDetail(null);
      setSelectedInvoiceId(null);
      fetchInvoices();
    } catch (err) {
      console.error("Error deleting invoice:", err);
      showMessage("Failed to delete invoice", "error");
    }
  };

  // -----------------------------
  // Mark Invoice Paid
  // -----------------------------
  const handleOpenPayDialog = () => {
    if (!invoiceDetail) return;
    const total = parseFloat(invoiceDetail.total || 0);
    const paid = parseFloat(invoiceDetail.total_paid || 0);
    setPayAmount(Math.max(0, total - paid));
    setPayMethod("Check");
    setPayMemo("");
    setPayCheckNumber("");
    setPayDisposition("auto");
    setOpenPayDialog(true);
  };

  const handleClosePayDialog = () => {
    setOpenPayDialog(false);
    setPayDocFile(null);
  };

  // Upload a scan against a payment or a payment group. `anchor` is either
  // "invoice_payments" (standalone payment) or "payment_groups" (one check
  // across N invoices). Returns true on success.
  const uploadPaymentDocument = async (anchor, anchorId, file, docType, pin) => {
    const resolvedPin = pin || await pinPrompt("Enter your PIN to attach the scan:");
    if (!resolvedPin) {
      showMessage("Scan not attached — no PIN entered. You can attach it from Payment History.", "warning");
      return false;
    }
    const form = new FormData();
    form.append("file", file);
    form.append("pin", resolvedPin);
    form.append("display_name", file.name);
    form.append("doc_type", docType);
    try {
      await axios.post(`/${anchor}/${anchorId}/documents`, form);
      return true;
    } catch (err) {
      console.error("Error uploading payment document:", err);
      // The payment itself is already recorded — say so, so nobody re-records it.
      showMessage(
        `Payment saved, but the scan failed to upload: ${err.response?.data?.detail || "unknown error"}. Attach it from Payment History.`,
        "warning"
      );
      return false;
    }
  };

  // Backfill — attach a scan to a payment that was recorded earlier (or to
  // the check group it belongs to, so it covers every invoice that check paid).
  const handleSubmitAttach = async () => {
    if (!attachTarget || !attachFile) return;
    const useGroup = !!attachTarget.payment_group_id;
    setAttachBusy(true);
    const ok = await uploadPaymentDocument(
      useGroup ? "payment_groups" : "invoice_payments",
      useGroup ? attachTarget.payment_group_id : attachTarget.invoice_payment_id,
      attachFile, attachDocType);
    setAttachBusy(false);
    if (ok) {
      showMessage(
        useGroup
          ? `Scan attached to check #${attachTarget.check_number || attachTarget.payment_group_id} — visible on every invoice it paid.`
          : "Scan attached to the payment.",
        "success");
      setAttachTarget(null);
      setAttachFile(null);
      if (invoiceDetail) fetchInvoiceDetail(invoiceDetail.invoice_id);
    }
  };

  const handleDeletePaymentDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.display_name || doc.original_filename}"? This removes the stored file.`)) return;
    try {
      await axios.delete(`/payment_documents/${doc.payment_document_id}`);
      showMessage("Document deleted.", "success");
      if (invoiceDetail) fetchInvoiceDetail(invoiceDetail.invoice_id);
    } catch (err) {
      console.error("Error deleting payment document:", err);
      showMessage(err.response?.data?.detail || "Failed to delete the document", "error");
    }
  };

  const handleConfirmPayment = async () => {
    if (!invoiceDetail) return;
    const invoiceId = invoiceDetail.invoice_id;
    // Ask for the PIN before recording so the operator isn't prompted after
    // the money is already booked.
    let docPin = null;
    if (payDocFile) {
      docPin = await pinPrompt("Enter your PIN to attach the scan:");
      if (!docPin) return;
    }
    try {
      const payload = {
        payment_amount: payAmount,
        payment_method: payMethod,
        memo: payMemo,
        disposition: payDisposition,
        // The deposit slip prints it for the teller.
        check_number: payMethod === "Check" ? (payCheckNumber.trim() || null) : null,
      };
      const response = await axios.post(`/invoices/${invoiceId}/payments`, payload);

      if (payDocFile && response.data.invoice_payment_id) {
        await uploadPaymentDocument(
          "invoice_payments", response.data.invoice_payment_id,
          payDocFile, payDocType, docPin);
        setPayDocFile(null);
      }

      // Surface real backend status (Paid vs Open) instead of hard-coded copy
      if (response.data.sync_error) {
        showMessage(
          `Payment recorded locally, but Bill.com sync failed: ${response.data.error_details || 'Unknown error'}. Please sync manually later.`,
          "warning"
        );
      } else {
        const statusText = response.data.invoice_status || "Open";
        let extra = "";
        if (response.data.retainage_held_amount) {
          extra = ` Retainage held: $${Number(response.data.retainage_held_amount).toFixed(2)}.`;
        } else if (response.data.writeoff_payment_id) {
          extra = " Short-pay write-off recorded.";
        }
        showMessage(`Payment recorded. Invoice is now ${statusText}.${extra}`, "success");
      }

      fetchInvoiceDetail(invoiceId);
      handleClosePayDialog();
    } catch (err) {
      console.error("Error recording payment:", err);
      showMessage(err.response?.data?.detail || "Failed to record payment", "error");
    }
  };

  // -----------------------------
  // Release Retainage
  // -----------------------------
  const handleOpenRetainageDialog = () => {
    if (!invoiceDetail) return;
    setRetainageAmount(parseFloat(invoiceDetail.retainage_held_amount || 0));
    setRetainageMethod("Check");
    setRetainageMemo("Retainage release");
    setOpenRetainageDialog(true);
  };

  const handleConfirmReleaseRetainage = async () => {
    if (!invoiceDetail) return;
    const invoiceId = invoiceDetail.invoice_id;
    try {
      const payload = {
        payment_amount: retainageAmount,
        payment_method: retainageMethod,
        memo: retainageMemo,
      };
      const response = await axios.post(`/invoices/${invoiceId}/release-retainage`, payload);
      showMessage(
        `Retainage released ($${Number(retainageAmount).toFixed(2)}). Invoice is now ${response.data.invoice_status}.`,
        "success"
      );
      fetchInvoiceDetail(invoiceId);
      setOpenRetainageDialog(false);
    } catch (err) {
      console.error("Error releasing retainage:", err);
      showMessage(err.response?.data?.detail || "Failed to release retainage", "error");
    }
  };

  // -----------------------------
  // Batch Payment Handlers
  // -----------------------------
  const handleOpenBatchPayDialog = async () => {
    setBatchCustomerId("");
    setBatchTotalAmount("");
    setBatchCheckNumber("");
    setBatchPayMethod("Check");
    setBatchMemo("");
    setBatchOpenInvoices([]);
    setBatchAllocations({});
    setBatchOverageToCredit(false);
    setBatchOpenCreditTotal(0);
    setOpenBatchPayDialog(true);
    try {
      const res = await axios.get("/customers");
      setBatchCustomersList(res.data);
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  const handleBatchCustomerChange = async (custId) => {
    setBatchCustomerId(custId);
    setBatchAllocations({});
    setBatchOpenCreditTotal(0);
    if (!custId) { setBatchOpenInvoices([]); return; }
    try {
      const res = await axios.get("/invoices", { params: { customer_id: custId, status: "Open" } });
      const invoices = res.data || [];
      setBatchOpenInvoices(invoices);
    } catch (err) {
      console.error("Error fetching open invoices:", err);
      setBatchOpenInvoices([]);
    }
    try {
      const cr = await axios.get(`/customers/${custId}/credits`, { params: { status: "open" } });
      setBatchOpenCreditTotal(cr.data.total_open_credit || 0);
    } catch { /* older API — no credits endpoint */ }
  };

  const handleBatchAutoAllocate = async () => {
    if (!batchCustomerId || !batchTotalAmount) {
      showMessage("Select a customer and enter a total amount first.", "warning");
      return;
    }
    try {
      const res = await axios.get("/payments/auto-allocate", {
        params: { customer_id: batchCustomerId, amount: parseFloat(batchTotalAmount) }
      });
      const newAllocations = {};
      (res.data.proposed_allocations || []).forEach(a => {
        if (a.allocation > 0) newAllocations[a.invoice_id] = a.allocation;
      });
      setBatchAllocations(newAllocations);
      if (res.data.warning) showMessage(res.data.warning, "warning");
    } catch (err) {
      console.error("Error auto-allocating:", err);
      showMessage(err.response?.data?.detail || "Failed to auto-allocate", "error");
    }
  };

  const batchAllocSum = useMemo(() => {
    return Object.values(batchAllocations).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  }, [batchAllocations]);

  const handleConfirmBatchPayment = async () => {
    const totalAmt = parseFloat(batchTotalAmount);
    if (!totalAmt || totalAmt <= 0) { showMessage("Enter a valid total amount.", "error"); return; }

    const allocations = Object.entries(batchAllocations)
      .filter(([, amt]) => parseFloat(amt) > 0)
      .map(([invoiceId, amt]) => ({ invoice_id: parseInt(invoiceId), amount: parseFloat(amt) }));

    if (allocations.length === 0) { showMessage("No allocations specified.", "error"); return; }

    const allocSum = allocations.reduce((s, a) => s + a.amount, 0);
    const overage = totalAmt - allocSum;
    if (overage < -0.01) {
      showMessage(`Total ($${totalAmt.toFixed(2)}) is less than allocations ($${allocSum.toFixed(2)}).`, "error");
      return;
    }
    if (overage > 0.01 && !batchOverageToCredit) {
      showMessage(`Total exceeds allocations by $${overage.toFixed(2)}. Check "Hold overage as customer credit" to record it, or correct the amounts.`, "error");
      return;
    }

    // PIN first — don't interrupt the operator after the money is booked.
    let docPin = null;
    if (batchDocFile) {
      docPin = await pinPrompt("Enter your PIN to attach the scan:");
      if (!docPin) return;
    }

    setBatchSubmitting(true);
    try {
      const payload = {
        customer_id: parseInt(batchCustomerId),
        total_amount: totalAmt,
        payment_method: batchPayMethod,
        check_number: batchCheckNumber || null,
        memo: batchMemo || null,
        allocations,
        ...(overage > 0.01 && batchOverageToCredit ? { overage_disposition: "credit" } : {})
      };
      const res = await axios.post("/payments/batch", payload);

      // One physical check → one scan on the GROUP, shared by every invoice
      // the check paid.
      if (batchDocFile && res.data.payment_group_id) {
        await uploadPaymentDocument(
          "payment_groups", res.data.payment_group_id,
          batchDocFile, batchDocType, docPin);
        setBatchDocFile(null);
      }

      const syncErrors = (res.data.allocations || []).filter(a => a.sync_error);
      const creditNote = res.data.customer_credit
        ? ` $${res.data.customer_credit.amount.toFixed(2)} overage held as Credit #${res.data.customer_credit.credit_id}.`
        : "";
      if (syncErrors.length > 0) {
        showMessage(`Payment recorded for ${allocations.length} invoice(s), but ${syncErrors.length} Bill.com sync(s) failed.${creditNote}`, "warning");
      } else {
        showMessage(`Batch payment recorded for ${allocations.length} invoice(s).${creditNote}`, "success");
      }

      setOpenBatchPayDialog(false);
      fetchInvoices();
      if (invoiceDetail) fetchInvoiceDetail(invoiceDetail.invoice_id);
    } catch (err) {
      console.error("Error recording batch payment:", err);
      showMessage(err.response?.data?.detail || "Failed to record batch payment", "error");
    } finally {
      setBatchSubmitting(false);
    }
  };

  // -----------------------------
  // View/Download PDF
  // -----------------------------
  const handleOpenPdf = async () => {
    if (!invoiceDetail) return;
    const invoiceId = invoiceDetail.invoice_id;
    const displayNum = invoiceDetail.invoice_number || invoiceId;
    try {
      const response = await axios.get(`/invoices/${invoiceId}/pdf`, {
        responseType: "arraybuffer",
      });
      const file = new Blob([response.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);

      // Trigger download
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `Invoice_${displayNum}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(fileURL);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      showMessage("Failed to download PDF", "error");
    }
  };

  // -----------------------------
  // Sync to Bill.com
  // -----------------------------
  const handleSyncToBillCom = async () => {
      if (!invoiceDetail || !invoiceDetail.invoice_id) return;
      if (!window.confirm("This will create a new invoice in Bill.com. Proceed?")) return;

      try {
          const res = await axios.post(`/invoices/${invoiceDetail.invoice_id}/sync_to_bill_com`);
          showMessage(res.data.message, "success");
          // Refresh the invoice detail to get the new bill_com_invoice_id
          fetchInvoiceDetail(invoiceDetail.invoice_id);
      } catch (err) {
          console.error("Error syncing to Bill.com:", err);
          showMessage(err.response?.data?.detail || "Failed to sync to Bill.com", "error");
      }
  };

  const handleSyncPayments = async () => {
      setIsSyncing(true);
      try {
          const res = await axios.post("/invoices/sync_from_bill_com");
          showMessage(res.data.message, "success");
          fetchInvoices(); // Refresh the entire list
      } catch (err) {
          console.error("Error syncing payments:", err);
          showMessage(err.response?.data?.detail || "Failed to sync payments from Bill.com", "error");
      } finally {
          setIsSyncing(false);
      }
  };

  // --- NEW: Audit Sync Function ---
  const handleAuditSync = async () => {
    if (!invoiceDetail || !invoiceDetail.invoice_id) return;
    setIsAuditing(true);
    setAuditResult(null);
    setOpenAuditDialog(true);

    try {
        const res = await axios.get(`/invoices/${invoiceDetail.invoice_id}/audit_bill_com`);
        setAuditResult(res.data);
    } catch (err) {
        console.error("Audit failed:", err);
        setAuditResult({ error: "Failed to connect to Bill.com for audit." });
    } finally {
        setIsAuditing(false);
    }
  };

  // -----------------------------
  // Email Invoice Functions
  // -----------------------------
  const handleOpenEmailDialog = async () => {
    if (!invoiceDetail || !invoiceDetail.customer_id) return;

    // Fetch contacts for this customer
    try {
      const res = await axios.get(`/customers/${invoiceDetail.customer_id}/contacts`);
      setEmailContacts(res.data.filter(c => c.email)); // Only show contacts with emails

      // Pre-select first contact with email if available
      const firstWithEmail = res.data.find(c => c.email);
      if (firstWithEmail) {
        setSelectedContactId(firstWithEmail.contact_id);
      } else {
        setSelectedContactId("");
      }
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setEmailContacts([]);
      setSelectedContactId("");
    }

    // Set default email body
    const invoiceNum = invoiceDetail.invoice_number || invoiceDetail.invoice_id;
    setEmailBody(`Hello,\n\nPlease find the attached Invoice #${invoiceNum} for your review.\n\nIf you have any questions, please don't hesitate to reach out.\n\nThank you for your business,\nKanCraft MFG`);

    setOpenEmailDialog(true);
  };

  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
    setEmailContacts([]);
    setSelectedContactId("");
    setCcContactIds([]);
    setSendCopyToSelf(false);
    setEmailBody("");
  };

  const handleSendInvoiceEmail = async () => {
    if (!invoiceDetail || !selectedContactId) {
      showMessage("Please select a contact to send the email to.", "warning");
      return;
    }

    setIsSendingEmail(true);
    try {
      const res = await axios.post(`/invoices/${invoiceDetail.invoice_id}/email`, {
        contact_id: selectedContactId,
        email_body: emailBody,
        cc_contact_ids: ccContactIds,
        send_copy_to_self: sendCopyToSelf
      }, { headers: emailSenderHeaders() });
      showMessage(res.data.message || "Invoice emailed successfully!", "success");
      handleCloseEmailDialog();

      // Update local state with new last_emailed_at
      const newEmailedAt = res.data.last_emailed_at || new Date().toISOString();
      if (invoiceDetail) {
        setInvoiceDetail(prev => prev ? { ...prev, last_emailed_at: newEmailedAt } : prev);
      }
      // Update the list item so the email icon appears immediately
      setInvoiceList(prev => prev.map(inv =>
        inv.invoice_id === invoiceDetail.invoice_id
          ? { ...inv, last_emailed_at: newEmailedAt }
          : inv
      ));
      // Refresh email history
      fetchEmailHistory(invoiceDetail.invoice_id);
    } catch (err) {
      console.error("Error sending invoice email:", err);
      showMessage(err.response?.data?.detail || "Failed to send email", "error");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // -----------------------------
  // Create Invoice Flow
  // -----------------------------
  const handleOpenCreateDialog = async () => {
    setOpenCreateDialog(true);
    setInvoiceCreationMode('sales_order'); // Default to SO mode
    setNewInvoiceDate(todayStr);
    setNewInvoiceCustomerId("");
    pickerCustomerRef.current = null;
    setJobsListForCustomer([]);
    setJobsListLoading(false);
    setNewLines([]);
    
    // Reset financial states
    setCreateShipping(0.0);
    setCreateFees(0.0);
    setCreateDiscount(0.0);
    setCreateTaxRate(0.0);
    setCreateTaxAmount(0.0);

    // Reset additional invoice fields
    setNewDueDate("");
    setNewPaymentTerms("");
    setNewNotes("");

    // Reset filters and selection
    setJobFilterMasterNum("");
    setJobFilterCustomerPo("");
    setSelectedJobIds(new Set());

    // Reset Sales Order selection
    setNewInvoiceSalesOrderId("");
    setSalesOrdersList([]);
    setSoBillingStatus(null);

    try {
      const res = await axios.get("/customers");
      setCustomersList(res.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  const handleCloseCreateDialog = () => setOpenCreateDialog(false);

  // Switching creation mode resets selections but keeps customer
  const handleCreationModeChange = (event, newMode) => {
    if (newMode !== null) {
      setInvoiceCreationMode(newMode);
      // Reset specific selections when switching modes
      setNewInvoiceSalesOrderId("");
      setSoBillingStatus(null);
      setSelectedJobIds(new Set());
      setNewLines([]);
      setJobFilterMasterNum("");
      setJobFilterCustomerPo("");
      // Recalculate financial reset? Keep existing values for now.
    }
  };

  const handleSelectCustomer = async (custId) => {
    setNewInvoiceCustomerId(custId);
    pickerCustomerRef.current = custId;
    setJobsListLoading(!!custId);
    
    // Reset downstream selections
    setNewInvoiceSalesOrderId("");
    setSalesOrdersList([]);
    setSoBillingStatus(null);
    setJobsListForCustomer([]);
    setNewLines([]);
    setSelectedJobIds(new Set());
    
    // Find customer to get tax rate (stored as decimal, convert to percentage for UI)
    const customer = customersList.find(c => c.customer_id === custId);
    setCreateTaxRate(customer ? ((customer.tax_rate || 0.0) * 100) : 0.0);
    setCreateTaxAmount(0.0); // Reset amount on customer change
    setNewPaymentTerms(customer?.payment_terms || "");
    
    if (!custId) return;

    try {
      // 1. Fetch Open Sales Orders for this customer
      const soRes = await axios.get("/sales_orders", { 
          params: { customer_id: custId, status: 'Open' } 
      });
      if (pickerCustomerRef.current !== custId) return; // another customer was picked meanwhile
      setSalesOrdersList(soRes.data);

      // 2. Fetch Jobs (for both modes - used for filtering in Direct Job mode). The picker reads
      // job fields only, so summary rows (no tasks/parts), page by page: one response for
      // Bad Idea Co. (6,800+ jobs, 20 MB) passed Lambda's 6 MB limit (utils/jobPages.js).
      // (A per-job GET /jobs/{id}/assemblies loop used to run here; nothing read its result.)
      const customerJobs = await fetchAllJobPages(
        (page) => axios.get("/jobs", { params: { customer_id: custId, summary: true, ...page } }).then((res) => res.data),
        { isCancelled: () => pickerCustomerRef.current !== custId }
      );
      if (customerJobs === null) return; // another customer was picked meanwhile
      setJobsListForCustomer(customerJobs);
      setJobsListLoading(false);
    } catch (err) {
      console.error("Error fetching data for customer:", err);
      if (pickerCustomerRef.current === custId) setJobsListLoading(false);
    }
  };
  
  // Auto-calculate due date from invoice date + payment terms
  const calculateDueDate = (invoiceDate, terms) => {
    if (!invoiceDate || !terms) return "";
    const base = new Date(invoiceDate + "T00:00:00"); // Avoid timezone shift
    if (isNaN(base.valueOf())) return "";

    if (terms === "Due on Receipt") return invoiceDate;

    const match = terms.match(/^Net\s+(\d+)$/);
    if (match) {
      const days = parseInt(match[1], 10);
      base.setDate(base.getDate() + days);
      return base.toISOString().split("T")[0];
    }
    return ""; // Unknown terms format — leave blank
  };

  // Auto-set due date when payment terms or invoice date change (Create Dialog)
  useEffect(() => {
    if (!openCreateDialog) return;
    const computed = calculateDueDate(newInvoiceDate, newPaymentTerms);
    if (computed) setNewDueDate(computed);
  }, [newPaymentTerms, newInvoiceDate, openCreateDialog]);

  // Recalculate create dialog tax when inputs change
  useEffect(() => {
      // Sum line items
      const subtotal = newLines.reduce((acc, ln) => acc + (ln.quantity * ln.unit_price), 0);
      const taxableBase = Math.max(0, subtotal + createFees - createDiscount); // Exclude shipping
      const taxAmt = taxableBase * (createTaxRate / 100);

      setCreateTaxAmount(parseFloat(taxAmt.toFixed(2)));
  }, [newLines, createFees, createDiscount, createTaxRate]); // Removed createShipping from dependency


  // Manual Tax Amount Override in Create Dialog
  const handleCreateTaxAmountOverride = (newAmount) => {
      const subtotal = newLines.reduce((acc, ln) => acc + (ln.quantity * ln.unit_price), 0);
      const taxableBase = Math.max(0, subtotal + createFees - createDiscount); // Exclude shipping
      
      setCreateTaxAmount(newAmount);
      
      // Reverse calc rate if base > 0
      if (taxableBase > 0) {
          const derivedRate = (newAmount / taxableBase) * 100;
          setCreateTaxRate(parseFloat(derivedRate.toFixed(3)));
      }
  };


  // *** KEY FEATURE: Auto-Fill Logic when Sales Order Selected ***
  const handleSelectSalesOrder = async (soId) => {
    setNewInvoiceSalesOrderId(soId);
    if (!soId) {
        setSoBillingStatus(null);
        setNewLines([]);
        // Reset financials
        setCreateShipping(0);
        setCreateFees(0);
        setCreateDiscount(0);
        // Keep tax rate from customer default
        return;
    }

    try {
        // F19: the order's /truth assembly groups carry per_set_price × sets —
        // the figure the quote card and the SO PDF print — so the invoice bills
        // THAT, not a local re-derivation. An older API or a failed fetch
        // leaves the local formula (below) in charge.
        const [res, truthGroups] = await Promise.all([
          axios.get(`/sales_orders/${soId}/billing_status`),
          axios.get(`/sales_orders/${soId}/truth`)
            .then((t) => indexTruthGroups(t.data?.lines))
            .catch(() => ({})),
        ]);
        setSoBillingStatus(res.data);

        // Store yield policy for validation
        setYieldPolicy({
            name: res.data.yield_policy_name,
            allowOverShip: res.data.allow_over_ship,
            allowShortClose: res.data.allow_short_close,
            minRequiredType: res.data.min_required_type
        });
        
        // Auto-Populate Lines from Sales Order
        // Step 1: Separate individual lines from assembly component lines
        const rawLines = res.data.lines;
        const rawPo = res.data.customer_po;
        const rawSo = res.data.customer_so;
        const orderNum = res.data.order_number;
        const cleanPo = (rawPo && rawPo !== orderNum) ? rawPo : "N/A";
        const cleanSo = rawSo || null;

        const individualLines = rawLines.filter(l => !l.source_assembly_id);
        const assemblyLines = rawLines.filter(l => l.source_assembly_id);

        // Step 2: Group assembly component lines by source_assembly_id
        const assemblyGroups = {};
        assemblyLines.forEach(l => {
            if (!assemblyGroups[l.source_assembly_id]) {
                assemblyGroups[l.source_assembly_id] = {
                    assembly_name: l.assembly_name,
                    primary_part_id: l.primary_part_id,
                    primary_part_quantity: l.primary_part_quantity || 1,
                    source_assembly_id: l.source_assembly_id,
                    components: []
                };
            }
            assemblyGroups[l.source_assembly_id].components.push(l);
        });

        // Step 3: Build grouped assembly lines (mirrors SO PDF logic)
        const groupedAssemblyLines = Object.values(assemblyGroups).map(group => {
            const { components, primary_part_id, primary_part_quantity, assembly_name, source_assembly_id } = group;

            // Calculate total assembly cost from all component lines
            const groupTotalCost = components.reduce(
                (sum, c) => sum + (c.quantity_ordered * c.unit_price), 0
            );

            // Derive display quantity from the primary part line
            const primaryLine = components.find(c => c.part_id === primary_part_id);
            let displayQty = 1;
            if (primaryLine) {
                displayQty = Math.floor(primaryLine.quantity_ordered / primary_part_quantity);
            }
            const assemblyUnitPriceLocal = displayQty > 0 ? groupTotalCost / displayQty : 0;
            // F19: prefer the order's per-set price (and ordered set count) from
            // /truth; invoiceAssemblyPricing() falls back to the local figure.
            const perSet = invoiceAssemblyPricing({
                truthGroup: truthGroups[String(source_assembly_id)],
                localPerSetPrice: assemblyUnitPriceLocal,
                orderNumber: orderNum,
            });
            const assemblyUnitPrice = perSet.per_set_price;
            const orderedSets = perSet.ordered_sets ?? displayQty;

            // Aggregate shipped & invoiced across all component lines
            const totalShipped = components.reduce((sum, c) => sum + c.quantity_shipped, 0);
            const totalInvoiced = components.reduce((sum, c) => sum + c.quantity_invoiced, 0);
            const totalOrdered = components.reduce((sum, c) => sum + c.quantity_ordered, 0);

            // If ALL component lines are fully billed, skip
            const allFullyBilled = components.every(c => c.quantity_remaining <= 0);
            if (allFullyBilled) return null;

            // Billable qty at assembly level: use primary part's shipped vs invoiced
            const primaryShipped = primaryLine ? primaryLine.quantity_shipped : 0;
            const primaryInvoiced = primaryLine ? primaryLine.quantity_invoiced : 0;
            const primaryBillableUnits = Math.floor(Math.max(0, primaryShipped - primaryInvoiced) / primary_part_quantity);
            const billableQty = primaryBillableUnits > 0 ? primaryBillableUnits : 0;

            // Use the primary part's SO line for traceability; collect all SO line IDs
            const primarySoLineId = primaryLine ? primaryLine.so_line_id : components[0].so_line_id;
            const linkedJobId = primaryLine?.suggested_job_id || components[0]?.suggested_job_id || null;
            const linkedMasterJobNum = primaryLine?.master_job_num || components[0]?.master_job_num || "";

            return {
                sales_order_line_id: primarySoLineId,
                part_id: primaryLine ? primaryLine.part_id : components[0].part_id,
                product_id: "ASSEMBLY",
                description: assembly_name,
                quantity: billableQty,
                unit_price: assemblyUnitPrice,
                customer_po: cleanPo,
                customer_so: cleanSo,
                quantity_ordered: orderedSets,
                quantity_shipped: Math.floor(primaryShipped / primary_part_quantity),
                quantity_invoiced: Math.floor(primaryInvoiced / primary_part_quantity),
                job_id: linkedJobId,
                master_job_number: linkedMasterJobNum || "",
                source_assembly_id: source_assembly_id,
                // F19: where the per-set price came from (rendered under the
                // description in the preview; stripped from the POST payload)
                _per_set_source: perSet,
                // Track all component SO lines for the backend
                // quantity_per_assembly derived from SO line qty / assemblies ordered
                _assembly_component_lines: components.map(c => ({
                    so_line_id: c.so_line_id,
                    part_id: c.part_id,
                    quantity_ordered: c.quantity_ordered,
                    quantity_per_assembly: displayQty > 0 ? Math.round(c.quantity_ordered / displayQty) : 1,
                    unit_price: c.unit_price,
                    source_assembly_id: source_assembly_id
                })),
                sources: [{
                    jobId: linkedJobId,
                    masterJobNum: linkedMasterJobNum || "",
                    customerPo: cleanPo,
                    quantity_ordered: orderedSets,
                    quantity_shipped: Math.floor(primaryShipped / primary_part_quantity)
                }]
            };
        }).filter(Boolean);

        // Step 4: Build individual (non-assembly) lines as before
        const individualAutoLines = individualLines
            .map(l => {
                const billableQty = Math.max(0, l.quantity_shipped - l.quantity_invoiced);
                if (l.quantity_remaining <= 0) return null;

                const linkedJobId = l.suggested_job_id || null;
                const linkedMasterJobNum = l.master_job_num || (linkedJobId ? `Job#${linkedJobId}` : "");

                return {
                    sales_order_line_id: l.so_line_id,
                    part_id: l.part_id,
                    product_id: l.product_id,
                    description: l.description,
                    quantity: billableQty > 0 ? billableQty : 0,
                    unit_price: l.unit_price,
                    customer_po: cleanPo,
                    customer_so: cleanSo,
                    quantity_ordered: l.quantity_ordered,
                    quantity_shipped: l.quantity_shipped,
                    quantity_invoiced: l.quantity_invoiced,
                    job_id: linkedJobId,
                    master_job_number: linkedMasterJobNum || "",
                    sources: [{
                        jobId: linkedJobId,
                        masterJobNum: linkedMasterJobNum || "",
                        customerPo: cleanPo,
                        quantity_ordered: l.quantity_ordered,
                        quantity_shipped: l.quantity_shipped
                    }]
                };
            })
            .filter(Boolean);

        // Step 5: Combine assembly lines first, then individual lines
        const autoLines = [...groupedAssemblyLines, ...individualAutoLines];
        
        setNewLines(autoLines);

        // *** FINANCIALS AUTO-FILL ***
        setCreateShipping(parseFloat(res.data.shipping_amount || 0));
        setCreateFees(parseFloat(res.data.additional_fees || 0));
        setCreateDiscount(parseFloat(res.data.discount_amount || 0));

        // Tax: Keep the customer-level tax rate (already set when customer was selected).
        // Only override if the Sales Order has an explicit non-zero tax amount stored,
        // which would indicate a manual tax override at the SO level.
        const soTaxAmount = parseFloat(res.data.tax_amount || 0);
        if (soTaxAmount > 0) {
            setCreateTaxAmount(soTaxAmount);
            // Reverse-calculate the rate for display purposes so the % field looks correct
            const subtotal = res.data.lines.reduce((acc, l) => acc + (l.quantity_ordered * l.unit_price), 0);
            if (subtotal > 0) {
                setCreateTaxRate(((soTaxAmount / subtotal) * 100).toFixed(3));
            }
        }
        // If SO tax is 0, the customer's default rate (already set) will auto-calculate
        // via the existing useEffect that watches [newLines, createFees, createDiscount, createTaxRate]

    } catch (err) {
        console.error("Error fetching billing status:", err);
        showMessage("Failed to load Sales Order data.", "error");
    }
  };

  const handleBillRemaining = () => {
      if (newInvoiceSalesOrderId) {
          handleSelectSalesOrder(newInvoiceSalesOrderId); // Re-trigger the auto-fill logic
      }
  };

  // *** RENDER JOB SELECTION LOGIC ***
  // Filter the jobs list based on mode
  const visibleJobsForInvoice = jobsListForCustomer.filter(job => {
      if (invoiceCreationMode === 'sales_order') {
          // Typically hidden in SO mode unless needed for legacy reasons
          return false;
      } else {
          // Direct Job Mode: Show Orphan Jobs ONLY (No Sales Order Link)
          return !job.sales_order_id;
      }
  });

  const filteredJobs = visibleJobsForInvoice.filter(job => {
    const masterNumMatch = !jobFilterMasterNum || (job.master_job_num && job.master_job_num.toString().toLowerCase().includes(jobFilterMasterNum.toLowerCase()));
    const poMatch = !jobFilterCustomerPo || (job.customer_po && job.customer_po.toLowerCase().includes(jobFilterCustomerPo.toLowerCase())) || (job.customer_so && job.customer_so.toLowerCase().includes(jobFilterCustomerPo.toLowerCase()));
    return masterNumMatch && poMatch;
  });
  // T&M (Commit C): a job of a T&M order (GET /jobs so_pricing_mode) is listed but cannot be picked:
  // its order is invoiced once, from the Sales Order page (the API refuses it here too). With no T&M
  // job in the list this is filteredJobs itself, so the fixed-price picker behaves as before.
  const selectableJobs = filteredJobs.some(isTmLinked) ? filteredJobs.filter(job => !isTmLinked(job)) : filteredJobs;

  const handleSelectJob = (jobId) => {
    if (jobsListForCustomer.some(job => job.job_id === jobId && isTmLinked(job))) return;
    setSelectedJobIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const handleSelectAllJobs = (event) => {
    if (event.target.checked) {
      const allFilteredJobIds = selectableJobs.map(job => job.job_id);
      setSelectedJobIds(new Set(allFilteredJobIds));
    } else {
      setSelectedJobIds(new Set());
    }
  };
  
  // *** Handler for bulk-adding parts from selected jobs (Direct Mode) ***
  const handleAddSelectedJobs = async () => {
    const jobsToAdd = jobsListForCustomer.filter(job => selectedJobIds.has(job.job_id) && !isTmLinked(job));
    if (jobsToAdd.length === 0) return;
  
    try {
      // Fetch parts for all selected jobs
      const fetchPromises = jobsToAdd.map(job => 
        axios.get(`/jobs/${job.job_id}/parts`).then(res => ({ ...res, jobData: job }))
      );
      const responses = await Promise.all(fetchPromises);
  
      const allPartsToAdd = responses.flatMap(res => 
        res.data.map(part => ({ ...part, sourceJob: res.jobData }))
      );
  
      setNewLines(prevLines => {
        // Create map of existing lines to merge duplicates
        const linesMap = new Map(prevLines.map(line => [line.part_id, line]));
  
        allPartsToAdd.forEach(part => {
          const job = part.sourceJob;
          const newSource = {
            jobId: job.job_id,
            masterJobNum: job.master_job_num,
            customerPo: part.customer_po || job.customer_po || 'N/A',
            customerSo: part.customer_so || job.customer_so || null,
            quantity_ordered: part.quantity_ordered || 0,
            quantity_shipped: part.quantity_shipped || 0, // In direct mode, might not track shipments same way, but field exists
          };
  
          if (linesMap.has(part.part_id)) {
            // Update existing line
            const existingLine = linesMap.get(part.part_id);
            existingLine.quantity_ordered += newSource.quantity_ordered;
            existingLine.quantity_shipped += newSource.quantity_shipped;
            // For direct jobs, default invoice quantity to total produced/ordered if not shipped
            existingLine.quantity = existingLine.quantity_ordered; 
            
            // Add unique source info
            if (!existingLine.sources.some(s => s.jobId === newSource.jobId)) {
              existingLine.sources.push(newSource);
            }
          } else {
            // New line
            linesMap.set(part.part_id, {
              part_id: part.part_id,
              product_id: part.product_id,
              description: part.description || "",
              quantity_ordered: newSource.quantity_ordered,
              quantity_shipped: newSource.quantity_shipped,
              quantity: newSource.quantity_ordered, // Default to order qty
              unit_price: part.price || 0,
              sources: [newSource],
            });
          }
        });
        return Array.from(linesMap.values());
      });
  
      // Clear selection after adding
      setSelectedJobIds(new Set());
  
    } catch (err) {
      console.error("Error fetching parts from selected jobs:", err);
      showMessage("Failed to fetch lines from one or more selected jobs.", "error");
    }
  };

  const handleCreateInvoice = async () => {
    // Validate rules before proceeding
    const msg = validateInvoiceRules(newLines, yieldPolicy);

    if (msg) {
        // Allow operator override via confirmation
        if (!window.confirm(msg + "Do you want to proceed with this invoice anyway?")) return;
    }

    const payload = {
      customer_id: Number(newInvoiceCustomerId),
      sales_order_id: newInvoiceSalesOrderId || null,
      invoice_date: newInvoiceDate || null,
      due_date: newDueDate || null,
      payment_terms: newPaymentTerms || null,
      notes: newNotes || null,
      shipping_amount: parseFloat(createShipping) || 0.0,
      additional_fees: parseFloat(createFees) || 0.0,
      discount_amount: parseFloat(createDiscount) || 0.0,
      tax_amount: parseFloat(createTaxAmount) || 0.0,
      lines: newLines.flatMap((ln) => {
        const { sources, quantity_ordered, quantity_shipped, quantity_invoiced, _assembly_component_lines, _per_set_source, ...lineData } = ln;
        const customerPoString = sources && sources.length > 0
            ? [...new Set(sources.map(s => s.customerPo).filter(Boolean))].join(', ')
            : lineData.customer_po || "N/A";
        const customerSoString = sources && sources.length > 0
            ? [...new Set(sources.map(s => s.customerSo).filter(Boolean))].join(', ')
            : lineData.customer_so || "";

        // Assembly lines: expand back into component lines for proper SO line tracking
        // Each component qty = assemblies being invoiced × quantity_per_assembly
        if (_assembly_component_lines && _assembly_component_lines.length > 0) {
            const invoiceAssemblyQty = parseInt(lineData.quantity, 10) || 0;
            return _assembly_component_lines.map(comp => {
                const qtyPerAsm = comp.quantity_per_assembly || 1;
                const compQty = invoiceAssemblyQty * qtyPerAsm;

                return {
                    part_id: comp.part_id,
                    description: lineData.description + " (component)",
                    job_id: sources && sources[0] ? sources[0].jobId : null,
                    customer_po: customerPoString.trim() || "N/A",
                    customer_so: customerSoString.trim() || null,
                    quantity: compQty,
                    unit_price: parseFloat(comp.unit_price) || 0,
                    sales_order_line_id: comp.so_line_id,
                    source_assembly_id: comp.source_assembly_id,
                };
            });
        }

        // Regular (non-assembly) line
        return [{
          ...lineData,
          part_id: lineData.part_id || 9999,
          job_id: sources && sources[0] ? sources[0].jobId : null,
          customer_po: customerPoString.trim() || "N/A",
          customer_so: customerSoString.trim() || null,
          quantity: parseInt(lineData.quantity, 10) || 0,
          unit_price: parseFloat(lineData.unit_price) || 0,
        }];
      }),
    };

    try {
      await axios.post("/invoices", payload);
      showMessage("Invoice created!", "success");
      setOpenCreateDialog(false);
      fetchInvoices();
    } catch (err) {
      console.error("Error creating invoice:", err);
      // The API's own reason (e.g. a T&M order's refusal) instead of a bare "failed"; the
      // sanitized 500 / database errors become the house guidance (utils/apiError).
      showMessage(axiosErrorMessage(err, "Failed to create invoice"), "error");
    }
};

  const handleChangeNewLine = (index, field, value) => {
    const updated = [...newLines];
    updated[index] = { ...updated[index], [field]: value };
    setNewLines(updated);
  };
  
  const handleRemoveNewLine = (index) => {
    const updated = [...newLines];
    updated.splice(index, 1);
    setNewLines(updated);
  };
  
  const handleAddBlankLine = () => {
    setNewLines((prev) => [
      ...prev,
      {
        master_job_number: "",
        customer_po: "N/A",
        customer_so: null,
        job_id: 0,
        part_id: 9999,
        product_id: "9999",
        description: "Custom Item",
        quantity: 1,
        unit_price: 0,
        quantity_ordered: 1,
        quantity_shipped: 0,
        sources: [],
      },
    ]);
  };

  // Progress Bar Component
  const renderBillingProgress = () => {
    if (!soBillingStatus) return null;
    const { total_order_value, total_billed_value, percent_billed, remaining_order_value } = soBillingStatus;
    
    return (
        <Box sx={{ mb: 3, p: 2, bgcolor: isDark ? '#1a1a2e' : '#f5f5f5', borderRadius: 2, border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e0e0e0'}` }}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">Sales Order Progress</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {percent_billed}% Billed
                </Typography>
            </Grid>
            
            <LinearProgress 
                variant="determinate" 
                value={percent_billed > 100 ? 100 : percent_billed} 
                sx={{ height: 10, borderRadius: 5, mb: 2 }}
                color={percent_billed >= 100 ? "success" : "primary"}
            />
            
            <Grid container spacing={2} textAlign="center">
                <Grid item xs={4}>
                    <Typography variant="caption" display="block">Total Order</Typography>
                    <Typography variant="body2" fontWeight="bold">${total_order_value.toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="caption" display="block" color="success.main">Billed</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">${total_billed_value.toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="caption" display="block" color="warning.main">Remaining</Typography>
                    <Typography variant="body2" fontWeight="bold" color="warning.main">${remaining_order_value.toLocaleString()}</Typography>
                </Grid>
            </Grid>
        </Box>
    );
  };

  // Group assembly component lines into single assembly rows for read-only detail view
  // Uses SO quoted prices (so_unit_price × quantity_per_assembly) for assembly unit price
  // to ensure the displayed price always matches the original quote.
  const groupedInvoiceLines = useMemo(
    () => groupInvoiceLines(invoiceDetail, detailTruthGroups), // features/invoices/invoiceData
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoiceDetail?.lines, invoiceDetail?.sales_order_id, invoiceDetail?.order_number, detailTruthGroups],
  );

  return (
    <MuiThemeProvider theme={theme}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: isDark ? "#121218" : "#f7f7f7" }}>
        {/* Tab Toggle */}
        <Box sx={{ px: 2, pt: 1, pb: 0.5, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#ddd'}`, backgroundColor: isDark ? "#1e1e2d" : "#fff" }}>
          <ToggleButtonGroup value={activeTab} exclusive onChange={(e, v) => v && setActiveTab(v)} size="small">
            <ToggleButton value="invoices"><ReceiptIcon sx={{ mr: 0.5, fontSize: 18 }} />Invoices</ToggleButton>
            <ToggleButton value="deposits">
              <Badge
                badgeContent={unmatchedDepositsCount}
                color="error"
                max={99}
                sx={{ '& .MuiBadge-badge': { right: -4, top: 2 } }}
              >
                <AccountBalanceWalletIcon sx={{ mr: 0.5, fontSize: 18 }} />
              </Badge>
              Deposits
            </ToggleButton>
            <ToggleButton value="retainage"><HandshakeIcon sx={{ mr: 0.5, fontSize: 18 }} />Retainage</ToggleButton>
            <ToggleButton value="credits"><SavingsIcon sx={{ mr: 0.5, fontSize: 18 }} />Credits</ToggleButton>
            <ToggleButton value="statements"><DescriptionIcon sx={{ mr: 0.5, fontSize: 18 }} />Statements</ToggleButton>
            <ToggleButton value="sales_tax"><AccountBalanceIcon sx={{ mr: 0.5, fontSize: 18 }} />Sales Tax</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {activeTab === "statements" ? (
          <StatementsView isDark={isDark} />
        ) : activeTab === "sales_tax" ? (
          <SalesTaxView isDark={isDark} />
        ) : activeTab === "deposits" ? (
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            <Box sx={{ px: 2, pt: 1.5 }}>
              <ToggleButtonGroup value={depositsView} exclusive size="small" onChange={(e, v) => v && setDepositsView(v)}>
                <ToggleButton value="slips">Deposit slips</ToggleButton>
                <ToggleButton value="unmatched">
                  Unmatched bank deposits{unmatchedDepositsCount ? ` (${unmatchedDepositsCount})` : ""}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {depositsView === "slips" ? (
              <DepositSlipsView isDark={isDark} onShowMessage={showMessage} />
            ) : (
              <UnmatchedDepositsView isDark={isDark} onShowMessage={showMessage} onCountChange={refreshUnmatchedDepositsCount} />
            )}
          </Box>
        ) : activeTab === "retainage" ? (
          <RetainageView isDark={isDark} onShowMessage={showMessage} />
        ) : activeTab === "credits" ? (
          <CreditsView isDark={isDark} onShowMessage={showMessage} />
        ) : (
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left Panel for Invoice List and Filters */}
        <Box sx={{ width: 320, borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#ccc'}`, p: 2, overflowY: "auto", backgroundColor: isDark ? "#1e1e2d" : "#ffffff" }}>
          <Typography variant="h5" gutterBottom>Invoices</Typography>
          <Button variant="contained" fullWidth onClick={handleOpenCreateDialog} sx={{ mb: 2 }}>
            + Create New Invoice
          </Button>
          <Button
            variant={!selectedInvoiceId ? "contained" : "outlined"}
            color="info"
            fullWidth
            startIcon={<i className="bi bi-bar-chart-line" />}
            onClick={() => { setInvoiceDetail(null); setSelectedInvoiceId(null); }}
            sx={{ mb: 1 }}
          >
            Dashboard
          </Button>
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              sx={{ mb: 1 }}
              placeholder="Search PO, SO#, job#, customer…"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") fetchInvoices(); }}
              helperText="Matches customer PO, our PO/job#, SO#, invoice#, or customer — across all statuses"
              FormHelperTextProps={{ sx: { mx: 0, fontSize: '0.68rem' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: filterSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="clear search"
                      onClick={() => { setFilterSearch(""); setTimeout(fetchInvoices, 0); }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                {INVOICE_STATUS_OPTIONS.map((o) => <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Customer Dropdown Filter */}
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Customer</InputLabel>
              <Select 
                label="Customer" 
                value={filterCustomerId} 
                onChange={(e) => setFilterCustomerId(e.target.value)}
              >
                <MenuItem value="">(All Customers)</MenuItem>
                {customerFilterList.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="Invoice #" size="small" fullWidth sx={{ mb: 1 }} value={filterInvoiceNumber} onChange={(e) => setFilterInvoiceNumber(e.target.value)} />
            <TextField label="Start Date" type="date" size="small" fullWidth sx={{ mb: 1 }} InputLabelProps={{ shrink: true }} value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
            <TextField label="End Date" type="date" size="small" fullWidth sx={{ mb: 1 }} InputLabelProps={{ shrink: true }} value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
            <Button variant="contained" fullWidth onClick={handleFilterApply}>Apply Filters</Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            {invoiceList.map((inv) => {
              const isPastDue = daysPastDue(inv, shopDay) > 0; // invoiceData: calendar days
              
              // features/invoices/invoiceData: the secondary string, part payment, retainage
              const { secondaryText, totalPaid, invTotal, retainage, retainageOpen, isPartiallyPaid } = invoiceRowInfo(inv);
              return (
                <ListItemButton
                    key={inv.invoice_id}
                    selected={inv.invoice_id === selectedInvoiceId}
                    onClick={() => handleSelectInvoice(inv.invoice_id)}
                    sx={{
                        mb: 1,
                        borderLeft: isPastDue ? "4px solid red" : "4px solid transparent",
                        backgroundColor: isPastDue ? (isDark ? "rgba(220,53,69,0.12)" : "#fff5f5") : undefined
                    }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {`#${inv.invoice_number} - ${inv.customer_name || 'N/A'}`}
                        {inv.last_emailed_at && (
                          <Tooltip title={`Emailed ${new Date(inv.last_emailed_at).toLocaleDateString()}`}>
                            <EmailIcon sx={{ fontSize: 14, color: 'success.main' }} />
                          </Tooltip>
                        )}
                        {inv.last_inquiry_at && (
                          <Tooltip title={`Payment inquiry sent ${new Date(inv.last_inquiry_at).toLocaleDateString()}`}>
                            <InquiryIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          </Tooltip>
                        )}
                        {isPartiallyPaid && !retainageOpen && (
                          <Chip size="small" label={`Partial ${fmt(totalPaid)}/${fmt(invTotal)}`} color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        {retainageOpen && (
                          <Tooltip title={`${fmt(retainage)} held as retainage`}>
                            <Chip size="small" icon={<HandshakeIcon sx={{ fontSize: 12 }} />} label={`Retainage ${fmt(retainage)}`} color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={secondaryText}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
          <Divider sx={{ my: 2 }} />
          <Button variant="outlined" onClick={handleSyncPayments} disabled={isSyncing} fullWidth sx={{ mt: 1 }}>
            {isSyncing ? "Syncing..." : "Sync Bill.com Payments"}
          </Button>
          <Button variant="outlined" onClick={handleOpenBatchPayDialog} fullWidth sx={{ mt: 1 }}>
            Record Batch Payment
          </Button>
        </Box>

        {/* Right Panel for Dashboard or Invoice Detail */}
        <Box sx={{ flex: 1, p: 2, overflowY: "auto" }}>
          {!invoiceDetail ? <DashboardView invoiceList={invoiceList} onSelectInvoice={handleSelectInvoice} onNotify={showMessage} /> : (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5">Invoice #{invoiceDetail.invoice_number}</Typography>
                  {invoiceDetail.last_emailed_at && (
                      <Chip
                        icon={<EmailIcon />}
                        label={`Emailed ${new Date(invoiceDetail.last_emailed_at).toLocaleDateString()}`}
                        color="info"
                        variant="outlined"
                        size="small"
                      />
                  )}
                  {paymentInquiries.length > 0 && (
                      <Tooltip title={`${paymentInquiries.length} payment inquir${paymentInquiries.length === 1 ? 'y' : 'ies'} — last to ${paymentInquiries[0].to_email}${paymentInquiries[0].sent_by ? ` by ${paymentInquiries[0].sent_by}` : ''}`}>
                        <Chip
                          icon={<InquiryIcon />}
                          label={`Inquiry sent ${new Date(paymentInquiries[0].sent_at).toLocaleDateString()}`}
                          color="warning"
                          variant="outlined"
                          size="small"
                        />
                      </Tooltip>
                  )}
                  {invoiceDetail.bill_com_invoice_id && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Synced to Bill.com"
                        color="success"
                        variant="outlined"
                        size="small"
                        onDelete={handleAuditSync} // Hack to show a clickable interaction or just add button next to it
                        deleteIcon={<SyncIcon />}
                      />
                  )}
                </Box>
                {/* Audit Button */}
                {invoiceDetail.bill_com_invoice_id && (
                    <Button 
                        startIcon={<WarningIcon />} 
                        variant="outlined" 
                        color="warning" 
                        size="small"
                        onClick={handleAuditSync}
                    >
                        Check Bill.com Status
                    </Button>
                )}
              </Box>

              <Box sx={{ mb: 2, display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(() => {
                    // Display status: synthesizes "Partially Paid" from total_paid math
                    // when invoices.status='Open' but a payment has landed. Real DB
                    // status is unchanged — only the label.
                    const { display, chipColor } = invoiceStatusDisplay(invoiceDetail); // invoiceData
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">Status:</Typography>
                        <Chip size="small" label={display} color={chipColor} sx={{ fontWeight: 600 }} />
                      </Box>
                    );
                  })()}
                  {invoiceDetail.last_synced_at && (
                       <Typography variant="body2" color="text.secondary">
                           Last Synced: {new Date(invoiceDetail.last_synced_at).toLocaleString()}
                       </Typography>
                  )}
              </Box>

              {isEditing ? (
                // ... EDITING VIEW ...
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}><Typography component="div" variant="body1" sx={{ mb: 1 }}><span style={{ fontWeight: 'bold' }}>Customer:</span> {invoiceDetail.customer_name || ""}</Typography></Grid>
                    <Grid item xs={6}><TextField label="Invoice Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={invoiceDetail.invoice_date || ""} onChange={(e) => {
                      const newDate = e.target.value;
                      const computed = calculateDueDate(newDate, invoiceDetail.payment_terms);
                      setInvoiceDetail({ ...invoiceDetail, invoice_date: newDate, ...(computed ? { due_date: computed } : {}) });
                    }} /></Grid>
                    <Grid item xs={6}><TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={invoiceDetail.due_date || ""} onChange={(e) => setInvoiceDetail({ ...invoiceDetail, due_date: e.target.value })} /></Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Terms</InputLabel>
                        <Select label="Payment Terms" value={invoiceDetail.payment_terms || ""} onChange={(e) => {
                          const terms = e.target.value;
                          const computed = calculateDueDate(invoiceDetail.invoice_date, terms);
                          setInvoiceDetail({ ...invoiceDetail, payment_terms: terms, ...(computed ? { due_date: computed } : {}) });
                        }}>
                          <MenuItem value="">(None)</MenuItem>
                          <MenuItem value="Due on Receipt">Due on Receipt</MenuItem>
                          <MenuItem value="Net 7">Net 7</MenuItem>
                          <MenuItem value="Net 10">Net 10</MenuItem>
                          <MenuItem value="Net 14">Net 14</MenuItem>
                          <MenuItem value="Net 15">Net 15</MenuItem>
                          <MenuItem value="Net 30">Net 30</MenuItem>
                          <MenuItem value="Net 45">Net 45</MenuItem>
                          <MenuItem value="Net 60">Net 60</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* Financial Fields - Edit Mode */}
                    <Grid item xs={4}>
                      <TextField 
                        label="Shipping" type="number" fullWidth 
                        value={invoiceDetail.shipping_amount || 0} 
                        onChange={(e) => {
                            const newShip = parseFloat(e.target.value || 0);
                            setInvoiceDetail(prev => {
                                // Re-calculate tax amount based on fixed tax rate because shipping affects total
                                // Note: Typically tax is calculated on (Subtotal + Shipping) depending on nexus.
                                // We are sticking to (Subtotal + Fees - Disc) * Rate (Shipping Excluded)
                                const sub = prev.lines.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
                                const base = Math.max(0, sub + (prev.additional_fees||0) - (prev.discount_amount||0));
                                const tax = base * (editTaxRate / 100);
                                const tot = base + tax + newShip; // Add shipping to total
                                return { ...prev, shipping_amount: newShip, tax_amount: parseFloat(tax.toFixed(2)), total: parseFloat(tot.toFixed(2)) };
                            });
                        }} 
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField 
                        label="Additional Fees" type="number" fullWidth 
                        value={invoiceDetail.additional_fees || 0} 
                        onChange={(e) => {
                            const newFees = parseFloat(e.target.value || 0);
                            setInvoiceDetail(prev => {
                                const sub = prev.lines.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
                                const base = Math.max(0, sub + newFees - (prev.discount_amount||0));
                                const tax = base * (editTaxRate / 100);
                                const tot = base + tax + (prev.shipping_amount||0);
                                return { ...prev, additional_fees: newFees, tax_amount: parseFloat(tax.toFixed(2)), total: parseFloat(tot.toFixed(2)) };
                            });
                        }} 
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField 
                        label="Discount" type="number" fullWidth 
                        value={invoiceDetail.discount_amount || 0} 
                        onChange={(e) => {
                            const newDisc = parseFloat(e.target.value || 0);
                            setInvoiceDetail(prev => {
                                const sub = prev.lines.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
                                const base = Math.max(0, sub + (prev.additional_fees||0) - newDisc);
                                const tax = base * (editTaxRate / 100);
                                const tot = base + tax + (prev.shipping_amount||0);
                                return { ...prev, discount_amount: newDisc, tax_amount: parseFloat(tax.toFixed(2)), total: parseFloat(tot.toFixed(2)) };
                            });
                        }} 
                      />
                    </Grid>

                    {/* Tax Rate & Amount Editing */}
                    <Grid item xs={6}>
                        <TextField 
                            label="Tax Rate (%)" 
                            type="number" 
                            fullWidth 
                            value={editTaxRate}
                            onChange={(e) => handleEditTaxRateChange(parseFloat(e.target.value || 0))}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField 
                            label="Tax Amount ($)" 
                            type="number" 
                            fullWidth 
                            value={invoiceDetail.tax_amount || 0} 
                            onChange={(e) => handleEditTaxAmountChange(parseFloat(e.target.value || 0))}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                    </Grid>

                    <Grid item xs={12}><TextField label="Notes" multiline rows={2} fullWidth value={invoiceDetail.notes || ""} onChange={(e) => setInvoiceDetail({ ...invoiceDetail, notes: e.target.value })} /></Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Line Items</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Master Job #</TableCell>
                          <TableCell>Customer PO</TableCell>
                          <TableCell>Cust SO</TableCell>
                          <TableCell>Product ID</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Subtotal</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceDetail.lines?.map((ln, idx) => {
                          const sub = ln.quantity * ln.unit_price;
                          return (
                            <TableRow key={ln.invoice_line_id || idx}>
                              <TableCell>{ln.master_job_number || ln.job_id}</TableCell>
                              <TableCell><TextField variant="standard" size="small" value={ln.customer_po || ""} onChange={(e) => { const updated = [...invoiceDetail.lines]; updated[idx].customer_po = e.target.value; setInvoiceDetail({ ...invoiceDetail, lines: updated }); }} /></TableCell>
                              <TableCell><TextField variant="standard" size="small" value={ln.customer_so || ""} onChange={(e) => { const updated = [...invoiceDetail.lines]; updated[idx].customer_so = e.target.value; setInvoiceDetail({ ...invoiceDetail, lines: updated }); }} /></TableCell>
                              <TableCell>{ln.product_id}</TableCell>
                              <TableCell><TextField variant="standard" size="small" fullWidth value={ln.description} onChange={(e) => { const updated = [...invoiceDetail.lines]; updated[idx].description = e.target.value; setInvoiceDetail({ ...invoiceDetail, lines: updated }); }} /></TableCell>
                              <TableCell align="right">
                                  <TextField 
                                    variant="standard" size="small" type="number" value={ln.quantity} 
                                    onChange={(e) => { 
                                        const updated = [...invoiceDetail.lines]; 
                                        updated[idx].quantity = parseInt(e.target.value || 0, 10); 
                                        
                                        // Recalc Tax on line change
                                        const newSub = updated.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
                                        const base = Math.max(0, newSub + (invoiceDetail.additional_fees||0) - (invoiceDetail.discount_amount||0));
                                        const newTax = base * (editTaxRate / 100);
                                        const newTotal = base + newTax + (invoiceDetail.shipping_amount||0);

                                        setInvoiceDetail({ ...invoiceDetail, lines: updated, tax_amount: parseFloat(newTax.toFixed(2)), total: parseFloat(newTotal.toFixed(2)) }); 
                                    }} 
                                  />
                              </TableCell>
                              <TableCell align="right">
                                  <TextField 
                                    variant="standard" size="small" type="number" value={ln.unit_price} 
                                    onChange={(e) => { 
                                        const updated = [...invoiceDetail.lines]; 
                                        updated[idx].unit_price = parseFloat(e.target.value || 0); 
                                        
                                        // Recalc Tax on line change
                                        const newSub = updated.reduce((acc, l) => acc + (l.quantity * l.unit_price), 0);
                                        const base = Math.max(0, newSub + (invoiceDetail.additional_fees||0) - (invoiceDetail.discount_amount||0));
                                        const newTax = base * (editTaxRate / 100);
                                        const newTotal = base + newTax + (invoiceDetail.shipping_amount||0);

                                        setInvoiceDetail({ ...invoiceDetail, lines: updated, tax_amount: parseFloat(newTax.toFixed(2)), total: parseFloat(newTotal.toFixed(2)) }); 
                                    }} 
                                  />
                              </TableCell>
                              <TableCell align="right">${sub.toFixed(2)}</TableCell>
                              <TableCell><IconButton color="error" onClick={() => handleRemoveEditLine(idx)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <Button sx={{ mt: 1 }} onClick={() => { const newLine = { invoice_line_id: null, master_job_number: "", customer_po: "N/A", customer_so: null, job_id: null, part_id: 9999, product_id: "9999", description: "Custom Item", quantity: 1, unit_price: 0 }; setInvoiceDetail({ ...invoiceDetail, lines: [...invoiceDetail.lines, newLine] }); }}>+ Add Line</Button>
                  </Box>
                  <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                    <Button variant="contained" onClick={handleSaveInvoice}>Save</Button>
                    <Button variant="outlined" onClick={() => setIsEditing(false)}>Cancel</Button>
                  </Box>
                </Box>
              ) : (
                // ... READ-ONLY VIEW ...
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ p: 2, mb: 2, backgroundColor: isDark ? "#1a1a2e" : "#fafafa", borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Customer</Typography><Typography variant="body1">{invoiceDetail.customer_name || ""}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Sales Order</Typography><Typography variant="body1">{invoiceDetail.order_number || "N/A"}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Payment Terms</Typography><Typography variant="body1">{invoiceDetail.payment_terms || ""}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Invoice Date</Typography><Typography variant="body1">{invoiceDetail.invoice_date || ""}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Due Date</Typography><Typography variant="body1">{invoiceDetail.due_date || ""}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Shipping</Typography><Typography variant="body1">${(invoiceDetail.shipping_amount || 0).toFixed(2)}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Additional Fees</Typography><Typography variant="body1">${(invoiceDetail.additional_fees || 0).toFixed(2)}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Discount</Typography><Typography variant="body1">${(invoiceDetail.discount_amount || 0).toFixed(2)}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Tax</Typography><Typography variant="body1">${(invoiceDetail.tax_amount || 0).toFixed(2)}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Subtotal</Typography><Typography variant="body1">${(invoiceDetail.subtotal || 0).toFixed(2)}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" variant="body2">Total</Typography><Typography variant="body1">${(invoiceDetail.total || 0).toFixed(2)}</Typography></Grid>
                      {(() => {
                        // features/invoices/invoiceData. Past due: the due date (a calendar day) has passed.
                        // Balance equals retainage means the only thing outstanding is retainage —
                        // muted yellow signals "this is the held portion, not a missed payment".
                        const { paid, balance, pastDue, balanceIsAllRetainage } = invoiceBalance(invoiceDetail, shopToday());
                        return (
                          <>
                            {paid > 0.01 && (
                              <Grid item xs={6}>
                                <Typography fontWeight="bold" variant="body2">Total Paid</Typography>
                                <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 600 }}>
                                  ${paid.toFixed(2)}
                                </Typography>
                              </Grid>
                            )}
                            {balance > 0.01 && (
                              <Grid item xs={6}>
                                <Typography fontWeight="bold" variant="body2">
                                  {balanceIsAllRetainage ? 'Balance (retainage)' : 'Balance Remaining'}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 700,
                                    color: balanceIsAllRetainage
                                      ? 'warning.main'
                                      : (pastDue ? 'error.main' : 'text.primary')
                                  }}
                                >
                                  ${balance.toFixed(2)}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        );
                      })()}
                      <Grid item xs={12}><Typography fontWeight="bold" variant="body2">Notes</Typography><Typography variant="body1">{invoiceDetail.notes || ""}</Typography></Grid>
                    </Grid>
                  </Box>

                  {/* Chain Alignment (Quote ↔ SO ↔ Invoice) */}
                  {invoiceDetail.chain_alignment && (() => {
                    const ca = invoiceDetail.chain_alignment;
                    const { severity, title, chipColor } = chainAlignmentMeta(ca.status); // invoiceData
                    const icon = ca.status === "aligned"
                      ? <CheckCircleIcon fontSize="inherit" />
                      : ca.status === "drift"
                        ? <WarningIcon fontSize="inherit" />
                        : <ReceiptIcon fontSize="inherit" />;
                    return (
                      <Box sx={{ mb: 2 }}>
                        <MuiAlert severity={severity} icon={icon} sx={{ "& .MuiAlert-message": { width: "100%" } }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <Typography fontWeight="bold" variant="body1">{title}</Typography>
                            <Chip size="small" label={ca.status.toUpperCase().replace("_", " ")} color={chipColor} />
                          </Box>
                          <Grid container spacing={1.5} sx={{ mt: 1 }}>
                            {ca.quote && (
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ p: 1.5, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.8)", borderRadius: 1, borderLeft: "3px solid", borderColor: "primary.main" }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>QUOTE</Typography>
                                  <Typography variant="body2" fontWeight="bold">#{ca.quote.quote_id} · {ca.quote.status}</Typography>
                                  <Typography variant="h6" sx={{ my: 0.5 }}>${ca.quote.computed_total.toFixed(2)}</Typography>
                                  <Typography variant="caption" color="text.secondary">{ca.quote.line_count} line{ca.quote.line_count === 1 ? "" : "s"}</Typography>
                                  {Math.abs((ca.quote.stored_total || 0) - ca.quote.computed_total) > 0.01 && (
                                    <Tooltip title={`Quote header column says $${(ca.quote.stored_total || 0).toFixed(2)} — recompute says $${ca.quote.computed_total.toFixed(2)}. Stored-column drift; next quote save fixes it.`}>
                                      <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.25 }}>
                                        stored: ${(ca.quote.stored_total || 0).toFixed(2)} ⓘ
                                      </Typography>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Grid>
                            )}
                            <Grid item xs={12} sm={ca.quote ? 4 : 6}>
                              <Box sx={{ p: 1.5, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.8)", borderRadius: 1, borderLeft: "3px solid", borderColor: "info.main" }}>
                                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>SALES ORDER</Typography>
                                <Typography variant="body2" fontWeight="bold">{ca.sales_order.order_number} · {ca.sales_order.status}</Typography>
                                <Typography variant="h6" sx={{ my: 0.5 }}>${ca.sales_order.computed_total.toFixed(2)}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                  Billed to date: ${ca.sales_order.cumulative_invoiced.toFixed(2)} across {ca.sales_order.invoice_count} invoice{ca.sales_order.invoice_count === 1 ? "" : "s"}
                                </Typography>
                                {Math.abs((ca.sales_order.stored_total || 0) - ca.sales_order.computed_total) > 0.01 && (
                                  <Tooltip title={`SO header column says $${(ca.sales_order.stored_total || 0).toFixed(2)} — recompute says $${ca.sales_order.computed_total.toFixed(2)}. Stored-column drift; PATCH SO with empty body to heal.`}>
                                    <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.25 }}>
                                      stored: ${(ca.sales_order.stored_total || 0).toFixed(2)} ⓘ
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={ca.quote ? 4 : 6}>
                              <Box sx={{ p: 1.5, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.8)", borderRadius: 1, borderLeft: "3px solid", borderColor: ca.status === "aligned" ? "success.main" : ca.status === "drift" ? "error.main" : "warning.main" }}>
                                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>THIS INVOICE</Typography>
                                <Typography variant="body2" fontWeight="bold">#{invoiceDetail.invoice_number} · {invoiceDetail.status}</Typography>
                                <Typography variant="h6" sx={{ my: 0.5 }}>${ca.invoice_total.toFixed(2)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Remaining on SO: ${ca.sales_order.remaining_to_bill.toFixed(2)}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                          {ca.flags && ca.flags.length > 0 && (
                            <Box sx={{ mt: 1.5, p: 1, bgcolor: isDark ? "rgba(255,0,0,0.08)" : "rgba(255,0,0,0.05)", borderRadius: 1 }}>
                              {ca.flags.map((flag, i) => (
                                <Typography key={i} variant="body2" sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, color: "error.main" }}>
                                  <WarningIcon fontSize="small" sx={{ mt: 0.25 }} />
                                  <span>{flag}</span>
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </MuiAlert>
                      </Box>
                    );
                  })()}

                  {/* Customer Documents from the linked Sales Order */}
                  {invoiceDetail.so_documents && invoiceDetail.so_documents.length > 0 && (
                    <Box sx={{ mb: 2, p: 2, backgroundColor: isDark ? "#1a1a2e" : "#fafafa", borderRadius: 1 }}>
                      <Typography fontWeight="bold" variant="body2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                        <DescriptionIcon fontSize="small" />
                        Customer Documents
                        {invoiceDetail.chain_alignment?.sales_order?.order_number && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            from {invoiceDetail.chain_alignment.sales_order.order_number}
                          </Typography>
                        )}
                      </Typography>
                      <List dense disablePadding>
                        {invoiceDetail.so_documents.map((doc) => {
                          const isPO = isCustomerPoDoc(doc); // invoiceData
                          return (
                            <ListItemButton
                              key={doc.document_id}
                              component="a"
                              href={doc.s3_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ borderRadius: 1, mb: 0.5 }}
                            >
                              <DescriptionIcon color={isPO ? "primary" : "action"} sx={{ mr: 1 }} />
                              <ListItemText
                                primary={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" fontWeight={isPO ? "bold" : "normal"}>
                                      {doc.display_name || doc.original_filename}
                                    </Typography>
                                    {isPO && <Chip size="small" label="Customer PO" color="primary" variant="outlined" />}
                                  </Box>
                                }
                                secondary={`${doc.doc_type || "Document"} · ${doc.uploaded_by_name || "Unknown"} · ${doc.file_size_bytes ? (doc.file_size_bytes / 1024).toFixed(0) + " KB" : ""}`}
                              />
                              <DownloadIcon fontSize="small" color="action" />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Box>
                  )}

                  {invoiceDetail.status === "Open" && detailCustomerCredits.length > 0 && (
                    <MuiAlert severity="info" sx={{ mb: 2 }}
                      action={
                        <Button color="inherit" size="small"
                                onClick={() => handleApplyCreditToDetailInvoice(detailCustomerCredits[0])}>
                          Apply Credit
                        </Button>
                      }>
                      {invoiceDetail.customer_name} has&nbsp;
                      <strong>${detailCustomerCredits.reduce((s, c) => s + c.remaining_amount, 0).toFixed(2)}</strong>
                      &nbsp;in open credit on account (Credit
                      #{detailCustomerCredits.map(c => c.credit_id).join(", #")}).
                    </MuiAlert>
                  )}
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Line Items</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Master Job #</TableCell>
                        <TableCell>Customer PO</TableCell>
                        <TableCell>Cust SO</TableCell>
                        <TableCell>Product ID</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedInvoiceLines.map((ln) => {
                        const sub = ln.quantity * ln.unit_price;
                        return (
                          <TableRow key={ln.invoice_line_id}>
                            <TableCell>{ln.master_job_number || ln.job_id}</TableCell>
                            <TableCell>{ln.customer_po || ""}</TableCell>
                            <TableCell>{ln.customer_so || ""}</TableCell>
                            <TableCell>{ln.product_id}</TableCell>
                            <TableCell>
                              {ln.description}
                              {ln._per_set_source && (
                                <Typography variant="caption" display="block" color="text.secondary" title={ln._per_set_source.formula}>
                                  {ln._per_set_source.note}
                                  {ln._per_set_source.pricing_note ? ` — ${ln._per_set_source.pricing_note}` : ""}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{ln.quantity}</TableCell>
                            <TableCell align="right">${(ln.unit_price || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">${sub.toFixed(2)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {/* Payment History */}
                  {invoiceDetail.payments && invoiceDetail.payments.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>Payment History</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Memo</TableCell>
                            <TableCell>Batch</TableCell>
                            <TableCell>Document</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoiceDetail.payments.map((p) => (
                            <TableRow key={p.invoice_payment_id}>
                              <TableCell>{p.payment_date || "—"}</TableCell>
                              <TableCell>{p.payment_method || "—"}</TableCell>
                              <TableCell align="right">${(p.payment_amount || 0).toFixed(2)}</TableCell>
                              <TableCell>{p.memo || "—"}</TableCell>
                              <TableCell>
                                {p.payment_group_id ? (
                                  <Chip
                                    size="small"
                                    label={`Check #${p.check_number || "—"} ($${(p.group_total || 0).toFixed(2)} total)`}
                                    color="info"
                                    variant="outlined"
                                  />
                                ) : "—"}
                              </TableCell>
                              <TableCell>
                                {p.documents && p.documents.length > 0 ? (
                                  <PaymentDocumentLinks
                                    documents={p.documents}
                                    onDelete={handleDeletePaymentDocument}
                                  />
                                ) : (
                                  <Button
                                    size="small"
                                    startIcon={<AttachFileIcon />}
                                    onClick={() => { setAttachTarget(p); setAttachFile(null); setAttachDocType(p.payment_method === "ACH" ? "ACH Advice" : "Check"); }}
                                  >
                                    Attach
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}

                  {/* Retainage banner — shows when retainage_held_amount > 0 and not yet released */}
                  {parseFloat(invoiceDetail.retainage_held_amount || 0) > 0 && !invoiceDetail.retainage_released_at && (
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HandshakeIcon sx={{ color: '#d97706' }} />
                      <Typography variant="body2">
                        <b>{fmt(invoiceDetail.retainage_held_amount)}</b> held as retainage
                        {invoiceDetail.retainage_held_at && <> since <b>{String(invoiceDetail.retainage_held_at).slice(0, 10)}</b></>}
                      </Typography>
                    </Box>
                      <Button variant="contained" color="success" size="small" startIcon={<HandshakeIcon />} onClick={handleOpenRetainageDialog}>
                        Release Retainage
                      </Button>
                    </Box>
                  )}

                  <ErrorBoundary label="The T&M invoice note">
                    <TmInvoiceLockNote invoice={invoiceDetail} />
                  </ErrorBoundary>
                  <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                    {invoiceDetail.status !== "Paid" && invoiceDetail.status !== "Void" && (
                      <>
                        <Button variant="contained" onClick={handleOpenPayDialog}>Record Payment</Button>
                        {!isTmLinked(invoiceDetail) && <Button variant="outlined" onClick={handleEditInvoice}>Edit</Button>}
                      </>
                    )}
                    {invoiceDetail.status !== "Void" && !isTmLinked(invoiceDetail) && <Button color="error" variant="outlined" onClick={handleVoidInvoice}>Void/Delete</Button>}
                    {invoiceDetail.status !== "Void" && !invoiceDetail.bill_com_invoice_id && <Button variant="contained" color="primary" onClick={handleSyncToBillCom}>Create in Bill.com</Button>}
                    <Button variant="text" onClick={handleOpenPdf}>View/Download PDF</Button>
                    <Button variant="outlined" startIcon={<EmailIcon />} onClick={handleOpenEmailDialog}>Email Invoice</Button>
                    {invoiceDetail.status === "Open" &&
                      parseFloat(invoiceDetail.total || 0) - parseFloat(invoiceDetail.total_paid || 0) > 0.005 && (() => {
                        const overdue = daysPastDue(invoiceDetail, shopToday()) > 0; // invoiceData
                        return (
                          <Tooltip describeChild title="Email the customer asking about the status of this payment, with the invoice PDF attached">
                            <Button
                              variant={overdue ? "contained" : "outlined"}
                              color="warning"
                              startIcon={<InquiryIcon />}
                              onClick={() => setOpenInquiryDialog(true)}
                            >
                              Payment Inquiry
                            </Button>
                          </Tooltip>
                        );
                      })()}
                  </Box>

                  {/* Email History */}
                  {emailHistory.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Email History</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Recipients</TableCell>
                            <TableCell>PDF</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {emailHistory.map((snap) => (
                            <TableRow key={snap.snapshot_id}>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(snap.created_at).toLocaleString()}</TableCell>
                              <TableCell>{(snap.action_type || "").replace(/^Emailed to\s*/i, "")}</TableCell>
                              <TableCell>
                                <Button size="small" variant="text" href={snap.s3_url} target="_blank" rel="noopener">
                                  Download
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}

                  {/* Payment Inquiries */}
                  {paymentInquiries.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment Inquiries</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Sent By</TableCell>
                            <TableCell>Recipients</TableCell>
                            <TableCell>Invoices</TableCell>
                            <TableCell align="right">Balance Asked</TableCell>
                            <TableCell>Message / PDF</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paymentInquiries.map((pi) => {
                            const mine = inquiryForInvoice(pi, invoiceDetail.invoice_id); // invoiceData
                            const isOpen = openInquiryMessageId === pi.inquiry_id;
                            return (
                              <React.Fragment key={pi.inquiry_id}>
                                <TableRow>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(pi.sent_at).toLocaleString()}</TableCell>
                                  <TableCell>{pi.sent_by || '—'}</TableCell>
                                  <TableCell>{[pi.to_email, ...(pi.cc_emails || [])].join(', ')}</TableCell>
                                  <TableCell>{(pi.invoice_numbers || []).map(n => `#${n}`).join(', ')}</TableCell>
                                  <TableCell align="right">{inquiryBalanceAsked(mine)}</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Button size="small" variant="text" onClick={() => setOpenInquiryMessageId(isOpen ? null : pi.inquiry_id)}>
                                      {isOpen ? 'Hide' : 'View'}
                                    </Button>
                                    {mine.pdf_url && (
                                      <Button size="small" variant="text" href={mine.pdf_url} target="_blank" rel="noopener">PDF</Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {isOpen && (
                                  <TableRow>
                                    <TableCell colSpan={6} sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#fafafa' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{pi.subject}</Typography>
                                      <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.85rem', m: 0 }}>
                                        {pi.body_text}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          )}
        </Box>

        {/* Payment Dialog */}
        <Dialog open={openPayDialog} onClose={handleClosePayDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogContent>
                {invoiceDetail && (() => {
                    const invTotal = parseFloat(invoiceDetail.total || 0);
                    const paid = parseFloat(invoiceDetail.total_paid || 0);
                    const remainingBeforePay = Math.max(0, invTotal - paid);
                    const numericPayAmount = parseFloat(payAmount || 0);
                    const remainingAfterPay = Math.max(0, remainingBeforePay - numericPayAmount);
                    const isPartial = numericPayAmount > 0 && numericPayAmount < remainingBeforePay - 0.01;
                    return (
                        <Box sx={{ mb: 1, mt: 0.5, p: 1.25, backgroundColor: 'action.hover', borderRadius: 1, fontSize: 13 }}>
                            <Box>Invoice total: <b>{fmt(invTotal)}</b> · Already paid: <b>{fmt(paid)}</b></Box>
                            <Box>Remaining: <b>{fmt(remainingBeforePay)}</b>{isPartial && <> · After this payment: <b>{fmt(remainingAfterPay)}</b></>}</Box>
                        </Box>
                    );
                })()}
                <TextField label="Amount" type="number" fullWidth sx={{ mt: 1 }} value={payAmount} onChange={(e) => setPayAmount(parseFloat(e.target.value || 0))} />
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Method</InputLabel>
                    <Select label="Method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                        <MenuItem value="Check">Check</MenuItem>
                        <MenuItem value="Credit Card">Credit Card</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="ACH">ACH / Bank Transfer</MenuItem>
                        <MenuItem value="Wire">Wire Transfer</MenuItem>
                        <MenuItem value="PayPal">PayPal</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </Select>
                </FormControl>
                {invoiceDetail && (() => {
                    const invTotal = parseFloat(invoiceDetail.total || 0);
                    const paid = parseFloat(invoiceDetail.total_paid || 0);
                    const remainingBeforePay = Math.max(0, invTotal - paid);
                    const numericPayAmount = parseFloat(payAmount || 0);
                    const isPartial = numericPayAmount > 0 && numericPayAmount < remainingBeforePay - 0.01;
                    if (!isPartial) return null;
                    const remainingAfterPay = Math.max(0, remainingBeforePay - numericPayAmount);
                    return (
                        <FormControl sx={{ mt: 2 }}>
                            <FormLabel>Disposition</FormLabel>
                            <RadioGroup value={payDisposition} onChange={(e) => setPayDisposition(e.target.value)}>
                                <FormControlLabel
                                    value="auto"
                                    control={<Radio size="small" />}
                                    label={<>Partial — still expected (invoice stays Open with <b>{fmt(remainingAfterPay)}</b> still owed)</>}
                                />
                                <FormControlLabel
                                    value="retainage_held"
                                    control={<Radio size="small" />}
                                    label={<>Retainage held — customer is holding <b>{fmt(remainingAfterPay)}</b> as retainage (we expect to collect it later)</>}
                                />
                                <FormControlLabel
                                    value="writeoff"
                                    control={<Radio size="small" />}
                                    label={<>Short-pay accepted as final — write off the remaining <b>{fmt(remainingAfterPay)}</b> and close the invoice</>}
                                />
                            </RadioGroup>
                        </FormControl>
                    );
                })()}
                {payMethod === "Check" && (
                    <TextField label="Check #" fullWidth sx={{ mt: 2 }} value={payCheckNumber}
                               onChange={(e) => setPayCheckNumber(e.target.value)}
                               helperText="Printed on the deposit slip (Invoices → Deposits)." />
                )}
                <TextField label="Memo" fullWidth sx={{ mt: 2 }} value={payMemo} onChange={(e) => setPayMemo(e.target.value)} />
                <PaymentDocumentPicker
                    file={payDocFile}
                    onFile={setPayDocFile}
                    docType={payDocType}
                    onDocType={setPayDocType}
                    inputId="pay-doc-input"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClosePayDialog}>Cancel</Button>
                <Button variant="contained" onClick={handleConfirmPayment}>Confirm</Button>
            </DialogActions>
        </Dialog>

        {/* Attach a scan to an already-recorded payment (backfill) */}
        <Dialog open={!!attachTarget} onClose={() => { setAttachTarget(null); setAttachFile(null); }} maxWidth="sm" fullWidth>
            <DialogTitle>Attach Payment Document</DialogTitle>
            <DialogContent>
                {attachTarget && (
                    <Box sx={{ mb: 1, mt: 0.5, p: 1.25, backgroundColor: 'action.hover', borderRadius: 1, fontSize: 13 }}>
                        <Box>{attachTarget.payment_date || "—"} · {attachTarget.payment_method || "—"} · <b>{fmt(attachTarget.payment_amount || 0)}</b></Box>
                        {attachTarget.payment_group_id && (
                            <Box sx={{ mt: 0.5 }}>
                                Part of check <b>#{attachTarget.check_number || attachTarget.payment_group_id}</b> ({fmt(attachTarget.group_total || 0)} total).
                                The scan will be filed on the check, so it shows on <b>every invoice this check paid</b>.
                            </Box>
                        )}
                    </Box>
                )}
                <PaymentDocumentPicker
                    file={attachFile}
                    onFile={setAttachFile}
                    docType={attachDocType}
                    onDocType={setAttachDocType}
                    inputId="attach-doc-input"
                    alwaysExpanded
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { setAttachTarget(null); setAttachFile(null); }}>Cancel</Button>
                <Button variant="contained" disabled={!attachFile || attachBusy} onClick={handleSubmitAttach}>
                    {attachBusy ? "Uploading…" : "Attach"}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Release Retainage Dialog */}
        <Dialog open={openRetainageDialog} onClose={() => setOpenRetainageDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
                <HandshakeIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }} />
                Release Retainage
            </DialogTitle>
            <DialogContent>
                {invoiceDetail && (
                    <Box sx={{ mb: 1, mt: 0.5, p: 1.25, backgroundColor: 'action.hover', borderRadius: 1, fontSize: 13 }}>
                        <Box>Invoice <b>#{invoiceDetail.invoice_number}</b> · Customer: <b>{invoiceDetail.customer_name}</b></Box>
                        <Box>Retainage held: <b>{fmt(invoiceDetail.retainage_held_amount || 0)}</b>
                            {invoiceDetail.retainage_held_at && <> since <b>{String(invoiceDetail.retainage_held_at).slice(0, 10)}</b></>}
                        </Box>
                    </Box>
                )}
                <TextField label="Amount Received" type="number" fullWidth sx={{ mt: 1 }} value={retainageAmount} onChange={(e) => setRetainageAmount(parseFloat(e.target.value || 0))} />
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Method</InputLabel>
                    <Select label="Method" value={retainageMethod} onChange={(e) => setRetainageMethod(e.target.value)}>
                        <MenuItem value="Check">Check</MenuItem>
                        <MenuItem value="ACH">ACH / Bank Transfer</MenuItem>
                        <MenuItem value="Wire">Wire Transfer</MenuItem>
                        <MenuItem value="Credit Card">Credit Card</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </Select>
                </FormControl>
                <TextField label="Memo" fullWidth sx={{ mt: 2 }} value={retainageMemo} onChange={(e) => setRetainageMemo(e.target.value)} />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenRetainageDialog(false)}>Cancel</Button>
                <Button variant="contained" color="success" onClick={handleConfirmReleaseRetainage}>Release Retainage</Button>
            </DialogActions>
        </Dialog>

        {/* Batch Payment Dialog */}
        <Dialog open={openBatchPayDialog} onClose={() => setOpenBatchPayDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Record Batch Payment</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Customer</InputLabel>
                <Select label="Customer" value={batchCustomerId} onChange={(e) => handleBatchCustomerChange(e.target.value)}>
                  {batchCustomersList.map((c) => (
                    <MenuItem key={c.customer_id} value={c.customer_id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Total Amount" type="number" value={batchTotalAmount} onChange={(e) => setBatchTotalAmount(e.target.value)} sx={{ width: 150 }} />
              <TextField label="Check #" value={batchCheckNumber} onChange={(e) => setBatchCheckNumber(e.target.value)} sx={{ width: 130 }} />
              <FormControl sx={{ minWidth: 130 }}>
                <InputLabel>Method</InputLabel>
                <Select label="Method" value={batchPayMethod} onChange={(e) => setBatchPayMethod(e.target.value)}>
                  <MenuItem value="Check">Check</MenuItem>
                  <MenuItem value="ACH">ACH</MenuItem>
                  <MenuItem value="Wire">Wire</MenuItem>
                  <MenuItem value="Credit Card">Credit Card</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField label="Memo" fullWidth sx={{ mt: 2 }} value={batchMemo} onChange={(e) => setBatchMemo(e.target.value)} />

            {batchOpenInvoices.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle1">Open Invoices</Typography>
                  <Button size="small" variant="outlined" onClick={handleBatchAutoAllocate} disabled={!batchTotalAmount}>
                    Auto-Allocate
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell align="right">Allocation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchOpenInvoices.map((inv) => {
                      const remaining = (inv.total || 0) - (inv.total_paid || 0);
                      return (
                        <TableRow key={inv.invoice_id}>
                          <TableCell>#{inv.invoice_number}</TableCell>
                          <TableCell>{inv.due_date || "—"}</TableCell>
                          <TableCell align="right">${(inv.total || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">${remaining.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number" size="small" variant="standard"
                              sx={{ width: 100 }}
                              value={batchAllocations[inv.invoice_id] ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setBatchAllocations(prev => ({ ...prev, [inv.invoice_id]: val }));
                              }}
                              inputProps={{ min: 0, max: remaining, step: 0.01 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 3 }}>
                  <Typography variant="body2">
                    Allocated: <strong>${batchAllocSum.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body2" color={Math.abs(parseFloat(batchTotalAmount || 0) - batchAllocSum) > 0.01 ? "error" : "success.main"}>
                    {Math.abs(parseFloat(batchTotalAmount || 0) - batchAllocSum) <= 0.01 ? "Balanced" : `Difference: $${(parseFloat(batchTotalAmount || 0) - batchAllocSum).toFixed(2)}`}
                  </Typography>
                </Box>
                {(parseFloat(batchTotalAmount || 0) - batchAllocSum) > 0.01 && (
                  <MuiAlert severity={batchOverageToCredit ? "info" : "warning"} sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={<Checkbox checked={batchOverageToCredit}
                                         onChange={(e) => setBatchOverageToCredit(e.target.checked)} />}
                      label={`Overpayment: hold the $${(parseFloat(batchTotalAmount || 0) - batchAllocSum).toFixed(2)} excess as a customer credit on account`}
                    />
                  </MuiAlert>
                )}
                {batchOpenCreditTotal > 0.009 && (
                  <MuiAlert severity="info" sx={{ mt: 1 }}>
                    This customer has <strong>${batchOpenCreditTotal.toFixed(2)}</strong> in open credits on
                    account — see the Credits tab to apply them.
                  </MuiAlert>
                )}
              </Box>
            )}

            {batchCustomerId && batchOpenInvoices.length === 0 && (
              <Typography sx={{ mt: 2 }} color="text.secondary">No open invoices for this customer.</Typography>
            )}

            {/* One physical check, one scan — filed on the batch so it shows
                on every invoice the check paid. */}
            <PaymentDocumentPicker
              file={batchDocFile}
              onFile={setBatchDocFile}
              docType={batchDocType}
              onDocType={setBatchDocType}
              inputId="batch-doc-input"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setOpenBatchPayDialog(false); setBatchDocFile(null); }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirmBatchPayment}
              disabled={batchSubmitting || !batchCustomerId || !batchTotalAmount || batchAllocSum === 0}
            >
              {batchSubmitting ? "Processing..." : "Record Payment"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Audit Dialog */}
        <Dialog open={openAuditDialog} onClose={() => setOpenAuditDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>Bill.com Sync Status Audit</DialogTitle>
            <DialogContent dividers>
                {isAuditing ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                        <LinearProgress sx={{ width: '100%', mb: 2 }} />
                        <Typography>Checking remote status...</Typography>
                    </Box>
                ) : auditResult ? (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Sync Status: 
                            <Chip 
                                label={auditResult.sync_status} 
                                color={auditResult.sync_status === 'Synced' ? 'success' : 'error'} 
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                        
                        {auditResult.mismatches && auditResult.mismatches.length > 0 ? (
                            <MuiAlert severity="warning" sx={{ mb: 2 }}>
                                <Typography fontWeight="bold">Discrepancies Found:</Typography>
                                <ul>
                                    {auditResult.mismatches.map((m, i) => <li key={i}>{m}</li>)}
                                </ul>
                            </MuiAlert>
                        ) : (
                            auditResult.sync_status === 'Synced' && (
                                <MuiAlert severity="success" sx={{ mb: 2 }}>Local data matches Bill.com exactly.</MuiAlert>
                            )
                        )}

                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Raw Remote Data:</Typography>
                        <Paper variant="outlined" sx={{ p: 1, maxHeight: 300, overflow: 'auto', bgcolor: isDark ? '#1a1a2e' : '#f5f5f5' }}>
                            <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                                {JSON.stringify(auditResult.remote_data, null, 2)}
                            </pre>
                        </Paper>
                    </Box>
                ) : (
                    <Typography color="error">Failed to load audit data.</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenAuditDialog(false)}>Close</Button>
            </DialogActions>
        </Dialog>

        {/* Payment Inquiry Dialog */}
        <PaymentInquiryDialog
            open={openInquiryDialog && !!invoiceDetail}
            invoiceId={invoiceDetail?.invoice_id}
            onClose={() => setOpenInquiryDialog(false)}
            onSent={handleInquirySent}
        />

        {/* Email Invoice Dialog */}
        <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Email Invoice #{invoiceDetail?.invoice_number || invoiceDetail?.invoice_id}</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Send To</InputLabel>
                    <Select
                        label="Send To"
                        value={selectedContactId}
                        onChange={(e) => {
                            const newId = e.target.value;
                            setSelectedContactId(newId);
                            setCcContactIds(prev => prev.filter(id => id !== newId));
                        }}
                    >
                        {emailContacts.length === 0 ? (
                            <MenuItem disabled value="">
                                <em>No contacts with email addresses found</em>
                            </MenuItem>
                        ) : (
                            emailContacts.map((contact) => (
                                <MenuItem key={contact.contact_id} value={contact.contact_id}>
                                    {contact.name} ({contact.email}){contact.role ? ` - ${contact.role}` : ''}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>CC Additional Recipients</InputLabel>
                    <Select
                        label="CC Additional Recipients"
                        multiple
                        value={ccContactIds}
                        onChange={(e) => setCcContactIds(e.target.value)}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((id) => {
                                    const c = emailContacts.find(ct => ct.contact_id === id);
                                    return <Chip key={id} label={c ? `${c.name}` : id} size="small" />;
                                })}
                            </Box>
                        )}
                    >
                        {emailContacts
                            .filter(c => c.contact_id !== selectedContactId)
                            .map((contact) => (
                                <MenuItem key={contact.contact_id} value={contact.contact_id}>
                                    <Checkbox checked={ccContactIds.indexOf(contact.contact_id) > -1} size="small" />
                                    {contact.name} ({contact.email}){contact.role ? ` - ${contact.role}` : ''}
                                </MenuItem>
                            ))
                        }
                        {emailContacts.filter(c => c.contact_id !== selectedContactId).length === 0 && (
                            <MenuItem disabled>
                                <em>No additional contacts available</em>
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={sendCopyToSelf}
                            onChange={(e) => setSendCopyToSelf(e.target.checked)}
                        />
                    }
                    label="Send a copy to info@kancraftmfg.com"
                    sx={{ mt: 1 }}
                />
                <TextField
                    label="Message Body"
                    fullWidth
                    multiline
                    rows={8}
                    sx={{ mt: 1 }}
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    The invoice PDF will be attached to this email automatically.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseEmailDialog} disabled={isSendingEmail}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSendInvoiceEmail}
                    disabled={isSendingEmail || !selectedContactId}
                    startIcon={<EmailIcon />}
                >
                    {isSendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
            <MuiAlert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                {snackbarMessage}
            </MuiAlert>
        </Snackbar>

        {/* Create Invoice Dialog */}
        <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="xl" fullWidth>
          <DialogTitle>Create Invoice</DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              {/* LEFT COLUMN: SELECTION CONTEXT */}
              <Grid item xs={12} md={4} sx={{ borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e0e0e0'}` }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <ToggleButtonGroup
                    color="primary"
                    value={invoiceCreationMode}
                    exclusive
                    onChange={handleCreationModeChange}
                    size="small"
                  >
                    <ToggleButton value="sales_order">By Sales Order</ToggleButton>
                    <ToggleButton value="direct_job">By Job (Direct)</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Typography variant="h6" gutterBottom>1. Select Source</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Customer</InputLabel>
                  <Select label="Customer" value={newInvoiceCustomerId} onChange={(e) => handleSelectCustomer(e.target.value)}>
                    <MenuItem value="">(Select a customer)</MenuItem>
                    {customersList.map((cust) => (<MenuItem key={cust.customer_id} value={cust.customer_id}>{cust.name}</MenuItem>))}
                  </Select>
                </FormControl>
                {/* MODE: SALES ORDER */}
                {invoiceCreationMode === 'sales_order' && (
                  <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Sales Order</InputLabel>
                      <Select
                        label="Sales Order"
                        value={newInvoiceSalesOrderId}
                        onChange={(e) => handleSelectSalesOrder(e.target.value)}
                        disabled={!newInvoiceCustomerId}
                      >
                        <MenuItem value="">(No specific order)</MenuItem>
                        {salesOrdersList.map((so) => (
                          <MenuItem key={so.sales_order_id} value={so.sales_order_id} disabled={isTm(so)}>
                            {so.order_number} {(() => {
                              const bits = [];
                              if (so.customer_po) bits.push(`PO: ${so.customer_po}`);
                              if (so.customer_so) bits.push(`SO: ${so.customer_so}`);
                              return bits.length ? `- ${bits.join(' / ')}` : '';
                            })()}
                            {isTm(so) && ' — T&M: invoice T&M orders from the Sales Order page'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {renderBillingProgress()}
                    {soBillingStatus && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Order Details:</Typography>
                        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                          <Table size="small">
                            <TableHead><TableRow><TableCell>Item</TableCell><TableCell align="right">Rem</TableCell></TableRow></TableHead>
                            <TableBody>
                              {soBillingStatus.lines.map(l => (
                                <TableRow key={l.so_line_id}>
                                  <TableCell>{l.product_id}</TableCell>
                                  <TableCell align="right">{l.quantity_remaining} / {l.quantity_ordered}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
                {/* MODE: DIRECT JOB */}
                {invoiceCreationMode === 'direct_job' && newInvoiceCustomerId && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Select Orphan Jobs to Invoice:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField size="small" label="Filter Job #" value={jobFilterMasterNum} onChange={(e) => setJobFilterMasterNum(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                      <TextField size="small" label="Filter PO" value={jobFilterCustomerPo} onChange={(e) => setJobFilterCustomerPo(e.target.value)} />
                    </Box>
                    <Box sx={{ maxHeight: 400, overflow: 'auto', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#ddd'}`, borderRadius: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox 
                                checked={selectableJobs.length > 0 && selectedJobIds.size === selectableJobs.length}
                                indeterminate={selectedJobIds.size > 0 && selectedJobIds.size < selectableJobs.length}
                                onChange={handleSelectAllJobs}
                              />
                            </TableCell>
                            <TableCell>Job #</TableCell>
                            <TableCell>PO</TableCell>
                            <TableCell>Cust SO</TableCell>
                            <TableCell>Desc</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredJobs.length === 0 ? (
                             <TableRow><TableCell colSpan={4} align="center">{jobsListLoading ? "Loading jobs…" : "No orphan jobs found."}</TableCell></TableRow>
                          ) : (
                            filteredJobs.map(job => isTmLinked(job) ? (
                              <TableRow key={job.job_id} data-testid={`tm-job-disabled-${job.job_id}`} title="Invoice T&M orders from the Sales Order page">
                                <TableCell padding="checkbox"><Checkbox checked={false} disabled inputProps={{ 'aria-label': `Job ${job.master_job_num}: T&M, invoice from the Sales Order page` }} /></TableCell>
                                <TableCell>{job.master_job_num}</TableCell>
                                <TableCell>{job.customer_po || '-'}</TableCell>
                                <TableCell>{job.customer_so || '-'}</TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{job.job_description} — T&amp;M: invoice T&amp;M orders from the Sales Order page</TableCell>
                              </TableRow>
                            ) : (
                              <TableRow key={job.job_id} hover onClick={() => handleSelectJob(job.job_id)} selected={selectedJobIds.has(job.job_id)} sx={{ cursor: 'pointer' }}>
                                <TableCell padding="checkbox"><Checkbox checked={selectedJobIds.has(job.job_id)} /></TableCell>
                                <TableCell>{job.master_job_num}</TableCell>
                                <TableCell>{job.customer_po || '-'}</TableCell>
                                <TableCell>{job.customer_so || '-'}</TableCell>
                                <TableCell>{job.job_description}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                    <Button variant="contained" fullWidth sx={{ mt: 2 }} disabled={selectedJobIds.size === 0} onClick={handleAddSelectedJobs} startIcon={<AddIcon />}>Add Selected Jobs ({selectedJobIds.size})</Button>
                  </Box>
                )}
              </Grid>

              {/* RIGHT COLUMN: INVOICE BUILDER */}
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">2. Invoice Lines</Typography>
                  {soBillingStatus && invoiceCreationMode === 'sales_order' && (
                    <Button variant="outlined" startIcon={<ReceiptIcon />} onClick={handleBillRemaining} color="secondary" size="small">Reset to "Bill Remaining"</Button>
                  )}
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}><TextField label="Invoice Date" type="date" size="small" fullWidth value={newInvoiceDate} onChange={(e) => setNewInvoiceDate(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                    <Grid item xs={4}><TextField label="Due Date" type="date" size="small" fullWidth value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Payment Terms</InputLabel>
                        <Select label="Payment Terms" value={newPaymentTerms} onChange={(e) => setNewPaymentTerms(e.target.value)}>
                          <MenuItem value="">(None)</MenuItem>
                          <MenuItem value="Due on Receipt">Due on Receipt</MenuItem>
                          <MenuItem value="Net 7">Net 7</MenuItem>
                          <MenuItem value="Net 10">Net 10</MenuItem>
                          <MenuItem value="Net 14">Net 14</MenuItem>
                          <MenuItem value="Net 15">Net 15</MenuItem>
                          <MenuItem value="Net 30">Net 30</MenuItem>
                          <MenuItem value="Net 45">Net 45</MenuItem>
                          <MenuItem value="Net 60">Net 60</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}><TextField label="Tax Rate (%)" type="number" size="small" fullWidth value={createTaxRate} onChange={(e) => { const newRate = parseFloat(e.target.value || 0); setCreateTaxRate(newRate); }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} /></Grid>
                    <Grid item xs={4}><TextField label="Tax Amount ($)" type="number" size="small" fullWidth value={createTaxAmount} onChange={(e) => handleCreateTaxAmountOverride(parseFloat(e.target.value || 0))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
                    <Grid item xs={4}></Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}><TextField label="Shipping Cost" type="number" size="small" fullWidth value={createShipping} onChange={(e) => setCreateShipping(parseFloat(e.target.value || 0))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
                    <Grid item xs={4}><TextField label="Additional Fees" type="number" size="small" fullWidth value={createFees} onChange={(e) => setCreateFees(parseFloat(e.target.value || 0))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
                    <Grid item xs={4}><TextField label="Discount" type="number" size="small" fullWidth value={createDiscount} onChange={(e) => setCreateDiscount(parseFloat(e.target.value || 0))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <TextField 
                        label="Notes" 
                        size="small" 
                        fullWidth 
                        multiline 
                        rows={2} 
                        value={newNotes} 
                        onChange={(e) => setNewNotes(e.target.value)} 
                        placeholder="Optional notes for this invoice..."
                      />
                    </Grid>
                </Grid>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right" width={100}>Qty</TableCell>
                      <TableCell align="right" width={100}>Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newLines.map((ln, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{ln.product_id || "Custom"}</TableCell>
                        <TableCell>
                          <TextField variant="standard" fullWidth value={ln.description} onChange={(e) => handleChangeNewLine(idx, "description", e.target.value)} />
                          {/* F19: the invoice preview names where an ASSEMBLY line's per-set
                              price came from — hidden once the operator overrides the price. */}
                          {ln._per_set_source && Math.abs((parseFloat(ln.unit_price) || 0) - ln._per_set_source.per_set_price) < 0.005 && (
                            <Typography variant="caption" display="block" color="text.secondary" title={ln._per_set_source.formula}>
                              {ln._per_set_source.note}
                              {ln._per_set_source.pricing_note ? ` — ${ln._per_set_source.pricing_note}` : ""}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right"><TextField type="number" variant="standard" fullWidth value={ln.quantity} inputProps={{ style: { textAlign: 'right' } }} onChange={(e) => handleChangeNewLine(idx, "quantity", e.target.value)} /></TableCell>
                        <TableCell align="right"><TextField type="number" variant="standard" fullWidth value={ln.unit_price} inputProps={{ style: { textAlign: 'right' }, step: "0.01" }} onChange={(e) => handleChangeNewLine(idx, "unit_price", parseFloat(e.target.value) || 0)} /></TableCell>
                        <TableCell align="right">${(ln.quantity * ln.unit_price).toFixed(2)}</TableCell>
                        <TableCell><IconButton size="small" color="error" onClick={() => handleRemoveNewLine(idx)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                    {newLines.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>{invoiceCreationMode === 'sales_order' ? 'Select a Sales Order to auto-fill lines, or add manually.' : 'Select jobs from the list to add lines.'}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button onClick={handleAddBlankLine}>+ Add Manual Line</Button>
                    <Typography variant="h6">Total: ${(newLines.reduce((a, b) => a + (b.quantity * b.unit_price), 0) + parseFloat(createShipping || 0) + parseFloat(createFees || 0) - parseFloat(createDiscount || 0) + parseFloat(createTaxAmount || 0)).toFixed(2)}</Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateInvoice} disabled={newLines.length === 0}>Create Invoice</Button>
          </DialogActions>
        </Dialog>
      </Box>
        )}
      </Box>
    </MuiThemeProvider>
  );
}

export default InvoicesModule;